import { AppDate } from '../envTypes';
import { NotificationPayloadType } from './NotificationPayloadType';

export type WithID<T> = T & { id: string };

export enum PopPlatform {
  Psl = 'psl',
  PopStore = 'popstore',
}

export interface ContentModerationInfo {
  moderationStatus: ModerationStatus;
  moderatedAt?: AppDate;
  moderatorProfile?: Pick<
    Profile,
    'firstName' | 'lastName' | 'username' | 'avatar'
  > & { fullName: string };
  moderatorUserId?: 'AI' | string;
  // admin can provide his own reason for approval or disapproval
  adminModerationReason?: string;
  aiModerationReason?: string;
}

export type LiveStreamFav = {
  /**
   * @deprecated replaced by `timestamps` since version 3.42
   */
  timestamp: AppDate;
  /**
   * app will collect live at certain interval and only fire one document
   */
  timestamps?: AppDate[];
  userId: string;
};

export interface TokenizedCreditCard {
  type: string;
  last4digits: string;
  bank?: string;
  info?: string;
  token?: string;
  stripePaymentMethodId?: string;
  stripeFingerprint?: string | null;
  imageUrl?: string;
  createdAt?: AppDate;
  updatedAt?: AppDate;
  creditCardId?: string;
  // from privateProfiles/billingAddresses
  billingAddressId?: string;
  // for fraud detection
  ip?: string;
  postalCode?: string;
}

export enum ProfileUserType {
  // a guest user has the same access as a normal user, it is mainly used to make quick checkout.
  guest = 'guest',
  normal = 'normal',
  seller = 'seller',
  verifiedSeller = 'verifiedSeller',
}

export type BasicProfile = Pick<
  Profile,
  'firstName' | 'lastName' | 'username' | 'avatar'
>;

export interface PopStoreThemeTemplateType {
  id: string;
  slug: string;
  value: string;
}

export enum PopStoreThemeTemplateAccessType {
  Free = 'free',
  Purchase = 'purchase',
  Subscription = 'subscription',
}

export type PopStoreThemeAvailability =
  | { type: PopStoreThemeTemplateAccessType.Free; id: string }
  | {
      type: PopStoreThemeTemplateAccessType.Purchase;
      id: string;
      price: number;
    }
  | {
      type: PopStoreThemeTemplateAccessType.Subscription;
      id: string;
      level: any /*not decided yet*/;
    };

export enum PopStoreThemeTemplateStyle {
  LIGHT = 'light',
  DARK = 'dark',
}

export enum SearchPopStoreThemeTemplatePurpose {
  MANAGE = 'manage',
  APPLY = 'apply',
}

// /popStoreThemeTemplates/{themeId}
export interface PopStoreThemeTemplate extends ModelBase {
  name: string;
  style: PopStoreThemeTemplateStyle;
  type: PopStoreThemeTemplateType;
  thumbnailUrl?: string;

  subscriptionLevel: PopStoreThemeAvailability;

  privacy: boolean;
  // specify which creators can use this theme
  creatorIds?: string[];

  createdAt: AppDate;
  createdByProfile?: BasicProfile;
  createdByUserId: string;

  moderationStatus?: ModerationStatus;
  moderatedAt?: AppDate;
  moderatorProfile?: BasicProfile;
  moderatorUserId?: string;
  theme: string;
  position?: number;
}

// profiles/{uid}
export interface Profile {
  avatar?: string;

  username?: string;

  firstName: string;

  /**
   *  move to private profile, set it to actual last name's first character
   * */

  lastName: string;
  /**
   *  @deprecated move to private profile set it to private@popshop.live
   * */
  email: string;
  storeFrontName?: string;
  createdAt?: AppDate;
  updatedAt?: AppDate;
  hashtags?: Record<string, string>;
  userType?: ProfileUserType;
  storeType?: StoreType;
  urlCode?: string;
  bio?: string;
  badges?: {
    activeSeller?: boolean;
    staff?: boolean;
  };
  hideBadgeInProfile?: boolean;
  /**
   * true if the firebase auth record for this profile is deleted (mainly for guest profile)
   */
  authDeleted?: boolean;
  // JSON serialized form of the seller profile customization
  customization?: string;
  apps?: {
    popStore?: boolean;
  };
  popStoreDomain?: string;
  followersSettings?: { description?: string; active?: boolean };
  birthday?: Date;
}

export enum BadgeStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  EDIT_REQUIRE = 'EDIT_REQUIRE',
}

export enum BadgeCreateStep {
  FILL_BASE_INFO = 'FILL_BASE_INFO',
  UPLOAD_BADGE = 'UPLOAD_BADGE',
  CONFIRM_BADGE = 'CONFIRM_BADGE',
  REVIEW_BADGE = 'REVIEW_BADGE',
}

export enum BadgeLimit {
  Badge_LIMIT_5 = 'Badge_LIMIT_5',
  Badge_LIMIT_25 = 'Badge_LIMIT_25',
  Badge_LIMIT_100 = 'Badge_LIMIT_100',
  Badge_LIMIT_250 = 'Badge_LIMIT_250',
  Badge_LIMIT_500 = 'Badge_LIMIT_500',
  Badge_LIMIT_1000 = 'Badge_LIMIT_1000',
  Unlimited = 'Unlimited',
}

// badges/badgeId
export interface Badge extends ModelBase {
  /**
   * the init status is BadgeStatus.DRAFT
   */
  status: BadgeStatus;
  approvedById?: string;
  approvedAt?: AppDate;
  name: string;
  description: string;
  currentStep: BadgeCreateStep;
  generatedAt?: AppDate;
  storeId: string;
  store: StoreFront;
  assignedCount: number;
  limitCount: number;
  limit: BadgeLimit;
  url?: string;
  /**
   * when delete a badge of a users then put that badge's editionNo to this array
   * when send the badge to another user will reuse this editionNo and remove from this array
   */
  editionNoGaps?: string[];
  /**
   * the next editionNo will use editionNoGaps first
   */
  nextEditionNo: number;
}

// profiles/profileId/badges/{badgeId}
export interface UserBadge extends ModelBase {
  earnedAt: AppDate;
  editionNo: string;
  storeId: string;
  badgeId: string;
  badge: Badge;
}

// badges/badgeId/users/{userId}
export interface BadgeUser extends ModelBase {
  earnedAt: AppDate;
  storeId: string;
  editionNo: string;
  badgeId: string;
  userId: string;
  profile: Profile;
}

export interface ProfileFollower extends Profile {
  timestamp: AppDate;
  followFrom?: FollowFrom;
}

export interface LiveStreamViewerProfile extends Profile {
  isWatching?: boolean;
  startedAt?: AppDate;
  stoppedAt?: AppDate;
  deviceType?: string;
  deviceId?: string;
  nftImageUrl?: string;
  userGroupIds?: string[];
}

export enum FollowFrom {
  INVITATION = 'invitation',
  COUPON = 'coupon',
  SELF = 'self',
}

// profiles/{uid}/followers/{followerId}
export interface Follower {
  user: Profile;
  followAt: AppDate;
}

export enum ShowPermission {
  All = 'all',
  Follower = 'follower',
  Subscriber = 'subscriber',
  None = 'no-one',
}

// upcomingShows/{showId}
export interface UpcomingShow {
  badges?: string[];
  title: string;
  user: Profile;
  userId: string;
  coverImage?: string;
  originalCoverImage?: string;
  previewVideoURL: string;
  previewVideoDurationInSeconds?: number;
  shareVideo?: string;
  // startsAt is mutable, so we need to keep the original value for other purpose
  initialStartsAt?: AppDate;
  startsAt: AppDate;
  updatedAt?: AppDate;
  createdAt?: AppDate;
  available: boolean;
  /**
   * @deprecated replaced by `permission` since TODO: version
   */
  visibility?: 'all' | 'allSeller' | 'allBuyer' | 'deferred';
  permission?: ShowPermission;
  eventId?: string;
  eventName?: string;
  userGroupIds?: string[];
  eventTemplateId?: string;
  disableComments?: boolean;
  notificationSent?: boolean;
  liveStreamId?: string;
  historyLiveStreamIds?: string[];
  number?: number;
  visibilityGroupIds?: string[];

  showCustomization?: ShowCustomization;
  // support upvotes for upcoming show
  upvoteCount?: number;
  accessLevel?: PopStoreUserAccessLevel;
}

export interface ContentSubscriber extends ModelBase {
  userId?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface ShowCover {
  name: string;
  imageUrl: string;
}

export interface WatchList {
  upcomingShow: UpcomingShow;
  createdAt: AppDate;
  _addedByCloudFunctionWhenFollowingSeller?: boolean;
}

export type CommentType =
  | 'comment'
  | 'userEnter'
  | 'userFollow'
  | 'purchase'
  | 'tip'
  | 'share';

export interface CommentInfo {
  giftedProduct?: Listing;
  giftedProductId?: string;
  giftedHost?: Profile;
  purchaseProduct?: Listing;
  purchaseProductId?: string;
  purchaseQuantity?: number;
  purchaseIsGifted?: boolean;
  giftedQuantity?: number;
  orderNumber?: string;
  masterOrderNumber?: string;
  // if this comment is posted by a user with nft
  // we will display the nft avatar instead
  nftImageUrl?: string;

  tipOrderId?: string;
  tipAmount?: number;

  minAppVersion?: '3.52.0' | string;
}

export type GiftingComment = {
  orderId: string;
  type: 'gifting';
  userId?: string;
  sender: {
    userId: string;
    firstName: string;
    lastName: string;
    username?: string;
    avatar?: string;
    userType?: ProfileUserType;
    nftImageUrl?: string;
    badges?: Profile['badges'];
  };
  receiver: GiftingComment['sender'];
  listing: {
    name: string;
    defaultImage: string;
  };
  quantity: number;
  createdAt: AppDate;
  visibility?: 'all' | 'blockedBySeller' | 'deletedBySeller';
};
// Copied from https://core.telegram.org/constructor/inputMessageEntityMentionName
export interface ChatMention {
  // Offset of message entity within message (in UTF-8 codepoints)
  offset: number;
  // Length of message entity within message (in UTF-8 codepoints)
  length: number;
  userId: string;
  profile: {
    username?: string;
    firstName: string;
    lastNameInitial: string;
    avatar?: string;
  };
}

// wave feature, user can invite(wave) followers to watch show
// wavingUser is inviter, wavedToUser is the follower invited by inviter
export interface WaveComment {
  // 'wave' means the user is inviting follower, 'enter' means follower is entering the show.
  type: 'wave' | 'wavedToUserEnter';
  userId: string;
  wavingUser: {
    userId: string;
    firstName: string;
    lastName: string;
    username?: string;
    avatar?: string;
    userType?: ProfileUserType;
    nftImageUrl?: string;
    storeFrontName?: string;
    badges?: Profile['badges'];
  };
  wavedToUser: WaveComment['wavingUser'];
  createdAt: AppDate;
  visibility?: 'all' | 'blockedBySeller' | 'deletedBySeller';
  // For type 'wave', we can wave to multiple users and wavedToUser will be first among them.
  wavedToUserIds?: string[];
}

export type OriginComment = {
  userId: string;
  content: string;
  type: CommentType;
  followingUser?: Profile & { id: string };
  createdAt?: AppDate;
  profile: Profile;
  info?: CommentInfo;
  visibility?: 'all' | 'blockedBySeller' | 'deletedBySeller';
  mentions?: ChatMention[];
  inviteBy?: {
    firstName: string;
    lastName: string;
    avatar?: string;
    userId: string;
  };
};

export type BadgeEarnedComment = {
  type: 'badgeEarned';
  userId: string;
  profile: Profile & { nftImageUrl?: string };
  badgeId: string;
  userBadge: UserBadge;
  createdAt?: AppDate;
  visibility?: 'all' | 'blockedBySeller' | 'deletedBySeller';
};

export interface GiveawayComment {
  type: 'giveawayComment';
  giveawayItemId: string;
  sellerId: string;
  seller: Profile & { nftImageUrl?: string };
  status:
    | 'created'
    | 'canceled'
    | 'userJoined'
    | 'sellerPickedWinner'
    | 'winnerAccepted'
    | 'winnerDeclined';
  entrantId?: string;
  entrant?: Profile & { nftImageUrl?: string };
  createdAt?: AppDate;
  visibility?: 'all' | 'blockedBySeller' | 'deletedBySeller';
  userId: string;
  listing: {
    name: string;
    defaultImage: string;
  };
  giveawayItem: {
    title: string;
    coverImage: string;
  };
}

export type Comment = (
  | OriginComment
  | GiftingComment
  | WaveComment
  | BadgeEarnedComment
  | GiveawayComment
) & {
  // output from bigquery
  upvoteCount?: number;
};

export interface FireListing {
  uuid: string;
  name: string;
  price: string;
  image: string;
}

export interface Purchase {
  buyername: string;
  buyerAvatar?: string;
  sellername: string;
  sellerAvatar?: string;
  listingName: string;
  purchasedAt: AppDate;
  listingUUID: string;
}

export interface PayoutVenmo {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
}

export interface PayoutBank {
  firstName: string;
  lastName: string;
  accountNumber: string;
  routingNumber: string;
  accountType?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
}

export interface PayoutPaypal {
  firstName: string;
  lastName: string;
  username: string;
  phone: string;
}

export type VisibilityType = 'all' | 'allSeller' | 'allBuyer' | 'deferred';

export type PaymentType = 'credit' | 'applepay' | 'empty';

// privateProfiles/{uid}/sessions/{sessionId}
export interface LoginSession {
  loggedIn: boolean;
  loggedInAt: AppDate;
  deviceId: string;
  deviceModel: string;
  loggedOutAt?: AppDate;
  // same device id used with new session
  abandonedAt?: AppDate;
  lastUsed: AppDate;
  lastUsedAppVersion: string;
  lastUsedAppId: string;
  pushToken?: string;
  hasPushToken: boolean;
  userAgent?: string;
  platform?: string;
}

/**
 * @deprecated since 12/06/22
 */
export enum USPSShippingServiceLevel {
  CHEAPEST_AVAILABLE = 'CHEAPEST_AVAILABLE',
  FASTEST_AVAILABLE = 'FASTEST_AVAILABLE',
  USPS_PARCEL_SELECT = 'USPS_PARCEL_SELECT',
  USPS_FIRST_CLASS = 'USPS_FIRST_CLASS',
  USPS_PRIORITY = 'USPS_PRIORITY',
  USPS_PRIORITY_EXPRESS = 'USPS_PRIORITY_EXPRESS',
}

/**
 * @deprecated since 12/06/22
 */
export enum SignatureConfirmationType {
  Standard = 'STANDARD',
  Adult = 'ADULT',
  Certified = 'CERTIFIED',
}

/**
 * @deprecated since 12/06/22
 */
export enum InsurancePolicyType {
  minOrderGroupInsureAmount = 'MIN_ORDER_GROUP_INSURE_AMOUNT',
  // same as undefined
  disable = 'DISABLE',
}

/**
 * @deprecated since 12/06/22
 */
export type InsurancePolicy =
  | { type: InsurancePolicyType.minOrderGroupInsureAmount; amount: number }
  | { type: InsurancePolicyType.disable };

export type SellerOnboardingStepState = {
  finished?: boolean;
  finishedAt?: AppDate;
};

type Counters<T extends string> = { [p in T]: number };
type Flags<T extends string> = { [p in T]?: boolean };

type ExtractRequirement<T> = T extends { requirement?: infer R } ? R : never;
type ExtractCounters<T> = T extends { counters?: infer C } ? C : never;
type ExtractFlags<T> = T extends { flags?: infer F } ? F : never;
type ExtractExtra<T> = T extends { extra?: infer E } ? E : never;

export type PopStoreOnboardingContestTaskType =
  keyof PopStoreOnboardingContestTasks;

// eslint-disable-next-line
type __EMPTY__ = {};

// Internal utility type that defines the types for each task in a one place.
type PopStoreOnboardingContestTasks = {
  // basic bio spec, including:
  // - profile picture, name, description and social links
  bio_basic_info?: {
    requirement?: {
      min_social_link_count?: number;
    };
    counters?: Counters<'social_links'>;
    flags?: Flags<'profile_picture' | 'name' | 'bio' | 'social_links'>;
    extra?: __EMPTY__;
  };
  // link/custom buttons spec
  bio_link_buttons?: {
    requirement?: {
      min_button_count?: number;
    };
    counters?: Counters<'link_buttons'>;
    flags?: __EMPTY__;
    extra?: __EMPTY__;
  };
  // subscriptions spec
  subscriptions?: {
    requirement?: {
      min_subscription_count?: number;
    };
    counters?: Counters<'subscriptions'>;
    flags?: Flags<PopStoreSubscriptionLevel>;
    extra?: __EMPTY__;
  };
  // videos spec, including:
  // - how many videos need to be uploaded (optionally to series)
  // - how many series need to be created
  // - how many videos gated by subscriptions need to be uploaded
  videos?: {
    requirement?: {
      min_total_video_count?: number;
      min_restricted_video_count?: number;
      min_series_count?: number;
      min_video_count_per_series?: number;
      min_restricted_video_count_per_series?: number;
    };
    counters?: Counters<'total_videos' | 'total_restricted_videos' | 'series'>;
    flags?: __EMPTY__;
    // save video and restricted video count for each series
    extra?: Record<
      string,
      {
        // total number of video in this series
        video_count: number;
        // total number of restricted videos in this series
        restricted_video_count: number;
        // if the series is archived
        archived?: boolean;
      }
    >;
  };
  // digital download product spec
  digital_product?: {
    requirement?: {
      min_digital_product_count?: number;
      require_added_to_my_store_collection?: boolean;
      require_added_to_link_button?: boolean;
    };
    counters?: Counters<'digital_products'>;
    flags?: __EMPTY__;
    extra?: Record<
      string,
      {
        // related my store collection ids the product is added to
        in_my_store_collections: string[];
        // id of the archived collections the product is in
        in_archived_collections?: string[];
        // related link button ids the product is added to
        with_link_buttons: string[];
      }
    >;
  };
  // reserved for future use
  store_reel?: {
    requirement?: __EMPTY__;
    counters?: __EMPTY__;
    flags?: Flags<'uploaded'>;
    extra?: __EMPTY__;
  };
  payout?: {
    requirement?: __EMPTY__;
    counters?: __EMPTY__;
    flags?: Flags<'configured'>;
    extra?: __EMPTY__;
  };
  commentchat?: {
    requirement?: {
      min_post_links?: number;
    };
    counters?: Counters<'post_links'>;
    flags?: Flags<'connected'>;
    extra?: __EMPTY__;
  };
};

export type PopStoreCreatorOnboardingContestTaskState<
  T extends PopStoreOnboardingContestTaskType,
> = {
  introViewed?: boolean;
  introViewedAt?: AppDate;
  startedAt?: AppDate;
  finished?: boolean;
  finishedAt?: AppDate;
  counters?: ExtractCounters<PopStoreOnboardingContestTasks[T]>;
  flags?: ExtractFlags<PopStoreOnboardingContestTasks[T]>;
  extra?: ExtractExtra<PopStoreOnboardingContestTasks[T]>;
};

export type PopStoreCreatorOnboardingContest = {
  startedAt?: AppDate;
  submittedAt?: AppDate;
  elapsedInSeconds?: number;
  electedAsWinner?: boolean;
  electedAsWinnerAt?: AppDate;
  electedAsWinnerMessage?: string;
  electedAsWinnerMessagePresented?: boolean;
  electedAsWinnerMessageDismissed?: boolean;
  tasks?: {
    [T in PopStoreOnboardingContestTaskType]?: PopStoreCreatorOnboardingContestTaskState<T>;
  };
};

export interface AppSellerApplicationPurpose {
  slug: string;
  name: string;
}

export interface PrivateProfile {
  birthday?: AppDate;
  profilePictureConfirmed?: boolean;
  storeCoverPhotoConfirmed?: boolean;
  storeInfoConfirmed?: boolean;
  businessLocationConfirmed?: boolean;
  shippingOptionsConfirmed?: boolean;
  sellerOnboardingCompleted?: boolean;
  sellerOnboardingCompletedBy?: 'seller' | 'admin';
  sellerOnboardingCompletedAt?: AppDate;
  isSellerFirstTimeWalkThroughPresented?: boolean;
  defaultPayment?: string;
  defaultPaymentType?: PaymentType;
  defaultAddress?: Address | null;
  extraAddresses?: Address[] | null;
  sellerShippingAddress?: Address;
  payoutMethodBank?: PayoutBank;
  payoutMethodVenmo?: PayoutVenmo;
  payoutMethodPaypal?: PayoutPaypal;
  billingCountryCode?: string;
  stripeBillingCountryCode?: string;
  purposes?: AppSellerApplicationPurpose[];
  /**
   * store level default tax code for products
   */
  defaultProductTaxCode?: string;

  /**
   * @deprecated replaced by `LoginSession` since 3.23.0
   */
  deviceTokens?: DeviceToken[];
  /**
   * @deprecated replaced by `LoginSession` since 3.23.0
   */
  pushNotificationTokens?: string[];
  /**
   * @deprecated replaced by `withdrawableBalance` in readonlyProfile
   */
  balance?: number;
  /**
   * move from profile
   */
  lastName?: string;
  /**
   * move from profile
   */
  email?: string;

  unsubscribe?: boolean;
  watchPasscodes?: string[];
  watchPasscodeCount?: number;
  referrerUserId?: string;
  referrerUserProfile?: Profile;
  referrerManuallyModifiedAt?: AppDate;
  referrerStore?: StoreFront;
  defaultVisibility?: VisibilityType;
  createdAt?: AppDate;

  notificationWatchedShowStart?: boolean;
  notificationAddedToWatched?: boolean;
  notificationFollowedUserStarted?: boolean;
  notificationYouAreFollowed?: boolean;
  notificationUpcomingShowDidCreate?: boolean;
  notificationUpcomingShowComment?: boolean;
  notification2WayStreamStarted?: boolean;
  notificationYouAreMentioned?: boolean;
  allowSMS?: boolean;
  /**
   * used to count how many new orders have been created after this date.
   *
   * if null, use start of today as default
   */
  startDateToCountNewOrders?: AppDate;

  notificationSetting?: string;
  timezone?: string;

  printFormat?: {
    packingSlip?: 'letter' | 'a6';
    shippingLabel?: 'a6' | 'PDF_SINGLE_8X11';
  };

  emailTags?: string[];

  /**
   * allow seller to see the upcoming and livestreaming show on web
   */
  shouldStartShowByWeb?: boolean;

  /**
   * Merchant Code, 4 digets codes.
   * codes list: https://github.com/greggles/mcc-codes/blob/main/mcc_codes.json
   * If the code is not set, it should be treated as `5262, Marketplaces`
   */
  mccCode?: string;

  iOSTrackingTransparencySetting?: string;

  orderFulfillmentSetting?: {
    /**
     * deprecated since 12/06/22
     */
    insurancePolicy?: InsurancePolicy;
    packageTemplateUnit?: PackageTemplateUnit;
    defaultPackageTemplate?: PackageTemplate & { id: string };
    /**
     * deprecated since 12/06/22
     */
    defaultShippingServiceLevel?: USPSShippingServiceLevel;
    showConfirmationWhenGeneratingMultipleLabels?: boolean;
    showConfirmationWhenRegeneratingMultipleLabels?: boolean;
    /**
     * deprecated since 12/06/22
     */
    extra?: {
      signatureConfirmation?: SignatureConfirmationType;
    };
  };

  stripeAccountId?: string;

  stripeAccountVerificationStatus?: 'verified' | 'unverified' | 'pending';

  /** for withdraw from connect account */
  stripeBankAccountId?: string;

  /** PSLPay connection and related information */
  pslPay?: {
    /** If seller has connected (i.e., signed up) PSLPay */
    connected: boolean;
    /** PSLPay sign up (connect) update timestamp */
    updatedAt: AppDate;
  };

  popStorePay?: {
    /** If creator has connected (i.e., signed up) Pop.Store Pay, enable transfer and payout ability */
    connected: boolean;
    /** Pop.Store Pay sign up (connect) update timestamp */
    updatedAt: AppDate;
  };

  /**
   * stripe connect account capability details, included charge and payout status and requirement
   */
  stripeAccountCapability?: {
    status: 'rejected' | 'pending' | 'completed' | 'enabled' | 'restricted';
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    requirement?: {
      /** Date by which the fields in currentlyDue must be collected to keep the capability enabled. */
      currentDeadline?: AppDate;
      /** need to be collected to keep the capability enabled */
      currentlyDue?: string[];
      eventuallyDue?: string[];
      /** Fields that weren’t collected by currentlyDue */
      pastDue?: string[];
      disabledReason?: string;
      errors?: StripeCapabilityRequirementError[];
    };
    futureRequirement?: {
      currentDeadline?: AppDate;
      currentlyDue?: string[];
      eventuallyDue?: string[];
      pastDue?: string[];
      disabledReason?: string;
      errors?: StripeCapabilityRequirementError[];
    };
    eventUpdateAt: AppDate;
  };

  /** Seller onboarding state and statistics information */
  sellerOnboardingState?: {
    [p in SellerOnboardingStepType]?: SellerOnboardingStepState;
  };

  /** commentchat related state */
  commentchat?: {
    connectionStatus?: CommentChatConnectionStatus;
    authorization?: CommentChatAuthorization;
    connections?: CommentChatConnection[];
    channels?: CommentChatChannel[];
    onboardingFinished?: boolean;
    onboardingFinishedAt?: AppDate;
    waitingForAppSetup?: boolean;
    appSetupFinished?: boolean;
  };

  // MSM(myShipManager)
  myShipManager?: {
    encryptedData: string;
    status: MsmStatus;
    scopes: string[];
    // redirect to MSM after approved authorization by CS API
    approveUrl: string;
    // redirect to MSM after deny authorization by CS API
    denyUrl: string;
    // shopify ID
    externalShopId: string;
    createdAt: AppDate;
    updatedAt: AppDate;
  };

  modelMePersonalData?: {
    faceImageAwsS3Urls: string[];
    bodyImageAwsS3Urls: string[];
    height: number;
    heightUnit: HeightUnit;
    dressSize: DressSize;
    ethnicity: Ethnicity;
    hairColor: HairColor;
  };
  googleCalendar?: {
    calendarId?: string;
    status: GoogleOauth2ConnectionStatus;
    redirectUri?: string;
    tokens?: {
      accessToken: string;
      refreshToken: string;
      scope: string;
      expiryDate: AppDate;
    };
  };

  popStoreNotification?: {
    phone?: string;
    email?: string;
  };
  sellerApplicationRegisterIp?: string;
  popStoreDropshipApplicationId?: string;
  popStoreOnboardingContestSubmissionId?: string;
  // popstore creator onboarding contest data
  popStoreOnboardingContest?: PopStoreCreatorOnboardingContest;
  popStoreThemePickerPresented?: boolean;
  lastCompletedDmSetupStep?: number;
  isAgeCertified?: boolean;

  theme?: {
    themeId: string;
    appliedAt: AppDate;
  } & Omit<
    PopStoreThemeTemplate,
    | 'createdByProfile'
    | 'createdByUserId'
    | 'moderationStatus'
    | 'moderatedAt'
    | 'moderatorProfile'
    | 'moderatorUserId'
  >;

  // default pay to view price for post
  defaultPostPayToViewPrice?: number;

  // default subscription level for post
  defaultPostSubscriptionLevel?: PopStoreExtendedSubscriptionLevel;

  // default target amount for post group tipping
  defaultPostGroupTippingTargetAmount?: number;

  bootstrappedFrom?: {
    urlCode: string;
    name: string;
    aboutUrl?: string;
    instagramUrl?: string;
    tiktokUrl?: string;
    youtubeUrl?: string;
    patreonUrl?: string;
    snapchatUrl?: string;
    linkInBioUrl?: string;
    popStoreUrl?: string;
    seriesName?: string;
    videoUrls?: string[];
  };
  lastestPaymentMethod?: {
    paymentMethodId?: string;
    stripeConfirmationToken?: string;
    currency?: string;
    paymentMethodDetails?: PaymentMethodDetails;
    createdAt: AppDate;
  };
}

export enum HeightUnit {
  'cm' = 'cm',
  'in' = 'in',
  'ft' = 'ft',
  'm' = 'm',
}

export enum DressSize {
  'XS' = 'XS',
  'S' = 'S',
  'M' = 'M',
  'L' = 'L',
  'XL' = 'XL',
  'XXL+' = 'XXL+',
}

export enum Ethnicity {
  Asian = 'Asian',
  European = 'European',
  MiddleEastern = 'Middle Eastern',
  Indian = 'Indian',
  Hispanic = 'Hispanic',
  African = 'African',
  Latino = 'Latino',
}

export enum HairColor {
  Blonde = 'Blonde',
  Brunette = 'Brunette',
  Red = 'Red Head',
  Black = 'Black hair',
  White = 'White hair',
}

export enum ModelMePersonalImageType {
  Face = 'face',
  Body = 'body',
}

export enum ModelMePersonalImageExt {
  jpeg = 'jpeg',
  jpg = 'jpg',
  png = 'png',
}

export interface FacebookAuthResponse {
  accessToken: string | undefined;
  data_access_expiration_time: number | undefined;
  expiresIn: number;
  signedRequest: string | undefined;
  userID: string;
  grantedScopes: string | undefined;
  reauthorize_required_in?: number | undefined;
  code?: string | undefined;
}

export enum CommentChatConnectionStatus {
  Connecting = 'connecting',
  Authorized = 'authorized',
  Success = 'success',
  Error = 'error',
}

export enum GoogleOauth2ConnectionStatus {
  Authorized = 'authorized',
  // need to re-authorize
  Expired = 'expired',
  Error = 'error',
}

export interface CommentChatAuthorization {
  lastAuthorizedAt: AppDate;
  authResponse: FacebookAuthResponse;
}

export interface CommentChatConnection {
  id: string;
  extId: string;
  platform: 'fb' | 'ig';
  name: string;
  accessToken: string;
  createdAt: number;
  updatedAt: number;
}

export enum CommentChatChannelType {
  FbPage = 'fb_page',
  IgAccount = 'ig_account',
}

export interface CommentChatChannel {
  // The channel type.
  type: CommentChatChannelType;

  // The channel ID
  id: string;

  // The connection ID
  connectionId: string;

  // The external channel ID
  extId: string;

  // The channel name
  name: string;

  // The channel access token
  accessToken?: string;

  // Whether the channel is subscribed to meta webhooks
  subscribedToMetaWebhooks?: boolean;

  // Profile picture URL.
  profilePictureUrl?: string;
}

export interface StripeCapabilityRequirementError {
  code: string;
  detailedCode?: string;
  requirement: string;
  reason: string;
}

export enum SellerOnboardingStepType {
  PslPay = 'pslPay',
  StoreInfo = 'storeInfo',
  StoreReel = 'storeReel',
  BusinessLocation = 'businessLocation',
  ShippingOptions = 'shippingOptions',
  Inventory = 'inventory',
  Storefront = 'storefront',
  CommentChat = 'commentchat',
}

export enum CommentSoldShopStatus {
  Pending = 'PENDING',
  Created = 'CREATED',
  Failed = 'FAILED',
}

export interface ReadonlyProfile {
  noCommissionBalance?: number;
  /**
   * @deprecated replaced by `withdrawableBalance`
   */
  balance?: number;
  /**
   * @deprecated replaced by `withdrawablePendingBalance`
   */
  pendingBalance?: number;
  bonusBalance?: number;
  referralBalance?: number;
  referralCount?: number;
  totalReferral?: number;
  newClientBonus?: boolean;
  commissionRate?: number;
  gatewayProcessingFeeRate?: number; // 2.9% per order from 09/01/23
  gatewayProcessingFeeExtra?: number; // $0.3 per capture group from 09/01/23
  // if shippingFee is nil, it is using goshippo.
  // if shippingFee is not nil, it is using custom shipping
  shippingFee?: number;
  // if this is set, it overrides the global shipping fee setting
  adjustedPopshopShippingFee?: number;
  /**
   * The price a buyer pays when shipping with shippo platform.
   *
   * Currently, it could be one of $2.99, $5.99, $9.99, and $15.99
   */
  shippingPrice?: number;
  // can only be true when features.shippoPlatform is enabled
  arrangeMyOwnShippingV2?: boolean;
  userGroupIds?: string[];
  withdrawableBalance?: number;
  withdrawablePendingBalance?: number;
  isLiveStreamStartNotificationDisabled?: boolean;
  features?: {
    commentchat?: boolean;
    commentchatOtherLinkType?: boolean;
    isCommentchatOnboardingOptional?: boolean;
    modelMe?: boolean;
    pickup?: boolean;
    unlimitedStoreFavorites?: boolean;
    shopifyOrderSync?: boolean;
    // set order number format as PS-#{PSL order number}
    shopifyOrderSyncWithPSPrefix?: boolean;
    shopifyWriteDraftOrders?: boolean;
    shopifyListingSyncWithoutSKU?: boolean;
    nftGatedShows?: boolean;
    patreonGatedShows?: boolean;
    // Allow additional Audience data in SD
    customersTab?: boolean;
    // if false, user can't schedule upcoming show or start livestream
    streaming?: boolean;
    voidLabel?: boolean;
    customBadge?: boolean;
    shippoPlatform?: boolean;
    tipping?: boolean;
    captureGroup?: boolean;

    stripeConnect?: boolean;
    commentSoldAccountSync?: boolean;
    // depends on commentSoldAccountSync
    commentSoldProductSync?: boolean;
    // depends on commentSoldProductSync
    commentSoldOrderSync?: boolean;
    commentSoldTax?: boolean;
    /**
     * whether to display dropship UI
     *
     * for creators, this is always true; for other types of sellers, this is false by default unless otherwise manually enabled.
     *
     * note: this feature toggle is only for UI display, it will not affect backend logic.
     */
    dropshipUI?: boolean;

    cloudRecording?: boolean;
    preferReplaysHiddenInApp?: boolean;
    /**
     * default is true
     */
    storeReelPopupOnProfile?: boolean;
    /**
     * default is true
     */
    storeReplayPopupOnProfile?: boolean;
    /**
     * If PSL Sourced (from PSL Catalog) badge should be display where applicable
     */
    showPslSourcedBadge?: boolean;
    // if === false, stores cannot sell products
    orders?: boolean;
    // if === false, do not display storefront widget for this store
    storefrontUI?: boolean;

    // if === false, do not display save theme for this store
    enableThemeCreator?: boolean;
    /**
     * For the US pop store creator: if true, use on_behalf_of for the stripe payment, and enable the card_payment for Stripe connect account
     * For the non-US pop store creator, it alway enable on_behalf_of as default, can not change by this value
     */
    onBehalfOfChargeModeEnabled?: boolean;
    /**
     * Indicates whether the onboarded user is using the new CommentChat Meta onboarding flow.
     * - `true`: User is onboarded with the Meta flow.
     * - `false` or `undefined`: User is onboarded with the legacy PopshopLive flow.
     */
    isCommentChatOnboardingWithMetaEnabled?: boolean;
  };
  // when isDormantSeller turns true, features.streaming turns false and seller dashboard access is blocked. vice versa.
  isDormantSeller?: boolean;
  // history records for isDormantSeller operations, and kept lastest 4 records.
  noteDormantSeller?: string[];
  nftContracts?: string[];
  patreonIdentifier?: string;
  taxIDConfirmed?: boolean;
  notes?: string;
  customBadgeLimit?: number;
  stripeCustomerId?: string;
  shippo?: {
    merchantId: string;
    uspsMasterCarrierId: string;
  };
  commentSoldShop?: {
    id?: string;
    status?: CommentSoldShopStatus;
    createdAt?: AppDate;
    failedAt?: AppDate;
    failureReason?: string;
    // expired after 24 hours, will be recreated after 12 hours using a cronjob
    token?: string;
    tokenUpdatedAt?: AppDate;
    webhook?: {
      success: boolean;
      // e.g. https://api.d.popshop.live
      baseUrl: string;
      errors: string[];
    };
    /**
     * The flag of CommentSold env for PSL in DEV
     */
    commentSoldEnv?: CommentSoldEnv;
  };
  sellerCategoryConfirmed?: boolean;
  /**
   * PSLPay balance migration
   */
  pslPayBalanceMigration?: {
    /** amount for create withdraw transaction of psl available balance */
    withdrawAmount?: number;
    /** amount for transfer balance to stripe custom account from psl pending balance */
    stripeTransferAmount?: number;
    updateAt: AppDate;
  };
  popStorePay?: {
    // delay time (s) for withdraw after successful stripe account setup
    withdrawDelayAfterAccountSetupInSeconds?: number;
    // delay time (s) for withdraw after add/edit payout method
    withdrawDelayAfterUpsertPaymentMethodInSeconds?: number;
    // delay time (s) for balance available after subscribe
    creatorSubscriptionBalanceAvailableDelayInSeconds?: number;
    // delay time (s) for digital download balance available after order create
    digitalDownloadBalanceAvailableDelayInSeconds?: number;
    // delay time (s) for meeting slot balance available after order create
    meetingSlotBalanceAvailableDelayInSeconds?: number;
    // delay time (s) for derived product balance available after order create
    derivedProductBalanceAvailableDelayInSeconds?: number;
  };
  enableStripePaymentElement?: boolean;
}

export enum CommentSoldEnv {
  lima = 'lima',
  hotel = 'hotel',
  qaext = 'qaext',
}

export interface BalanceChangeTrack {
  withdrawableBalance: number;
  withdrawablePendingBalance: number;
}

export enum BalanceOperation {
  CREATE_ORDER = 'CREATE_ORDER',
  CREATE_TIPPING_ORDER = 'CREATE_TIPPING_ORDER',
  SHIPPO_WEB_HOOK = 'SHIPPO_WEB_HOOK',
  COMMENT_SOLD_FULFILL_WEB_HOOK = 'COMMENT_SOLD_FULFILL_WEB_HOOK',
  FINALIZE_ORDER = 'FINALIZE_ORDER',
  REFUND_ORDER = 'REFUND_ORDER',
  CANCEL_ORDER = 'CANCEL_ORDER',
  MANUAL = 'MANUAL',
  WITHDRAW_CREATE = 'WITHDRAW_CREATE',
  WITHDRAW_REJECT = 'WITHDRAW_REJECT',
  WITHDRAW_FAILED = 'WITHDRAW_FAILED',
  DEDUCT_SHIPPING_COSTS = 'DEDUCT_SHIPPING_COSTS',
  REFUND_SHIPPING_COSTS = 'REFUND_SHIPPING_COSTS',
  DEDUCT_SHIPPING_SURCHARGE = 'DEDUCT_SHIPPING_SURCHARGE',
  ORDER_HOLDING_TRANSFERAL = 'ORDER_HOLDING_TRANSFERAL',
  /**
   * Will deprecated when branch(feat/creator-subscription-my-earnings) deployed
   */
  POP_STORE_CREATOR_SUBSCRIPTION = 'POP_STORE_CREATOR_SUBSCRIPTION',
  POP_STORE_CREATOR_SUBSCRIPTION_SUBSCRIBER = 'POP_STORE_CREATOR_SUBSCRIPTION_SUBSCRIBER',
  POP_STORE_CREATOR_SUBSCRIPTION_FAN = 'POP_STORE_CREATOR_SUBSCRIPTION_FAN',
  POP_STORE_CREATOR_SUBSCRIPTION_VIP = 'POP_STORE_CREATOR_SUBSCRIPTION_VIP',
  POP_STORE_CREATOR_DIGITAL_PRODUCT = 'POP_STORE_CREATOR_DIGITAL_PRODUCT',
}

export enum BalanceSourceType {
  WITHDRAW = 'WITHDRAW',
  ORDER = 'ORDER',
  SHIPPING_LABEL = 'SHIPPING_LABEL',
  TIPPING_ORDER = 'TIPPING_ORDER',
  USER = 'USER',
  /**
   * Will deprecated when branch(feat/creator-subscription-my-earnings) deployed
   */
  POP_STORE_CREATOR_SUBSCRIPTION = 'POP_STORE_CREATOR_SUBSCRIPTION',
  POP_STORE_CREATOR_SUBSCRIPTION_SUBSCRIBER = 'POP_STORE_CREATOR_SUBSCRIPTION_SUBSCRIBER',
  POP_STORE_CREATOR_SUBSCRIPTION_FAN = 'POP_STORE_CREATOR_SUBSCRIPTION_FAN',
  POP_STORE_CREATOR_SUBSCRIPTION_VIP = 'POP_STORE_CREATOR_SUBSCRIPTION_VIP',
  POP_STORE_CREATOR_DIGITAL_PRODUCT = 'POP_STORE_CREATOR_DIGITAL_PRODUCT',
}

// /readonlyProfiles/{userId}/balanceChanges/{auto-id}
export interface BalanceChanges extends ModelBase {
  before: BalanceChangeTrack & {
    balanceChangeId: string | null;
    index?: number;
  };
  operation: BalanceOperation;
  withdrawableAmount: number;
  withdrawablePendingAmount: number;
  sourceType: BalanceSourceType;
  sourceId?: string;
  after: BalanceChangeTrack;
  index?: number;
  note?: string;
  noteBy?: string;
}

export enum PayoutHistoryChangeStatus {
  approved = 'approved',
  pending = 'pending',
  rejected = 'rejected',
  canceled = 'canceled',
}

export enum PayoutHistoryRequestSourceType {
  admin = 'admin',
  seller = 'seller',
}
export interface PayoutHistory {
  createdAt: AppDate;
  updatedAt?: AppDate;
  before?: PayoutBank | PayoutVenmo | PayoutPaypal;
  after?: PayoutBank | PayoutVenmo | PayoutPaypal;
  operatorId?: string;
  operatorProfile?: Profile;
  userId?: string;
  profile: Profile;
  status?: PayoutHistoryChangeStatus;
  sourceType?: PayoutHistoryRequestSourceType;
}

export interface DeviceToken {
  token: string;
  registerTime: AppDate;
}

export interface Address {
  firstName: string;
  lastName: string;
  primaryPhone: string;
  email: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  country?: string;
  postalCode: string;
  compareHash?: string;
}

// stores/xx/pickupAddresses/yy
export interface PickupAddress extends Address, ModelBase {
  compareHash: string;
  pickupNote?: string;
  isDefault: boolean;
}

export enum ShippingSolution {
  SHIP_WITH_POPSHOP = 'SHIP_WITH_POPSHOP',
  ARRANGE_MY_OWN_SHIPPING = 'ARRANGE_MY_OWN_SHIPPING',
}

export enum PackageTemplateType {
  /**
   * @deptecated
   */
  Custom = 'CUSTOM',
  /**
   * @deptecated
   */
  Preset = 'PRESET',
  // Global = 'Global',
  GLOBAL = 'GLOBAL',
  SELLER_CUSTOM = 'SELLER_CUSTOM',
  SELLER_ARRANGE_MY_OWN = 'SELLER_ARRANGE_MY_OWN',
}

export enum DistanceUnit {
  'cm' = 'cm',
  'in' = 'in',
  'ft' = 'ft',
  'm' = 'm',
  'mm' = 'mm',
  'yd' = 'yd',
}

export enum MassUnit {
  'g' = 'g',
  'kg' = 'kg',
  'lb' = 'lb',
  'oz' = 'oz',
}

/**
 * @deprecated since 12/06/22
 */
export enum PackageType {
  XS = 'XS',
  Small = 'Small',
  Medium = 'Med',
  L = 'L',
  XL = 'XL',
  XXL = 'XXL',
  Other = 'Other',
}

/**
 * @deprecated since 12/06/22
 */
export enum SignatureConfirmation {
  STANDARD = 'STANDARD',
  ADULT = 'ADULT',
  CERTIFIED = 'CERTIFIED',
  INDIRECT = 'INDIRECT',
  CARRIER_CONFIRMATION = 'CARRIER_CONFIRMATION',
}

/**
 * @deprecated since 12/06/22
 * @description carrierPackageTemplates/{id}
 */
export interface CarrierPackageTemplate extends ModelBase {
  carrier: Carrier;
  name: string;
  token: string;
  width?: number;
  height?: number;
  depth?: number;
  distanceUnit?: DistanceUnit;
  weight: number;
  massUnit: MassUnit;
  materialWeight?: {
    value: number;
    unit: MassUnit;
  };
}

/**
 * @deprecated since 12/06/22
 * @description /carrierPackageTemplates/{autoId}
 */
export interface PresetPackageTemplate extends CarrierPackageTemplate {
  type: PackageTemplateType.Preset;
  isDefault: boolean;
}

/**
 * @deprecated since 12/06/22
 * @description /globalPackageTemplates/{autoId}, these templates are managed by PSL
 */
export interface GlobalPackageTemplate extends ModelBase {
  type: PackageTemplateType.GLOBAL;
  name: string;
  width: number;
  height: number;
  depth: number;
  distanceUnit: DistanceUnit;
  weight?: number;
  massUnit?: MassUnit;
  isDefault: boolean;
  materialWeight?: {
    value: number;
    unit: MassUnit;
  };

  // currently only supports usps
  carrier: Carrier;
  imageUrl?: string;
  description?: string;
  shippingPrice: number;
  shippingCost?: string;
  packageType?: PackageType;
  serviceLevel?: USPSShippingServiceLevel;
  signatureConfirmation?: SignatureConfirmation;
}

/**
 * @deprecated since 12/06/22
 * @description /stores/{storeId}/packageTemplates/{packageTemplateId}, these templates are managed by sellers
 */
export interface CustomPackageTemplate extends ModelBase {
  type: PackageTemplateType.Custom;
  name: string;
  width: number;
  height: number;
  depth: number;
  distanceUnit: DistanceUnit;
  weight?: number;
  massUnit?: MassUnit;
  isDefault: boolean;
  materialWeight?: {
    value: number;
    unit: MassUnit;
  };
  isAutoCalculatePackageWeight?: boolean;
}

export interface PackageTemplateUnit {
  distanceUnit: DistanceUnit;
  massUnit: MassUnit;
}

export interface PackageTemplate extends ModelBase {
  type: PackageTemplateType;
  isDefault: boolean;
  imageUrl?: string;
  name: string;
  description?: string;
  shippingPrice: number;
  shippingCost?: string;
  carrierId?: string;
  carrier?: ShippoCarrierOption;
  parcelTemplateId?: string;
  parcelTemplate?: ShippoCarrierParcelTemplateOption;
  width?: number;
  height?: number;
  depth?: number;
  distanceUnit?: DistanceUnit;
  weight?: number;
  massUnit?: MassUnit;
  // materialWeight?: number;
  // materialMassUnit?: MassUnit;
  signatureConfirmationId?: string;
  signatureConfirmation?: ShippoCarrierShipmentExtrasSignatureConfirmationOption;
  serviceLevelId?: string;
  serviceLevel?: ShippoCarrierPopshopServiceLevelOption;
  // used for global package template
  countUsedAsDefault?: number;
  // used for store package template
  storeId?: string;
  store?: StoreFront;
}

export enum AgencyStatus {
  Active = 'active',
  Disabled = 'disabled',
}
// /agencies/{auto-id}
export interface Agency extends ModelBase {
  logo?: string;
  name: string;
  email: string;
  address: Address;
  status: AgencyStatus;
}

export enum StoreType {
  Business = 'Business',
  Creator = 'Creator',
  // PopStoreCreator = 'PopStoreCreator',
  CommentChat = 'CommentChat',
  VFS = 'VFS',
}

// /agencies/{auto-id}/users/{auto-id}
export interface AgencyUser extends ModelBase {
  avatar?: string;
  name: string;
  email: string;
  role: UserRole.AgencyAdmin | UserRole.AgencyUser;
  isDefault: boolean;
  disabled: boolean;
}

export enum OpenBoxMode {
  FixedDuration = 'FIXED_DURATION',
  FixedTimeOfWeek = 'FIXED_TIME_OF_WEEK',
}

export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

/** UTC day, hour, minute, second of the week */
export interface TimeOfWeek {
  dayOfWeek: DayOfWeek; // 0 - 6
  hour: number; // 0 - 23
  minute: number; // 0 - 59
  second: number; // 0 - 59
}

export interface TippingAmount {
  amount: number;
  // to support different currency in the future
  currency: 'USD';
}

export interface PopStoreCreatorTippingSettings {
  // if tipping is enabled, tipping should be enabled manually by creator.
  enabled?: boolean;
  // minimum amount for tipping, default to system default $1.00
  minimumAmount?: number;
  // tipping button text, default to "Tip Me"
  defaultTippingButtonTitle?: string;
  // predefined tipping amounts
  tippingAmounts?: Array<TippingAmount>;
}

export interface StoreFront {
  name: string;
  passcode: string;
  sellerPasscode?: string;

  // an url short code for the store, from appSellerApplication
  sellerCode?: string;
  profile: Profile;
  createdAt: AppDate;
  storeType?: StoreType;
  tags?: string[];
  // if shippingFee is nil, it is using goshippo.
  // if shippingFee is not nil, it is using custom shipping
  shippingFee?: number;
  // if this is set, it overrides the global shipping fee setting
  // however, the fee in the Storefront is only being used for displaying shipping fee
  // for calculation, use shippingFee field in the Readonly Profile
  adjustedPopshopShippingFee?: number;
  bio?: string;
  announcement?: string;
  salesTaxRate?: { [key: string]: number };
  /**
   * use secondOfShippingLabelGeneration if openBoxMode is undefined or OpenBoxMode.FixedDuration
   * use weekTimeOfShippingLabelGeneration if openBoxMode is OpenBoxMode.FixedTimeOfWeek
   */
  openBoxMode?: OpenBoxMode;
  secondOfShippingLabelGeneration: number;
  weekTimeOfShippingLabelGeneration?: TimeOfWeek;
  upcomingShow?: UpcomingShow;
  upcomingShowId?: string;
  liveStream?: LiveStream;
  liveStreamId?: string;
  // when was the last time the seller started a show
  lastLiveStreamDate?: AppDate;
  evergreenVideo?: {
    videoURL: string;
    previewImageURL: string;
    size: {
      width: number;
      height: number;
    };
  };
  badges?: string[];
  shortDescription?: string;
  /**
   * @deprecated since 09/25/24
   * @description instead of shippingMode
   */
  pickUpEnabled?: boolean;
  // default [ShippingMode.Shipping], and ShippingMode.pickup included when pickUpEnabled is true
  shippingMode?: ShippingMode[];
  showCustomization?: ShowCustomization;
  defaultCoverImage?: string;

  // the dynamic link for referral purposes
  referralLink?: string;

  // the url used for sharing purposes
  sharingUrl?: string;

  // output from bigquery
  gmvT7d?: number; // gmv in past 7 days

  // onboarded by which agency
  // only available for creator and seller, not VFS
  agencyId?: string;
  defaultProductCategoryIds?: string[];
  socialAccounts?: SocialAccount;

  // popstore creator tipping settings.
  tippingSettings?: PopStoreCreatorTippingSettings;
}

export enum MsmStatus {
  approved = 'approved',
  deny = 'deny',
}

export enum CreateOrderSourceType {
  UpcomingShow = 'UPCOMING_SHOW',
  LiveStream = 'LIVE_STREAM',
  Store = 'STORE',
  TwoWayStream = 'TWO_WAY_STREAM',
  ReferralGifting = 'REFERRAL_GIFTING',
  SendItem = 'SEND_ITEM',
  TopProducts = 'TOP_PRODUCTS',
  Shop = 'SHOP',
  Replay = 'REPLAY',
  CommentChat = 'COMMENT_CHAT',
}
export type CreateOrderSource =
  | {
      type: CreateOrderSourceType.UpcomingShow;
      upcomingShowId: string;
    }
  | { type: CreateOrderSourceType.LiveStream; liveStreamId: string }
  | {
      type: CreateOrderSourceType.Store;
      storeId: string;
      liveStreamId?: string;
    }
  | {
      type: CreateOrderSourceType.TwoWayStream;
      liveStreamId: string;
    }
  | {
      type: CreateOrderSourceType.ReferralGifting;
      sentReferralGiftId: string;
    }
  | {
      type: CreateOrderSourceType.SendItem;
      sendItemId: string;
      source:
        | { type: SendItemShowSource.liveStream; liveStreamId: string }
        | { type: SendItemShowSource.upcomingShow; upcomingShowId: string };
    }
  | { type: CreateOrderSourceType.TopProducts }
  | { type: CreateOrderSourceType.Replay; liveStreamId: string }
  | {
      type: CreateOrderSourceType.CommentChat;
      storeId: string;
      postLinkId: string;
    }
  | { type: CreateOrderSourceType.Shop };

export interface CreateOrderV2 extends ComputeOrder {
  paymentMethodId: string;
  paymentType: PaymentType;
  buyerName: string;
  useBonus?: boolean;
  requestTrackingID: string;
  userAgent?: string;
  giftPackage?: {
    isGiftPackage: boolean;
    giftMessage?: string;
  };
}

export interface CreateOrder extends ComputeOrder {
  nonce?: string;
  creditCardId?: string;
  paymentType: PaymentType;
  buyerName: string;
  useBonus?: boolean;
  requestTrackingID: string;
  userAgent?: string;
  giftPackage?: {
    isGiftPackage: boolean;
    giftMessage?: string;
  };
}

export interface ComputeOrder {
  listingId: string;
  sellerId: string;
  sendItemId?: string;
  liveStreamId?: string;
  variantId?: string;
  shippingAddress: Address;
  pickupAddress?: OrderPickupAddress;
  redeemedCouponIds?: string[];
  /** @TJS-type integer */
  quantity: number;
  // giftIds?: string[];
  shippingMethod?: OrderShippingMethod;
  source?: CreateOrderSource;
  recipientId?: string;
  upcomingShowId?: string;
  utmParameters?: Record<string, any>;
}

export interface ComputeOrderOutput {
  buyerPaidAmount: number;
  sellerGotAmount: number;
  newBonusBalance: number;
  shippingMethod: string;
  total: number;
  bonus: number;
  salesTaxAmount: number;
  salesTaxRate: number;
  shippingFee: number;
  couponDiscountShippingFeeAmount: number;
  couponDiscountProductAmount: number;
  couponSellerGotAmount: number;
}

export interface CreateTicketOrder
  extends Pick<
    CreateOrder,
    'nonce' | 'creditCardId' | 'paymentType' | 'buyerName' | 'userAgent'
  > {
  shippingAddress?: Address;
  eventId: string;
  ticketId: string;
}

export interface CreateTicketOrderV2
  extends Pick<
    CreateOrderV2,
    'paymentMethodId' | 'paymentType' | 'buyerName' | 'userAgent'
  > {
  shippingAddress?: Address;
  eventId: string;
  ticketId: string;
}

export enum OrderStatus {
  Created = 'created',
  Completed = 'completed',
  Canceled = 'canceled',
  /**
   * haven't paid order
   */
  PendingPayment = 'pendingPayment',
  /**
   * payment is being processed, but waiting for Stripe or bank confirmation or need further action
   */
  ProcessingPayment = 'processingPayment',
  /**
   * When order hasn't pay in limit time, it will be closed by system
   */
  Closed = 'closed',
}

export enum OrderGroupStatus {
  Open = 'open',
  Processing = 'processing',
  Fulfilling = 'fulfilling',
  Completed = 'completed',
  Canceled = 'canceled',
}

export type CouponCreateMethod = 'sellerProvide' | 'popshopProvide';
// OrderShippingMethod has to be after CouponCreateMethod
// otherwise it will not get generated on Swift side
export type OrderShippingMethod =
  | 'online'
  | 'sellerProvide'
  | 'popshopProvide'
  | 'pickup'
  | 'dropshipSupplierProvide';

export interface SellerTrackingInfo {
  carrier?: string;
  trackingInfo: string; // tracking number
  trackingURL?: string;
  createdAt: AppDate;
  voided?: boolean;
}

export enum Platform {
  PopshopLive = 'popshop.live',
  PopStore = 'pop.store',
}

export interface BaseOrder {
  // default to Platform.PopshopLive if not specified
  platform?: Platform;
  eventId?: string;
  /**
   * @deprecated
   */
  buyerName: string;
  // --------------
  orderNumber: string;
  /** @TJS-type integer */
  quantity: number;
  buyerPaidAmount: number;
  shippingFee: number; // the original shipping fee of the order
  shippingFeeAdjusted: number; // the actual shipping fee with refunds if any
  braintreeShippingFeeRefunded?: boolean;

  salesTaxRate?: number;
  salesTaxAmount?: number;
  total: number; // the total of the original order without any refunds
  buyerId: string;
  buyer: Profile;
  shipment?: Shipment;
  packingSlip?: PackingSlip;
  refund?: number; // how much the refund is, this number will add all the refunds up.
  createdAt: AppDate;
  userAgent?: string;
  /**
   * @deprecated
   */
  orderPayment?: OrderPayment;
  /**
   * @instead of orderPayment exist if buyerPaidAmount > 0
   */
  payment?: Payment;
  coupons?: {
    redeemedCouponId: string;
    couponId: string;
    coupon: Coupon;
    fromHistory?: boolean; // true if this coupon has been used in former orders in the same openbox
  }[];
  couponDiscountProductAmount?: number;
  couponDiscountShippingFeeAmount?: number;
  sellerTrackingInfo?: SellerTrackingInfo;
  sellerTrackingInfoHistory?: SellerTrackingInfo[];
  /**
   * Info of Pending balance to Available balance for Pop Store Creator
   */
  balance?: {
    availableAt: AppDate;
    availableAmount: number;
  };
}

export type OrderPickupAddress = { addressId: string } & Omit<
  PickupAddress,
  'isDefault' | 'createdAt' | 'updatedAt'
>;

export enum OrderPickupStatus {
  Open = 'open',
  New = 'new',
  Processing = 'processing',
  // master order only
  Pending = 'pending',
  Packed = 'packed',
  Completed = 'completed',
  Canceled = 'canceled',
}

export enum OrderCancelStatus {
  SellerApplied = 'seller_applied',
  AdminApproved = 'admin_approved',
}

export enum GiftingStatus {
  Pending = 'PENDING',
  Accepted = 'ACCEPTED',
  Rejected = 'REJECTED',
}

/**
 * @deprecated since 12/06/22
 */
export interface OrderShipmentConfig {
  templateId?: string;
  templateSnapshot?: PackageTemplate;
  parcel?: OrderShipmentParcel | ShipmentTemplateParcel;
  selectedRateId?: {
    default: string;
    userSelected: string;
  };
  serviceLevel?:
    | USPSShippingServiceLevel.USPS_PARCEL_SELECT
    | USPSShippingServiceLevel.USPS_FIRST_CLASS
    | USPSShippingServiceLevel.USPS_PRIORITY
    | USPSShippingServiceLevel.USPS_PRIORITY_EXPRESS;
  rateAttributes?:
    | USPSShippingServiceLevel.CHEAPEST_AVAILABLE
    | USPSShippingServiceLevel.FASTEST_AVAILABLE;
  extra?: { signatureConfirmation?: SignatureConfirmationType };
}

export enum OrderRefundStatus {
  PartiallyRefunded = 'PARTIALLY_REFUNDED',
  FullyRefunded = 'FULLY_REFUNDED',
}

export enum OrderGroupCancelStatus {
  PartiallyCanceled = 'PARTIAL_CANCELED',
  FullyCanceled = 'FULLY_CANCELED',
}

export interface DropshipPartner {
  id: number;
  name: string;
}

export interface DropshipSupplier {
  name: string;
  acceptReturn?: boolean; // default true
  freeShippingThreshold?: number; // default undefined
  supportedForeignCountries?: string[]; // default undefined; value should be country code(https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes)
}

export interface DropshipDetails {
  isDropship: boolean;
  partnerId: number;
  partnerName: string;
  supplierId: number;
  supplierName: string;
  commentSoldProductId: number;
  commentSoldVariantId: number;
}

export interface DropshipAmounts {
  costAmount: number;
  soldAmount: number;
  subtotalPaidByPopshop?: number; // PSL provided listing coupon
  subtotalPaidBySeller?: number; // seller provided listing coupon
  managementRate: number; // 5%
  managementFee: number; // soldAmount * managementRate
  cumulativeListingDiscountedSubTotal: number; // cumulative discounted subtotal in one openbox
  cumulativeBuyerPaidShippingFee: number; // cumulative discounted shipping fee in one openbox
  cumulativeBuyerPaidShippingSalesTaxAmount: number; // cumulative sales tax amount of discounted shipping fee in one openbox
  freeShippingThreshold?: number; // threshold that qualify for free shipping.
  freeShippingDiff?: number; // diff to reatch threshold for free shipping. it equals freeShippingThreshold - cumulativeListingDiscountedSubTotal - [current order listing discounted subtotal], but 0 when it's negative
  freeShippingDiscountAmount?: number; // free shipping policy discount amount
  qualifiedFreeShipping?: boolean; // true when cumulativeListingDiscountedSubTotal >= freeShippingThreshold
  toBeQualifiedFreeShipping?: boolean; // true when cumulativeListingDiscountedSubTotal + discountedSubtotal >= freeShippingThreshold
  shippingFee: number; // dropship supplier receivable shipping fee.
  shippingFeePaidByPopshop?: number; // PSL provided shipping coupon + free shipping policy discount amount
  shippingFeePaidBySeller?: number; // seller provided shipping coupon
  supplierWithdrawableAmount: number; // costAmount + managementFee + shippingFee
}

export interface MultiCurrencyOrderDetails {
  currency: string;
  /**
   * USD to target currency exchange rate
   * For example: if the currency is cad, the exchange rate is 1.35
   */
  exchangeRate: number;
  buyerPaidAmount: number;
  shippingFee: number; // the original shipping fee of the order
  shippingFeeAdjusted: number; // the actual shipping fee with refunds if any
  salesTaxRate?: number;
  salesTaxAmount?: number;
  total: number; // the total of the original order without any refunds
  totalPriceIncludeSlaveOrders: number;
  totalPriceIncludeSlaveOrdersWithProductCoupon: number;
  totalRefundIncludeSlaveOrders?: number;
  refund?: number; // how much the refund is, this number will add all the refunds up.
  couponDiscountProductAmount?: number;
  couponDiscountShippingFeeAmount?: number;
  sellerGotAmount: number;
  bonus?: number;
  orderChargeDistribution?: OrderChargeDistribution | null;
  refunds?: RefundRecord[];
  priceComponents?: OrderPriceComponents;
}

// /orders/{id}
export interface Order extends BaseOrder {
  // orderNumber: string;
  // total: number; // the total of the original order without any refunds
  // buyerPaidAmount: number; // how much the buyer actually paid with refunds if any
  sellerGotAmount: number; // how much the seller actually got with refunds if any
  // shippingFee: number; // the original shipping fee of the order
  // shippingFeeAdjusted: number; // the actual shipping fee with refunds if any
  // salesTaxRate?: number;
  // salesTaxAmount?: number;
  bonus?: number;
  /**
   * if gifting exists, this is the recipient's address, else it's the buyer's address
   */
  shippingAddress: Address;
  pickupAddress?: OrderPickupAddress;
  listingId: string;
  listingSnapshot: Listing;
  sendItemId?: string;
  sendItem?: SendItem;
  sentReferralGiftId?: string;
  sentReferralGift?: SentReferralGift;
  sellerId: string;
  seller: Profile;
  // dropship
  dropshipDetails?: DropshipDetails;

  gifting?: {
    recipientId: string;
    recipient: Profile & { nftImageUrl?: string };
    status: GiftingStatus;
  };

  status: OrderStatus;
  // this field is only available on master order
  orderGroupStatus?: OrderGroupStatus;
  // this field is only available on master order since 2023.08.18
  // business use:
  // 1. two weeks after the order group is closed, if the order is a dropship order, it will
  //    be finalized by the system(a bullmq cronjob) automatically no matter whether an order-fulfilled event is received or not.
  orderGroupClosedAt?: AppDate;
  orderRefundStatus?: OrderRefundStatus;
  orderGroupRefundStatus?: OrderRefundStatus; // master/customer order
  orderGroupCancelStatus?: OrderGroupCancelStatus; // master/customer order
  pickupStatus?: OrderPickupStatus;
  pickupNotifiedAt?: AppDate;
  /**
   * currently used by pickup orders to record whether this order will be canceled
   */
  cancelStatus?: OrderCancelStatus;
  shippingMethod: OrderShippingMethod;
  shippoOrderId?: string;
  liveStreamId?: string | null;
  variantId?: string | null;
  giftIds?: string[];
  /** @ignore */
  gifts?: Gift[];
  store: StoreFront;

  orderShipmentGroupStatus: {
    master: boolean;
    slave: boolean;
    customer: boolean; // shipping label is generated by customer
    legacy: boolean;
  };

  shippingLabelStatus: {
    disabled: boolean; // slave or customer
    noLabel: boolean;
    generating: boolean;
    generated: boolean; // label is generated in a fixed time
    preGenerated: boolean; // label is generated earlier manually by user or admin
    failed: boolean;
  };

  packingSlipStatus: {
    disabled: boolean; // slave or customer
    noLabel: boolean;
    generating: boolean;
    generated: boolean; // label is generated in a fixed time
    preGenerated: boolean; // label is generated earlier manually by user or admin
    failed: boolean;
  };

  // if this is a slave order, it indicates the master
  // order it belongs to
  // if this is not a slave order, it's order id
  masterOrderId?: string;

  // if this is a master order, it indicates the time which
  // start getting the shipping label
  planTimeOfShippingLabelGeneration?: AppDate;

  // it adds all the quantity of the order's items.
  // exp: if master order has 1, slave order1 has quantity of 2
  // and slave order2 has quantity of 3, this number is 1 + 2 + 3 = 6
  /** @TJS-type integer */
  totalQuantitiesIncludeSlaveOrders: number;

  // if this is a master order, the total price from all orders
  // excluding shipping fee
  totalPriceIncludeSlaveOrders: number;

  // if this is a master order, the total price from all orders
  // excluding shipping fee with product coupon
  totalPriceIncludeSlaveOrdersWithProductCoupon: number;

  totalRefundIncludeSlaveOrders?: number;

  newCustomer?: boolean;
  firstPurchaseAt?: AppDate;

  orderCompositionCancellationStatus?: {
    allCancelled: boolean;
    noneCancelled: boolean;
    partiallyCancelled: boolean;
  };
  orderChargeDistribution?: OrderChargeDistribution | null;

  packed?: boolean; // ready to ship
  upcomingShowId?: string;
  giftPackage?: {
    isGiftPackage: boolean;
    giftMessage?: string;
  };
  source?: CreateOrderSource;
  priceComponents?: OrderPriceComponents;
  input?: CreateOrder;
  shipmentParcel?: ShipmentParcel;
  shipmentOriginalParcel?: ShipmentParcel;
  shipmentOriginalParcelSource?: ShipmentOriginalParcelSource;
  refunds?: RefundRecord[];
  /**
   * @deprecated since 12/06/22
   */
  shipmentConfig?: OrderShipmentConfig;
  packageTemplateId?: string;
  packageTemplate?: PackageTemplate;
  presetShippingCost?: ShippingCost;

  /**
   * if true, refund from connect account
   */
  isPslPayOrder?: boolean;

  commentSold?:
    | {
        shopId?: string;
        orderId: number;
        syncAt: AppDate;
        success: true;
        subtotalAdjusted?: boolean;
      }
    | {
        success: false;
        error: string;
      };
  /**
   * regular order or dropship order first time balance available date
   */
  fundAvailableDate?: AppDate;
  /**
   * dropship order second time balance available date
   */
  fundAvailableDate2?: AppDate;
  /**
   * shipping cost of label
   */
  labelShippingCost?: {
    initialCost?: number;
    initialDate?: AppDate;
    totalCost?: number;
    lastUpdatedAt?: AppDate;
  };
  // For commentChat order
  utmParameters?: Record<string, any>;

  calendarEvent?: {
    eventId: string;
    calendarId: string;
    meetingUrl?: string;
  };
  creatorCurrencyOrderDetails?: MultiCurrencyOrderDetails;
  buyerCurrencyOrderDetails?: MultiCurrencyOrderDetails;
  /**
   * for pop store order, the order will be close if not paid in this time
   */
  paymentExpireAt?: AppDate;
  crowdTipping?: {
    // indicates whether the tipping amount is accumulated into Post
    accumulated?: boolean;
  };
}

export interface PackingSlip {
  created: AppDate;
  expires: AppDate;
  slipUrl: string;
}

export enum Carrier {
  SHIPPO = 'SHIPPO', // test only
  USPS = 'USPS',
  UPS = 'UPS',
  CANADA_POST = 'CANADA_POST',
  DHL_EXPRESS = 'DHL_EXPRESS',
  DPD_UK = 'DPD_UK',
  HERMES_UK = 'HERMES_UK',
}

export interface Shipment {
  buyerId: string;
  sellerId: string;
  orderId: string;
  shippoTransactionId: string;
  labelUrl: string;
  trackingNumber: string;
  trackingStatus: string;
  trackingUrlProvider: string;
  amount: number;
  margin?: number;
  transaction: string;
  transactionStatus: string;
  createdAt: AppDate;
  updateAt: AppDate;
  carrier?: Carrier;
  platform?: {
    merchantId: string;
    rateId: string;
  };
  /**
   * when refund and this value not null, make refund on stripe
   */
  stripeTransferId?: string | null;
}

export interface BraintreeTransaction {
  id: string;
  status: string;
  type: string;
  amount: string;
  refundIds: string[];
  refundedTransactionId?: null | string;
  createdAt: string;
  updatedAt: string;
  disputes?: BraintreeDispute[];
  disbursementDetails?: BraintreeDisbursementDetails;
}

/**
 * @deprecated braintree
 */
export interface OrderPayment {
  orderId: string;
  // i.e. transactionId
  braintreeId: string;
  nonce?: string;
  creditCardId?: string;
  paymentType: PaymentType;
  status: 'approved' | 'rejected';
  braintreeStatus?: string;

  transaction?: BraintreeTransaction;
  /** @ignore */
  refundTransactions?: BraintreeTransaction[]; // deprecated
  createdAt: AppDate;
}

/**
 *  instead of braintree
 */

export enum CaptureGroupStatus {
  RequiresPayment = 'requiresPayment',
  RequiresCapture = 'requiresCapture',
  Captured = 'captured',
  Canceled = 'canceled',
  Failed = 'failed',
}

export interface PaymentMethodDetails {
  type?: string;
  brand?: string;
  last4?: string;
  country?: string;
  network?: string;
  wallet?: {
    type?: string;
    dynamicLast4?: string;
  };
}

export interface Payment {
  customerId: string;
  buyerId: string;
  paymentMethodId: string;
  orderId: string;
  createdAt: AppDate;
  intentId: string;
  status:
    | 'canceled'
    | 'processing'
    | 'requires_action'
    | 'requires_capture'
    | 'requires_confirmation'
    | 'requires_payment_method'
    | 'succeeded';
  paymentType: PaymentType;
  captureGroupId?: string; //use capture group
  captureGroupStatus?: CaptureGroupStatus;
  /**
   * The currency of paymentIntent creation
   */
  currency?: string;
  stripeClientSecret?: string | null;
  /**
   * 1 USD to target currency exchange rate
   */
  exchangeRateFromUSD?: number;
  /**
   * Stripe multi-currency exchange rate quote ID, use for creating paymentIntent
   */
  fxQuoteId?: string;
  /**
   * Use to confirm paymentIntent
   */
  stripeConfirmationToken?: string;
  paymentMethodType?: string;
  paymentMethodDetails?: PaymentMethodDetails;
}

export interface ManualTransaction {
  sellerId: string;
  sellerProfile: Profile;
  createdById: string;
  createdByProfile: Profile;
  amount: number;
  balance: number;
  note: string;
  createdAt: AppDate;
  withdrawableBalance?: number;
}

export interface OrderTransaction {
  orderId: string;
  sellerId?: string;
  buyerId: string;
  amount: number;
  createdAt: AppDate;
  updatedAt: AppDate;
  fromBraintree: boolean;
  // if there is not partial refund to braintree, then:
  // 0 == sale + refundBraintree
  // sale and refundBraintree are records between braintree and popshop

  // 0 == shippingFee + refundShippingFee
  // shippingFee and refundShippingFee are records between buyer and popshop
  // when shipping method is popshopProvide
  type:
    | 'sale' // amount we charged from buyer
    | 'refundBraintree' // amount we refunded to buyer
    | 'shippingFee' // popshopProvided shipping, popshop got amount
    | 'refundShippingFee' // popshopProvided shipping, amount is refunded from popshop
    | 'bonus'
    | 'refundBonus'
    | 'productCoupon'
    | 'refundProductCoupon';

  /* suggested change:
  immutableType?:
    | 'sale'
    | 'refundBraintree'
    // ----
    | 'toSellerPendingBalance'
    | 'refundSellerPendingBalance'
    // ----
    | 'toSellerBalance'
    // ----
    | 'toPopshopShippingFee'
    | 'refundPopshopShippingFee';
   */

  // we do not use 'canceled' status
  // completed meanings that money transferred from pending to completed
  status: 'pending' | 'completed' | 'canceled';
  // after order transaction is completed, new balance for seller (not pending)
  /**
   * @deprecated
   */
  sellerBalance?: number;

  braintreeTransaction?: BraintreeTransaction;
}

export enum CouponType {
  discountPercent = 'discountPercent',
  discountAmount = 'discountAmount',
  freeShipping = 'freeShipping',
}

export type Coupon = PercentCoupon | AmountCoupon | FreeShippingCoupon;

export interface BaseCoupon {
  code: string;
  name?: string;
  imageUrl?: string;
  pagUrl?: string;
  description?: string;
  enabled: boolean;
  createdAt: AppDate;
  quantityLimit: boolean;
  quantity?: number | null;
  redeemedQuantity: number;
  startedAt: AppDate;
  expiredAt: AppDate;
  storeIds?: string[];
  store?: StoreFront;
  couponCreateMethod?: CouponCreateMethod;
  onlyPurchasedUser?: boolean;
  onlyNewUser?: boolean;
  onlyNewUserPerStore?: boolean;
  onlyReactivatedUser?: boolean;
  // when true, storeIds is mandatory
  onlyFollower?: boolean;
  // when true, order.shippingMethod must be popshopProvide
  onlyPopshopShipping?: boolean;
  // when onlyAssignToUser.enabled is true, the coupon could only be assigned to user by seller
  onlyAssignToUser?: {
    enabled: boolean;
    userIds: string[];
  };
  userGroupIds?: string[];
  oncePerStore?: boolean;
  sellerGroupIds?: string[];
  followSelectedSellers?: boolean;
  liveStreamIds?: string[];
  // true means this coupon will not display in available coupon list
  visible?: boolean;
  // true, the coupon cannot be used with other coupons
  exclusive?: boolean;
  createdBy?: {
    profileId: string;
    // some admin roles do not have profile record
    profile?: Profile;
  };
  lastUpdatedBy?: {
    profileId: string;
    // some admin roles do not have profile record
    profile?: Profile;
  };
}

export enum ApplicableOrderType {
  OrderGroup = 'OrderGroup',
  // also applies to master order as master order is a special type of child order
  ChildOrder = 'ChildOrder',
}
export interface PercentCoupon extends BaseCoupon {
  type: CouponType.discountPercent;
  discountPercent: number; // 0-100
  maximumDiscount?: number;

  excludedDropshipPartnerIds?: number[];
  applicableOrderType?: ApplicableOrderType;
}

export interface AmountCoupon extends BaseCoupon {
  type: CouponType.discountAmount;
  discountAmount: number; // 5 = $5.00
  minimalPurchase?: number;
}

export interface FreeShippingCoupon extends BaseCoupon {
  type: CouponType.freeShipping;
  minimalPurchase?: number;
  maximumFreeShippingAmount?: number;
  /**
   * - when applied to legacy shippo orders, this is 5.99
   * - when applied to shippo platform orders, this cannot be greater than actual remaining shipping price
   */
  sellerShippingPriceSubsidy?: number;
}

export interface RedeemedCoupon {
  couponId: string;
  coupon: Coupon;
  buyerId: string;
  buyerProfile: Profile;
  createdAt: AppDate;
  usedAt?: AppDate;
  used: boolean;
  orderId?: string;
  targetSellerId?: string;
}

// /coupons/{couponId}/redeemedCoupons/{redeemedCouponId}
export interface CouponRedeemed extends ModelBase {
  buyerId: string;
  buyerProfile: Profile;
  createdAt: AppDate;
  usedAt?: AppDate;
  used: boolean;
  orderId?: string;
  totalRedeemed: number;
}

export type WithdrawTransactionStatus =
  | 'approved'
  | 'rejected'
  | 'pending'
  | 'failed';
/**
 * if pslPay, it is pslPay withdrawal transaction.
 * Present banner on seller dashboard according to this value.
 *
 * if creator, it is creator withdraw transaction
 * this kind of withdraw will be created at monday 00:00:00 weekly automatically
 */
export enum WithdrawTransactionType {
  PslPay = 'pslPay',
  Creator = 'creator',
}
export interface WithdrawTransaction {
  userId: string;
  profile: Profile;
  amount: number;
  balance: number;
  status: WithdrawTransactionStatus;
  note?: string;
  payoutMethodBank?: PayoutBank;
  payoutMethodVenmo?: PayoutVenmo;
  payoutMethodPaypal?: PayoutPaypal;
  createdAt: AppDate;
  updatedAt: AppDate;
  withdrawableAmount?: number;
  withdrawableBalance?: number;
  userAgent?: string;
  idNumber?: number;
  /**
   * if true, it is pslPay withdrawal transaction.
   * Present banner on seller dashboard according to this value.
   */
  isPslPayPayment?: boolean;
  withdrawType?: WithdrawTransactionType;
  /**
   * failure message in Stripe
   */
  failureMessage?: string;
}
export interface DimsInfo {
  //one of these dims is required
  width?: number;
  height?: number;
  depth?: number;
  unit: DistanceUnit;
}
export interface BaseWeightInfo {
  type: 'singleUnit' | 'poundWithOunce';
}
export interface SingleUnitWeightInfo extends BaseWeightInfo {
  type: 'singleUnit';
  value: number;
  unit: MassUnit;
}
export interface PoundWithOunceWeightInfo extends BaseWeightInfo {
  type: 'poundWithOunce';
  lbValue: number;
  ozValue: number;
}
export type WeightInfo = SingleUnitWeightInfo | PoundWithOunceWeightInfo;
export interface ShippingSurchargeInfo {
  amount: number;
  rule: ShippingSurchargeRule;
}
export interface ListingVariant {
  id: string;
  name: string;
  /** @TJS-type integer */
  quantity: number;
  sku?: string;
  price?: number;
  discountPrice?: number;
  hidden?: boolean;

  originalInfo?: {
    type: ListingOriginType;
    shopifyVariantId?: string;
    shopifyInventoryLevelId?: string;
  };

  dims?: DimsInfo;
  weight?: WeightInfo;

  commentSoldVariantId?: number;

  // e.g. {attribute_1: 'red', attribute_2: 'small'}
  // skuAttributes?: {
  //   attribute_1: string | null;
  //   attribute_2: string | null;
  //   attribute_3: string | null;
  //   [key: string]: string | null;
  // };
  skuAttributes?: Record<string, string | null>;

  dropshipDetails?: VariantDropshipDetails;
  externalCheckoutUrl?: string;
}

export enum ListingOriginType {
  Shopify = 'shopify',
}

export enum ListingStatus {
  // The product is ready to sell and is available to customers
  Active = 'active',
  // The product is no longer being sold and isn't available to customers
  Archived = 'archived',
}
export enum ShippingSurchargeRule {
  PerItem = 'PER_ITEM',
  PerOrderGroup = 'PER_ORDER_GROUP',
}

export interface ListingDropshipDetails {
  partnerId: number;
  partnerName: string;
  supplierId: number;
  supplierName: string;
  hasActiveAllocation?: boolean;
  allocationStartDate?: AppDate;
  allocationEndDate?: AppDate;
}
export interface VariantDropshipDetails {
  unitCost: number;
  partnerCommission: number;
  minimumConfigurableRetailPrice: number;
  maximumConfigurableRetailPrice: number;
  /**
   * calculate by charge distribution in PSL
   */
  suggestedMinimumRetailPrice?: number;
}

export interface GoogleProductCategory extends ModelBase {
  name: string;
  // [fooId, barId]
  ancestorIds: string[];
  // barId
  parentId?: string;
  // foo > bar > bar
  fullName: string;
  enabled: boolean;
  isLeaf: boolean;
  isRoot: boolean;
}

// Categories curated by PSL
export interface ProductCategory extends ModelBase {
  // e.g 'Foo > Bar'
  fullName: string;
  name: string;
  // should be unique
  // better to be url-safe
  slug?: string;
  sort: number;
  parentId?: string;
  isRoot: boolean;
  enabled: boolean;
  custom: boolean;
  digital?: boolean;
  googleProductCategoryIds: string[];
}

export enum ProductCollectionStrategy {
  ByCategory = 'BY_CATEGORY',
  ManualSelection = 'MANUAL_SELECTION',
}
export interface ProductCollection extends ModelBase {
  name: string;
  sort: number;
  strategy: ProductCollectionStrategy;
  // valid when strategy is BY_CATEGORY
  productCategoryIds: string[];
  archived: boolean;
}

export enum SeriesLabelFormat {
  Short = 'SHORT',
  Long = 'LONG',
}
export enum SeriesChapterLabelFormat {
  Ch = 'CH', // short form
  Chapter = 'CHAPTER', // long form
}
export enum SeriesElementSortingStrategy {
  MostRecentFirst = 'MOST_RECENT_FIRST',
  MostRecentLast = 'MOST_RECENT_LAST',
  Custom = 'CUSTOM',
}
export enum SeriesType {
  Default = 'DEFAULT',
  Course = 'COURSE',
  SubSeries = 'SUB_SERIES',
}
export interface Series extends ModelBase {
  name: string;
  type?: SeriesType;
  description?: string;

  // available for sub-series
  parentId?: string;

  /*** episode settings ***/
  labelFormat?: SeriesLabelFormat;
  showLabel?: boolean;

  /*** chapter settings ***/
  hasChapter?: boolean;
  chapterLabelFormat?: SeriesChapterLabelFormat;

  /*** marketing banner settings ***/
  mobileBannerImage?: string;
  desktopBannerImage?: string;

  sort: number;
  elementSortingStrategy?: SeriesElementSortingStrategy;
  archived: boolean;

  /*** course price settings ***/
  standaloneSale?: boolean;
  price?: number;
  discountPrice?: number;
  discountEnabled?: boolean;
  discountStartDate?: AppDate;
  discountEndDate?: AppDate;

  // available when standaloneSale is true
  listingId?: string;

  accessLevel?: PopStoreUserAccessLevel;

  /**
   * @deprecated
   */
  popClipIds: string[];
}

export type ClipAuditLog = {
  message?: string;
  userId: string;
  approverName?: string;
  timestamp: string;
  status?: 'Waiting' | 'Approved' | 'Rejected';
};

export type ClipModerationInfo = {
  aiFlagUnderReview?: boolean;
  isContentModerated?: boolean;
  lastReviewedByAi?: boolean;
  aiVerdict?: 'Passed' | 'Failed' | 'Requires Human Intervention';
};

export type PopClipProductInfo = WithID<Listing> & {
  productId?: string;
  shopifyProductId?: string;
  shopifyProductHandle?: string;
  shopifyProductUrl?: string;
};

export enum PopClipStatus {
  Uploading = 'uploading',
  Uploaded = 'uploaded',
  Processed = 'processed',
  Failed = 'failed',
}

export type VideoClipSourceType = 'instagram' | 'tiktok';

export type TranscriptAttachmentInfo = {
  filename: string;
  size: number;
  type: string;
  url: string;
};

export type MediaGalleryObjectTag =
  | 'com.popstore.course.video'
  | 'com.popstore.clips.video'
  | 'com.popstore.post.image'
  | 'com.popstore.post.video'
  | 'com.popshoplive.clips.video';

export interface PopClipMetadata {
  auditLogs?: ClipAuditLog[];
  available?: boolean;
  featured?: boolean;
  gifURL?: string;
  sellerId: string;
  profile: WithID<Profile>;
  userType?: ProfileUserType;
  avatar?: string;
  createdAt: string;
  updatedAt?: string;
  videoTitle?: string;
  videoURL: string;
  processedURL?: string;
  shopifyDomain?: string;
  videoDescription?: string;
  thumbnailURL?: string;
  videoCoverUrl?: string;
  product?: PopClipProductInfo;
  products?: PopClipProductInfo[];
  status?: PopClipStatus;
  isPrivate?: boolean;
  accessLevel?: PopStoreUserAccessLevel;
  showLockedVideoThumbnail?: boolean;
  importedFrom?: VideoClipSourceType;
  externalVideoUrl?: string;
  contentType?: string;
  isStartOfChapter?: boolean;
  previewDuration?: string;
  customThumbnailImageURL?: string;
  customThumbnailMetaidentifier?: string;
  // how can you imagine we get these typings by just inspecting the api response???
  defaultThumbnailImageURL?: string;
  defaultThumbnailImageStatus?: 'processing' | 'processed';
  muteForUnsubscribedUsers?: boolean;
  transcriptAttachment?: TranscriptAttachmentInfo;
  // internal video tag, e.g,. `popstore:course-video`, etc.
  tag?: MediaGalleryObjectTag;
}

export enum ClipFileType {
  Clips = 'clips',
  ShopifyClips = 'shopifyClips',
  PopStoreClips = 'popStoreClips',
  csClips = 'csClips',
}

export type PopClipItemWithId = ClipModerationInfo & {
  id: string; // this actually is the sellerId
  metaIdentifier: string;
  sortKey: string;
  createdAt: string;
  updatedAt?: string;
  isExternal?: boolean;
  fileType: ClipFileType;
  cdnUrl: string;
  status?: PopClipStatus;
  metaData: PopClipMetadata;
};

// /popClips/{sellerId}-{metaIdentifier}
export interface PopClip {
  metaIdentifier: string;
  sellerId: string;
  fileType: 'clips' | 'shopifyClips' | 'popStoreClips' | 'popStorePosts';
  cdnUrl: string;
  metaData: any;
  status?: 'processed' | 'uploaded' | string;
  lastRefreshedAt: AppDate;
  origin: {
    updatedAt: string;
    createdAt?: string;
  };
  // derived from origin.createdAt
  createdAt?: AppDate;
  upvoteCount?: number;
}

export enum SeriesElementType {
  PopClip = 'POP_CLIP',
}
export interface PopClipShopifyProduct {
  defaultImage: string;
  shopifyProductId: string;
  shopifyProductUrl: string;
  shopifyProductHandle: string;
}

export enum SeriesElementAccessLevel {
  ALL = 'all',
  SUBSCRIBER = 'subscriber',
  FAN = 'fan',
  VIP = 'vip',
}
export enum SeriesElementContentType {
  Feed = 'FEED',
  Episode = 'EPISODE',
  Teaser = 'TEASER',
  Promo = 'PROMO',
  Sponsored = 'SPONSORED',
}
export type SeriesElementBase = ModelBase & {
  accessLevel?: SeriesElementAccessLevel;
  pinned?: boolean;
  sort: number;
  rootSeriesId: string;
  startOfChapter?: boolean;
};
// can support other types in the future
export type SeriesElement = SeriesElementBase & {
  type: SeriesElementType.PopClip;
  popClip: PopClip;
  contentType?: SeriesElementContentType;
};

export enum ListingModelMeProcessStatus {
  Triggered = 'TRIGGERED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}

export enum ListingType {
  Physical = 'PHYSICAL',
  Digital = 'DIGITAL',
  External = 'EXTERNAL',
  Meeting = 'MEETING',
  Affiliate = 'AFFILIATE',
  Derived = 'DERIVED',
}
export enum ListingDerivedFrom {
  Series = 'SERIES',
  Post = 'POST',
  Tip = 'TIP',
}
export interface ListingDerivedFromSeries {
  from: ListingDerivedFrom.Series;
  seriesId: string;
}

export interface ListingDerivedFromPost {
  from: ListingDerivedFrom.Post;
  postId: string;
}

export enum TipRecipientType {
  Seller = 'SELLER',
  Post = 'POST',
}
export interface ListingDerivedFromTipToSeller {
  to: TipRecipientType.Seller;
  sellerId: string;
}

export interface ListingDerivedFromTipToPost {
  to: TipRecipientType.Post;
  postId: string;
  sellerId: string;
}
export interface ListingDerivedFromTip {
  from: ListingDerivedFrom.Tip;
  to: ListingDerivedFromTipToSeller | ListingDerivedFromTipToPost;
}

export enum MeetingRepeatFrequency {
  OneTime = 'ONE_TIME',
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY',
  OnWeekdays = 'ON_WEEKDAYS',
}
export interface Listing {
  name: string;
  // support rich text
  description?: string;
  price: number;
  // this take precedence if exist
  discountPrice?: number;
  sku?: string;
  createdAt?: AppDate;
  updatedAt?: AppDate;
  defaultImage: string;
  extraImages?: string[];

  // this value update in PSL
  sizingGuideImage?: string;
  // this value update by CS webhook
  defaultSizingGuideImage?: string;

  /** @TJS-type integer */
  quantity: number;
  variants?: ListingVariant[];
  /** @TJS-type integer */
  maximumPurchaseQuantity?: number | null;
  maximumPurchaseQuantityPerLiveStream?: number;
  shippingFee?: number | null;
  tags?: string[];
  // default to active
  status?: ListingStatus;

  originalInfo?: {
    type: ListingOriginType;
    shopifyStoreId?: string;
    shopifyLocationId?: string;
    shopifyProductId?: string;
  };

  dims?: DimsInfo;
  weight?: WeightInfo;
  shippingSurcharge?: ShippingSurchargeInfo;

  // aggregated sold quantity in the last 7 days
  soldQuantityLast7Days?: number;

  // redundant data for es search
  storeFrontName?: string;
  storeCoverImage?: string;

  commentSoldProductId?: number;
  // available if this listing has no variant
  commentSoldVariantId?: number;
  eligibleForModelMe?: boolean;
  shopModelMe?: boolean;
  // e.g. {attribute_1: 'color', attribute_2: 'size'}
  skuAttributes?: Record<string, string | null>;
  taxCode?: string;
  // skip tax code if nontaxable is true
  nontaxable?: boolean;
  dropshipDetails?: ListingDropshipDetails;
  // For the shopify product, if true, it will not update variant price by Shopify sync
  useLocalPrice?: boolean;

  category?: Omit<ProductCategory, 'createdAt' | 'updatedAt'> & {
    id: string;
  };
  categoryIds?: string[];
  collectionIds?: string[];

  rating?: ListingRating;
  /**
   * The attributes flag updated in PSL.
   * DS will use these flags to determine whether to override product attributes.
   */
  attrsWithLocalUpdates?: {
    name?: boolean;
    price?: boolean; // price will be true, if price or discount price update in PSL
    description?: boolean;
    image?: boolean; // image will be true, if defaultImage or extraImages update in PSL
    category?: boolean;
  };

  modelMeProcessRecord?: { id: string } & ModelMeProcessRecord;
  modelMeImagesStatus?: ListingModelMeImagesStatus;
  // unix milliseconds
  // currently used to avoid stale updates of cs products
  version?: number;

  // default to physical if not specified
  type?: ListingType;
  // valid when type is EXTERNAL
  externalCheckoutUrl?: string;
  externalBrandName?: string;
  externalBrandImage?: string;
  // no rich text support
  // for meeting, this is topic of discussion
  summary?: string;
  discountEnabled?: boolean;
  discountStartDate?: AppDate;
  discountEndDate?: AppDate;
  coverImage?: string;
  thumbnailImage?: string;
  /**
   * use cases:
   * - when listing is of digital type, uploaded file is mandatory
   * - when listing is of meeting type, uploaded file is optional and serves the purpose of email attachment
   */
  uploadedFile?: {
    filename: string;
    sizeInBytes: number;
    mimeType: string;
    // this is the doc id that points to stores/{storeId}/digitalAssets/{docId}
    fileHandle: string;
  };
  moderationStatus?: ModerationStatus;
  moderatedAt?: AppDate;
  moderatorProfile?: Pick<
    Profile,
    'firstName' | 'lastName' | 'username' | 'avatar'
  > & { fullName: string };
  moderatorUserId?: string;
  // admin can provide his own reason for approval or disapproval
  adminModerationReason?: string;

  meeting?: {
    startDate: AppDate;
    endDate: AppDate;
    durationInSeconds: number;
    calendarId?: string;
    eventId?: string;
    timeZone?: string;
    repeatFrequency?: MeetingRepeatFrequency;
    repeatCount?: number;
    // for recurring meeting, minDate is the first startDate of the event,
    // and max date is the last endDate of the event.
    minDate?: AppDate;
    maxDate?: AppDate;
    // milliseconds without time part
    availableDates?: number[];
    // milliseconds without date part
    startTime?: number;
    // milliseconds without date part
    endTime?: number;
    recurrenceRule?: string;
  };
  internalTags?: Array<
    'AddedToStorefrontBefore' | 'PopStore' /*unreliable*/ | string
  >;
  derivedFrom?:
    | ListingDerivedFromSeries
    | ListingDerivedFromPost
    | ListingDerivedFromTip;
}

// immutable
export interface DigitalProductAsset {
  listingId: string;
  createdAt: AppDate;
  fileUrl: string;
  filePath: string;
  mimeType: string;
}

export interface PurchasedDigitalProduct extends ModelBase {
  orderId: string;
  sellerId: string;
  listingId: string;
  buyerId: string;
  filename: string;
  sizeInBytes: number;
  mimeType: string;
  filePath: string; // to generate signed url for download
  signedUrl?: string; // signed url
  downloadedCount: number; // initial value 0
  downloadLimitCount?: number; // to limit download by count, default 3
  downloadEndAt?: AppDate; // to limit download by time, defaut 3 days
}

export enum ListingModelMeImagesStatus {
  Ready = 'READY',
  Approved = 'APPROVED',
  AllApproved = 'ALL_APPROVED',
  AllDismissed = 'ALL_DISMISSED',
}

export interface ModelMeProcessRecord extends ModelBase {
  processId: string; // trigger-modelme-process ID
  storeId: string;
  commentSoldShopId: string;
  email: string; // lower cased
  listingId: string;
  sku: string;
  status: ListingModelMeProcessStatus;
  webhook?: {
    timestamp: AppDate;
    payload: any;
  };
}

export interface ListingRating {
  details: Record<string, number | null>;
}

export interface Rating {
  unifiedSource: string; // userId or (ip + device)
  ip: string;
  device: string;
  score: number;
  userId?: string;
  profile?: Profile; // user profile when available
  message?: string; // optional rating message content
  createdAt: AppDate;
}

// currently there'll be only one doc for each tip collection
// /liveStreams/xx/tips/{autoId}
// /upcomingShows/xx/tips/{autoId}
export interface Tip extends ModelBase {
  // always be 'Tip Jar'
  name: string;
  description?: string;
  minimumAmount: number;
  // always be $1
  stepAmount: number;
  defaultImage: string;
  extraImages?: string[];
}

export interface TippingOrderPriceComponents {
  total: number;
  // currently this is the same as total
  tipperPaidAmount: number;

  recipientGotAmount: number;
  recipientGotWithdrawableAmount: number;

  // defaults to 0.03
  gatewayProcessingFeeRate: number;
  gatewayProcessingFee: number;

  // defaults to 0
  commissionFeeRate: number;
  commissionFee: number;
}

// /tippingOrders/{autoId}
export interface TippingOrder extends ModelBase {
  orderNumber: string;
  priceComponents: TippingOrderPriceComponents;

  tipId: string;
  tip: Tip;

  tipperId: string;
  tipperProfile: Profile;
  recipientId: string;
  recipientProfile: Profile;

  source:
    | { type: 'liveStream'; liveStreamId: string; liveStream: LiveStream }
    | {
        type: 'upcomingShow';
        upcomingShowId: string;
        upcomingShow: UpcomingShow;
      };

  payment?: Payment;
  userAgent?: string;
}

export interface ShopifyProduct {
  title: string;
  imageUrl: string;
  price: string;
  sku?: string;
  description?: string;
  /** @TJS-type integer */
  quantity?: number;
}

export interface ShopifyStore {
  shopName: string;
  accessToken?: string;
  updatedAt: AppDate;
  uuid?: string;
  autoSync?: boolean;
  locationId?: string;
  userId: string;
  writeDraftOrders?: boolean;
}

export interface ShopifySync {
  orderId: string;
  listingId: string;
  varaintId: string;
  quantity: number;
  total: number;
  storeId: string;
  shopifyStoreName: string;
  syncStatus: ShopifySyncStatus;
}

export interface ShopifySyncedOrder {
  orderId: string;
  shopifyOrderId: string;
  shopifyStoreId: string;
}

export interface ShopifyBulkUploadHistory extends ModelBase {
  shopifyStoreId: string;
  shopifyStoreName: string;
  csvUrl: string;
  listingsCount: number;
}

export interface LiveStreamUser {
  userId: string;
  profile: Profile;
  device?: string;
  lastConnect?: AppDate;
  status: LiveStreamStatus;
}

export interface LiveStreamStatus {
  initialized: boolean;
  available: boolean;
  finished: boolean;
  aborted?: boolean;
}

export enum ShippingMode {
  Shipping = 'shipping',
  Pickup = 'pickup',
}
export enum SoldOutAction {
  MoveToBack = 'MoveToBack',
  RemoveFromShow = 'RemoveFromShow',
}

export enum TwoWayStreamStatus {
  CREATED = 'CREATED',
  CONNECTED = 'CONNECTED',
  ENDED = 'ENDED',
}

// liveStreams/liveStreamId/twoWayStreamRecords/{docId}
export interface TwoWayStreamRecord extends ModelBase {
  viewer: Profile & { nftImageUrl?: string };
  viewerId: string;
  status: TwoWayStreamStatus;
  connectedAt?: AppDate;
  endedAt?: AppDate;
}

export enum AgoraCloudRecordingMode {
  individual = 'individual',
  mix = 'mix',
  web = 'web',
}

export enum RecordingResolution {
  R_720X960 = 'R_720X960',
  R_1080X1920 = 'R_1080X1920',
}
export enum ModerationStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  Rejected = 'Rejected',
  // used when AI cannot determine the verdict
  ReviewRequired = 'ReviewRequired',
}
// doc: https://www.notion.so/AI-CONTENT-MODERATION-17ea02f62d0e8074a2b4f18c26f4644e
export interface AiModerationResult {
  video_path: string;
  total_frames_analyzed: number;
  exposed_frames_count: number;
  processing_time: number;
  all_labels: string[];
  exposed_labels: string[];
  frames_with_kids: number;
  critical_frames: number;
  count_critical_g: number;
  count_critical_a: number;
  count_critical_b: number;
  highest_confidence_fg: number;
}
export interface LiveStream {
  badges?: string[];
  primary: LiveStreamUser;
  secondary?: LiveStreamUser;
  createdAt?: AppDate;
  disconnectedAt?: AppDate;
  title: string;
  /**
   * @deprecated no longer need limit number of people in livestream  since 1/29/2022
   */
  mode: 'normal' | 'cap';
  background?: string;
  preview?: string;
  upcomingShow?: UpcomingShow;
  upcomingShowId?: string;
  /**
   * @deprecated replaced by `permission` since 4/9/2021
   */
  visibility?: VisibilityType;
  permission?: ShowPermission;
  hasListing?: boolean;
  hasOrder?: boolean;
  isNftGated?: boolean;
  isPatreonGated?: boolean;
  nftContracts?: string[];
  patreonIdentifier?: string;
  // @deprecated replaced by `cloudRecording` since 9/2022
  recording?: {
    status: 'unknown' | 'recording' | 'recorded' | 'error';
    videos?: {
      url: string;
      size: number;
    }[];
  };
  cloudRecording?: Record<
    RecordingResolution,
    {
      status: 'unknown' | 'recording' | 'recorded' | 'error';
      m3u8Url?: string;
      bitrate?: number;
    }
  >;
  masterPlaylist?: string;
  eventId?: string;
  eventName?: string;
  userGroupIds?: string[];
  eventTemplateId?: string;

  // home page, not event discovery page
  visibilityOfEventShowOnDiscovery?: boolean;
  versionTag?: string;
  wrapUpEmailSentAt?: AppDate;
  // undefined means [ShippingMode.Shipping]
  shippingMode?: ShippingMode[];
  pickupAddress?: { addressId: string } & PickupAddress;
  isDebutShow?: boolean;

  streamPausedText?: string;
  soldOutAction?: SoldOutAction;
  pausedBy?: PausedBy;

  visibilityGroupIds?: string[];

  showCustomization?: ShowCustomization;
  //live stream go live
  startedAt?: AppDate;
  // update of this data might be throttled, so it's an approximate value
  // use case: when a listing is updated, sync it to live stream if the live stream's lastReplayedAt is within 60 minutes
  lastReplayedAt?: AppDate;
  // support upvotes for replay
  upvoteCount?: number;

  moderationStatus?: ModerationStatus;
  moderatedBy?: 'AI' | 'User';
  moderatedAt?: AppDate;
  moderatorProfile?: Pick<
    Profile,
    'firstName' | 'lastName' | 'username' | 'avatar'
  > & { fullName: string };
  moderatorUserId?: string;
  // such as "Spam", "Adult Content", etc
  moderationFlag?: string;
  // user can provide an extra msg
  moderationAdditionalMsg?: string;
  // admin can provide his own reason for approval or disapproval
  adminModerationReason?: string;
  aiStorage?: {
    json_s3_file_key?: string;
    expected_processed_time?: number;
    // e.g: Message Pushed to Moderation Queue
    status?: string;
    error?: string;
    submittedAt?: AppDate;
  };
  // @deprecated, use aiModerationResponse
  aiModerationResult?: AiModerationResult;
  aiModerationResponse?: {
    // e.g.: "Passed"
    aiVerdict?: string;
    isContentModerated?: boolean;
    // e.g.: "Reasoning why it was failed by AI"
    message?: string;
  };
  aiModerationLastError?: any;
  accessLevel?: PopStoreUserAccessLevel;
}

export interface LiveStreamPurchase {
  seller: Profile;
  sellerId: string;
  buyer: Profile;
  buyerId: string;
  isFirstPurchaseFromSeller?: boolean;
  listingId?: string;
  listing?: Listing;
  createdAt: AppDate;
}

// liveStreams/{liveStreamId}/gifts/{giftId}
export interface Gift {
  fromUser: Profile;
  fromUserId: string;
  toUser: Profile;
  toUserId: string;
  listingId: string;
  listing: Listing;
  createdAt: AppDate;
  status: GiftingStatus;
  orderId?: string;
}

export enum SendItemStatus {
  PROCESSING = 'PROCESSING',
  CANCELED = 'CANCELED',
  CONFIRMED = 'CONFIRMED',
  REJECTED = ' REJECTED',
}

export enum SendItemType {
  normal = 'normal',
  liveStreamAuction = 'liveStreamAuction',
  giveaway = 'giveaway',
}

export interface BaseSendItem {
  fromUser: Profile & { nftImageUrl?: string };
  fromUserId: string;
  toUser: Profile & { nftImageUrl?: string };
  toUserId: string;
  listingId: string;
  listing: Listing;
  createdAt: AppDate;
  variantId?: string;
  price?: number;
  updatedAt?: AppDate;
  status: SendItemStatus;
  needShippingFee: boolean;
  quantity: number;
  /** deprecated replaced by new union type since 06/24/22 */
  liveStreamAuction?: {
    liveStreamId: string;
    auctionId: string;
  };
}

export enum SendItemShowSource {
  upcomingShow = 'upcomingShow',
  liveStream = 'liveStream',
}
export interface NormalSendItem extends BaseSendItem {
  type: SendItemType.normal;
  liveStreamId: string;
}
export interface LiveStreamAuctionSendItem extends BaseSendItem {
  type: SendItemType.liveStreamAuction;
  liveStreamAuction: {
    liveStreamId: string;
    auctionId: string;
  };
}
export interface GiveawaySendItem extends BaseSendItem {
  type: SendItemType.giveaway;
  giveawayItem: {
    giveawayItemId: string;
    source: GiveawaySource;
  };
}

export type SendItem =
  | NormalSendItem
  | LiveStreamAuctionSendItem
  | GiveawaySendItem;

export enum GiveawayPrerequisiteEnum {
  IsFollower = 'IsFollower',
  IsCustomer = 'isCustomer',
}
export enum GiveawayCriterionEnum {
  CommentToEnter = 'CommentToEnter',
  ShareToEnter = 'ShareToEnter',
}

export enum GiveawayItemStatus {
  created = 'created',
  sellerPickedWinner = 'sellerPickedWinner',
  /** winner declined this send item and in this case, seller can assign it to another buyer. */
  winnerDeclined = 'winnerDeclined',
  canceled = 'canceled',
  /** finished means winner accepted send item, giveaway flow is ended */
  finished = 'finished',
}
export enum GiveawayItemSourceType {
  upcomingShow = 'upcomingShow',
  liveStream = 'liveStream',
}

export type GiveawayLiveStreamSource = {
  type: GiveawayItemSourceType.liveStream;
  liveStreamId: string;
};
export type GiveawayUpcomingShowSource = {
  type: GiveawayItemSourceType.upcomingShow;
  upcomingShowId: string;
};

export type GiveawaySource =
  | GiveawayLiveStreamSource
  | GiveawayUpcomingShowSource;

export type GiveawayItem = {
  seller: Profile & { nftImageUrl?: string };
  sellerId: string;
  listingId: string;
  listing: Listing;
  variantId?: string;
  price?: number;
  discountPrice?: number;

  status: GiveawayItemStatus;
  quantity: number;

  totalEntrants: number;
  prerequisites?: GiveawayPrerequisiteEnum[];
  criteria?: GiveawayCriterionEnum[];
  entryCloseAt?: AppDate;

  createdAt: AppDate;
  updatedAt?: AppDate;

  source: GiveawaySource;

  coverImages?: string[];
  title?: string;
  description?: string;
};

export interface GiveawayEntrant {
  profile: Profile & { nftImageUrl?: string };
  entryPoint: number;
  entryCriteria?: GiveawayCriterionEnum[];
  entryPrerequisites?: GiveawayPrerequisiteEnum[];
  joinedAt: AppDate;

  source: GiveawaySource;

  isWinner?: boolean;
  winningAt?: AppDate;
  winnerActionAt?: AppDate;
  status?: 'processing' | 'confirm' | 'reject';
}

export interface GiveawayWinner extends GiveawayEntrant {
  isWinner: true;
  winningAt: AppDate;
  winnerActionAt: AppDate;
  status: 'processing' | 'confirm' | 'reject';
}

export interface SellerPasscode {
  passcode: string;
  createdAt: AppDate;
}

// force each model to generate prefixed name of status
export interface PopDummyModel {
  status: 'notused' | 'notused2';
}

export interface SiteSeller {
  firstName: string;
  lastName: string;
  email: string;
  socialMediaUrl?: string;
  storeUrl?: string;
  phoneNumber?: string;
  note?: string;
  createdAt: AppDate;
}

export interface LiveStreamStateCustomer {
  userId: string;
  orderId: string;
  purchasedAt: AppDate;
  firstOrder: boolean;
  firstOrderPurchasedAt: AppDate;
}

export interface LiveStreamState {
  updatedAt?: AppDate;
  gmv?: number;
  gtv?: number;
  apv?: number;
  aov?: number;
  /** @TJS-type integer */
  viewer: number;
  /** @TJS-type integer */
  follower?: number;
  /** @TJS-type integer */
  like?: number;
  /** @TJS-type integer */
  customer?: number;
  /** @TJS-type integer */
  uniqViewer: number;
  uniqViewerInvitedByThisSeller: number;
  /** @TJS-type integer */
  favorite: number;
  /** @TJS-type integer */
  uniqFavorite: number;
  /** @TJS-type integer */
  comment: number;
  /** @TJS-type integer */
  uniqComment: number;
  /** @TJS-type integer */
  uniqEngaged: number;
  engagedRate: number;
  /** @TJS-type integer */
  share: number;
  revenue: number;
  /** @TJS-type integer */
  uniqCustomer: number;
  /** @TJS-type integer */
  returnCustomer: number;
  /** @TJS-type integer */
  order: number;
  duration: number;
  viewedDuration: number;
  liveStream: LiveStream;
  // shipping
  shippingFee: number; // the original shipping fee of the order
  shippingFeeAdjusted: number; // the actual shipping fee with refunds if any
  shippingLabelCount: number;
  //order
  orderCount: number;
  masterOrder: number;
  itemCount: number;
  orderSale: number; // total - shipping fee
  orderRefund: number;
  orderTotal: number; // the total of the original order without any refunds
  buyerPaidAmount: number; // how much the buyer actually paid with refunds if any
  sellerGotAmount: number; // how much the seller actually got with refunds if any
  salesTaxAmount: number;
  // customer
  customers: LiveStreamStateCustomer[];
  newCustomerCount?: number; // the count of customers who made their first purchase
  newHostCustomerCount?: number; // the count of customers who made their first purchase at this seller
  // viewer
  viewerIds: string[];
  viewerCount: number; // the count of viewers
  newViewerCount: number; // the count of viewers who made their first watch
  newHostViewerCount: number; // the count of viewers who made their first watch at this seller
  firstViewAndPurchasedCount: number;
  engagementCount: number;
  total?: number;
  mainOrderCount?: number;
  watchedSecond?: number;
  averageWatchedSecond?: number;
  commissionFee?: number;
  gatewayProcessingFee?: number;
  netRevenue?: number;
  taxAmount?: number;
  canceledPurchaseCount?: number;
  auctionCount?: number;
}

export interface PublicLiveStreamState {
  updatedAt?: AppDate;
  uniqViewer: number;
  /** @TJS-type integer */
  activeViewer?: number;
  /** @TJS-type integer */
  favorite?: number;
  isLive: boolean;
}

export interface Customer {
  profile: Profile;
  address?: Address;
  order?: number;
  /**
   * @deprecated
   */
  revenue?: number;
  totalPaid?: number;
  duration?: number;
  show?: number;
  like?: number;
  share?: number;
  comment?: number;
  firstOrder?: Order;
  firstOrderId?: string;
  secondOrder?: Order;
  secondOrderId?: string;
  lastOrder?: Order;
  lastOrderId?: string;
  hasPurchased: boolean;
  // statistics
  showWatched?: number;
  showPurchased?: number;
  orderTotal?: number;
  buyerPaidAmount?: number;
  itemPurchased?: number;
  orderCount?: number;
  salesTaxAmount?: number;
  shippingFee?: number;
}

export interface FeaturedStore {
  userId: string;
  /**
   * @deprecated replaced by `sortedAt` since 11/30/20
   */
  position: number;
  sortedAt?: AppDate;
  store: StoreFront;
}

export type ReportReason = 'spam' | 'inappropriate' | 'aggressive';

export interface BlockedUser {
  reason: ReportReason | 'seller_block' | 'user_block';
  profile: Profile;
  blockedAt?: AppDate;
}

export interface Report {
  targetId: string;
  targetProfile: Profile;
  reporterId: string;
  reporterProfile: Profile;
  reportFrom: {
    documentId: string;
    documentType:
      | 'LiveStream'
      | 'UpcomingShow'
      | 'Listing'
      | 'Profile'
      | 'VideoClip'
      | 'OrderReview';
  };
  reportContent?: {
    contentType:
      | 'Comment'
      | 'LiveStream'
      | 'UpcomingShow'
      | 'Listing'
      | 'Profile'
      | 'VideoClip'
      | 'OrderReview';
    documentId: string;
    content?: string;
  };
  reason: ReportReason;
  createdAt: AppDate;
}

export interface Hashtag {
  tag: string;
  count: number;
  createdAt: AppDate;
  enabled: boolean;
}

export interface OrderSummary {
  liveStreamId: string;
  userId: string;
  user: Profile;
  status: 'pending' | 'success' | 'failed' | 'closed';
  downloadUrl?: string;
  liveStreamCreatedAt: AppDate;
  createdAt: AppDate;
  updatedAt: AppDate;
  orderCount?: number;
  shippingLabelCount?: number;
  packingSlipCount?: number;
}

export interface TestAccount {
  name: string;
  userId: string;
}

export interface Referral {
  presenteeUserId: string;
  presenteeUserProfile: Profile; // snapshot, value is not updated when modify profile
  referrerUserId: string;
  referrerUserProfile: Profile; // snapshot, value is not updated when modify profile
  passcode: string;
  bonus: number;
  referralBonus: number;
  createdAt: AppDate;
  type?: ReferralType;
}

export type SellerInviteStatus = 'initial' | 'processing' | 'finished';

export interface SellerInvite extends ModelBase {
  presenteeUserProfile: {
    firstName: string;
    lastName: string;
    email: string;
    storeFrontName: string;
    phone?: string;
  };
  referrerUserId: string;
  referrerUserStoreName: string;
  status: SellerInviteStatus;
}

export interface BonusTransaction {
  userId: string;
  userProfile: Profile; // snapshot, value is not updated when modify profile
  type: 'newClient' | 'shopping';
  amount: number;
  balance: number;
  createdAt: AppDate;
  orderId?: string;
  referrerUserId?: string;
  referrerUserProfile?: Profile; // snapshot, value is not updated when modify profile
}

export interface ReferralTransaction {
  userId: string;
  userProfile: Profile; // snapshot, value is not updated when modify profile
  type: 'referral' | 'transfer';
  amount: number;
  balance: number;
  createdAt: AppDate;
  presenteeUserId?: string;
  presenteeUserProfile?: Profile; // snapshot, value is not updated when modify profile
}

export interface SellerMarketing {
  firstName: string;
  lastName: string;
  email: string;
  instagram?: string;
  website?: string;
  phoneNumber?: string;
  createdAt: AppDate;
}

export interface DashboardReport {
  label: string;
  startAt: Date;
  startDate: string;
  endAt: Date;
  endDate: string;
  period: 'daily' | 'weekly' | 'monthly';
  type: string;
  audienceCount: number;
  newAudienceCount: number;
  firstCustomerCount: number;
  firstCustomerPercentage: number;
  newSeller: number;
  newUser: number;
  activeUser: number;
  waitlistCount: number;
  showDuration: number;
  viewedDuration: number;
  showCount: number;
  engagementCount: number;
  averageEngagement: number;
  activeSellerCount: number;
  averageShowPerAudience: number;
  averageViewedDuration: number;
  // shipping label
  shippingLabel: number;
  shippingFee: number;
  // order
  orderCount: number;
  itemPurchased: number;
  orderTotal: number;
  salesTaxAmount: number;
  buyerPaidAmount: number; // how much the buyer actually paid with refunds if any
  sellerGotAmount: number; // how much the seller actually got with refunds if any
  // customer
  customerCount: number;
  newCustomerCount: number;
  repeatCustomerCount: number;
  repeatCustomerPercentage: number;
}

export interface LiveStreamViewerChart {
  date: string;
  dateSeconds: number;
  orderCount: number;
  show: number;
  purchase: number;
  engagement: number;
  viewTime: number;
}

export interface LiveStreamViewer {
  refererUser: Profile;
  referrerStore: StoreFront;
  userId: string;
  user: Profile;
  liveStreamId: string;
  liveStream: LiveStream;
  createdAt: AppDate;
  startedAt: AppDate;
  stoppedAt?: AppDate;
  firstView?: boolean;
  firstHostShow?: boolean;
  // @deprecated this field is never used or updated
  orders?: { orderId: string; order: Order }[];
  hasOrder?: boolean;
  firstHostCustomer?: boolean;
  // refererUserId?: string;
  // refererUser?: Profile;
  // referrerStore?: StoreFront;
  watchMinute?: number;
  liveStreamWatched?: number;
  state: {
    like: number;
    share: number;
    comment: number;
    orderCount: number;
    orderTotal: number;
    buyerPaidAmount: number;
    sellerGotAmount: number;
    engagementCount: number;
    isEngagement?: boolean;
  };
}

export interface SellerReport {
  label?: string;
  startAt?: Date;
  endAt?: Date;
  liveStream: number;
  referral: number;
  follower: number;
  viewer: number;
  uniqViewer: number;
  newUniqViewer: number;
  comment: number;
  like: number;
  orderCount: number;
  orderTotal: number;
  sellerGotAmount: number;
  duration: number;
  viewedDuration: number;
  salesPerMinute: number;
  uniqCustomer: number;
  returnCustomer: number;
}

export enum AppGlobalDocumentID {
  PromotionPage = 'PromotionPage1',
  ScheduleNextShowPage = 'ScheduleNextShowPage',
  CrystalWeekPage = 'PromotionPage0',
  LeaveAppReview = 'LeaveAppReview',
}

export enum ShopifySyncStatus {
  NotStart = 'NotStart',
  Success = 'Success',
  Failed = 'Failed',
}

export interface LeaveAppReview {
  title: string;
  message: string;
  cta: string;
  enabled: boolean;
}

export interface PromotionPage {
  video: string;
  avatar: string;
  title: string;
  coverImage: string;
  enabled: boolean;
  thumbnailNickName: string;
}

export interface ScheduleNextShowPage {
  video: string;
  title: string;
  coverImage: string;
  enabled: boolean;
}

export interface GlobalMessage {
  thumbnail?: string;
  cta?: string;
  title: string;
  message: string;
  enabled: boolean;
  createdAt: AppDate;
}

export enum LiveStreamExperienceRate {
  Beginner = 'BEGINNER',
  Intermediate = 'INTERMEDIATE',
  Pro = 'PRO',
}

export enum AppSellerApplicationType {
  Host = 'HOST',
  Seller = 'SELLER',
}

export interface SocialAccount {
  instagramUrl?: string;
  youtubeUrl?: string;
  depopUrl?: string;
  ebayUrl?: string;
  facebookUrl?: string;
  tiktokUrl?: string;
  etsyUrl?: string;
  discordUrl?: string;
  lemon8Url?: string;
  snapchatUrl?: string;
  twitterUrl?: string;
  patreonUrl?: string;
  linkInBioUrl?: string;
}
export interface AppSellerApplication {
  birthday?: AppDate;
  fullName?: string;
  location?: string;
  whereOnline?: string;
  whatSell?: string;
  whatExperienceProducts?: string;
  whatLiveStream?: string;
  whenSell?: string;
  howHearUs?: string;
  sellerAddress?: Address;
  storeName?: string;
  storeDesc?: string;
  website?: string;
  social?: string;
  sellerDesc?: string;
  sellerHasPhysicalStore?: string;
  categoryIds?: string[];
  /**
   * new fields from join.popshop.live
   */

  /* common fields */
  // @deprecated, in favor of storeType
  applicationType?: AppSellerApplicationType;
  storeType?: StoreType;
  // an url short code for the store
  sellerCode?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  country?: string;
  socialAccounts?: SocialAccount;
  additionalNotes?: string;
  category?: string;

  billingCountryCode?: string;

  /* for seller or business application */
  // something like '10l - 50k' or '500k+'
  businessName?: string;
  totalCommunityReach?: string;
  productTypes?: string[];
  annualSalesRevenue?: string;
  monthlySalesRevenue?: string;
  liveStreamExperience?: string;
  liveStreamExperienceRate?: LiveStreamExperienceRate;
  // Who will be hosting your shows
  hostingOperator?: string;

  /* for host or creator application */
  experienceInRetailSales?: string[];
  interestedInPromoting?: boolean;
  openQuestion?: string;

  // since 2023.12.13
  allowSMS?: boolean;
  /**
   * 1. my own products
   * 2. products dropship catalog
   * 3. both
   */
  productSource?: string;
}

export interface MessageBase {
  type: 'orderVideoMessage' | 'otherMessageType';
  userIdFrom: string;
  userProfileFrom: Profile;
  userIdTo: string;
  userProfileTo: Profile;
  createdAt: AppDate;
}

type OrderVisibilityType = 'public' | 'private';

export type VideoTranscodeStatus =
  | 'none'
  | 'processing'
  | 'transcode'
  | 'error';

export interface OrderVideoMessage extends MessageBase {
  orderId: string;
  buyerId: string;
  sellerId: string;
  videoURL: string;
  originalVideoURL?: string;
  transcodeStatus: VideoTranscodeStatus;
  type: 'orderVideoMessage';
  gifURL?: string;
  thumbnailURL?: string;
  emoji?: OrderVideoReviewEmoji;
  visibility: OrderVisibilityType;
  available: boolean;

  purpose?: MessagePurpose;
  order?: Order;
  show?: {
    showName: string;
    showImage?: string;
    showId: string;
    showType: MessageShowType;
  };
  // review v2 https://www.notion.so/popshoplive/Ready-Reviews-V2-dc66a60157544303a902c6aeb6d43e4f
  upvoteCount?: number;
  totalReportCount?: number;
  newReportCount?: number; //reset newReportCount to 0 when admin dismiss message reports or change message available property
  reportResolveStatus?: MessageReportResolveStatus;
  thankYouType?: MessageThankYouType;
  // if true, will show in top reviews row
  featured?: boolean;
}

export enum MessageReportResolveStatus {
  Unresolved = 'Unresolved',
  Resolved = 'Resolved',
  Dismissed = 'Dismissed',
}
export enum MessageThankYouType {
  RecipientToBuyer = 'RecipientToBuyer',
  SellerToBuyer = 'SellerToBuyer',
}
export enum MessagePurpose {
  review = 'review',
  thankYou = 'thankYou',
}
export enum MessageShowType {
  liveStream = 'liveStream',
  upcomingShow = 'upcomingShow',
}

export type Message = OrderVideoMessage;

export type OrderVideoCreatePayload = Pick<
  OrderVideoMessage,
  'orderId' | 'userIdTo' | 'videoURL' | 'emoji'
  // has to add addition {emoji?} for swiftgen to work
> & { emoji?: OrderVideoReviewEmoji };

export enum OrderVideoReviewEmoji {
  RollingEyesFace = 'rolling_eyes_face',
  Sweat = 'sweat',
  Blush = 'blush',
  Smile = 'smile',
  HeartEyes = 'heart_eyes',
}

export const US_STATES = [
  'AK',
  'AL',
  'AR',
  'AS',
  'AZ',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'GU',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NC',
  'ND',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VA',
  'VI',
  'VT',
  'WA',
  'WI',
  'WV',
  'WY',
];

export interface InvitationInterface {
  code: string;
  sellerIds: string[];
  couponCodes: string[];
  createdAt?: AppDate;
  enabled: boolean;
}

export enum ReferralType {
  seller = 'Seller',
  popshop = 'popshop',
}

export interface TempTransactionData {
  listingId: string;
  sellerId: string;
  soldQuantity: number;
  liveStreamId: string | null;
  variantId: string | null;
  isSynced: boolean;
  sellerGotAmount: number;
  isShippo: boolean;
  isDone: boolean;
  orderId: string;
}

// /userGroups/{groupId}
// /userGroups/{groupId}/users/{userId}
export interface UserGroup {
  name: string;
  description?: string;
  createdAt: AppDate;
  updatedAt?: AppDate;
  active: boolean;
  sendGridListId?: string;
  userType?: ProfileUserType;
}

// /showEvents/{eventId}
export interface ShowEvent {
  name: string;
  urlCode: string;
  previewVideoURL?: string;
  description?: string;
  backgroundURL?: string;
  verticalBackgroundURL?: string;
  active: boolean;
  sellerUserGroupIds: string[];
  // can user see the upcoming show and live show in this event
  // if they can see the show, but not in the user group of the show
  // (UpcomingShow -> userGroupIds) (LiveStream -> userGroupIds)
  // they will show 'require ticket' when open such show.
  // userGroupIds mean that user has purchased the event ticket so they
  // have access to the event.
  userGroupIds: string[];
  createdAt: AppDate;
  salesTaxRate?: number | null;
  maximumPurchaseQuantity?: number | null;
  startedAfter?: AppDate | null;
  endedBefore?: AppDate | null;
  shippingFee?: number;
  isClosed?: boolean;
  // can user see the event in the discovery page
  // only user in this group will see the event
  // users in the visibilityGroupIds will be able to see the event
  // and making ticket purchase
  visibilityGroupIds?: string[] | null;
  sortedAt?: AppDate | null;
  commissionFeeRate?: number | null;
  extraCommissionFeeRate?: number | null;
  featuredStoreStackName?: string | null;
  sellerApplicationDisclosure?: string;
  sellerApplicationDisclosureUrl?: string;

  unBundleOnDiscovery?: boolean;
  visibilityOnDiscoveryBanner?: boolean;
  visibleOnCategoryBanner?: boolean;
  categoryCoverUrl?: string;

  /**
   * @deprecated replaced by `showCustomization`
   */
  customLightingBoltImages?: string[];
  showCustomization?: ShowCustomization;
  showCover?: boolean;
}

export interface EventSettingTemplate
  extends Pick<ShowEvent, 'commissionFeeRate' | 'salesTaxRate' | 'shippingFee'>,
    PrivateShowEvent,
    ModelBase {
  name: string;
  description?: string;
}

// /showEvents/{eventId}/tickets/{ticketId}
export interface ShowTicket
  extends Pick<
    Listing,
    | 'name'
    | 'defaultImage'
    | 'description'
    | 'createdAt'
    | 'price'
    | 'discountPrice'
    | 'sku'
  > {
  active: boolean;
  requireShipping: boolean;
  shippingFee?: number;
  userGroupIds: string[];
  quantity?: number;
  soldQuantity?: number;
  maximumPurchaseQuantity?: number;
  upgradeTicket?: boolean | null;
  fromTicketId?: string | null;
  toTicketId?: string | null;
  emailConfirmationDisclosure?: string;
  emailConfirmationDisclosureUrl?: string;
  emailConfirmationHeaderBackgroundUrl?: string;
  sortedAt?: AppDate;
}

// /showEvents/{eventId}/ticketOrders/{showTicketOrderId}
export interface ShowTicketOrder extends BaseOrder {
  showEventId: string;
  showEvent: ShowEvent;
  showTicketId: string;
  showTicket: ShowTicket;
  shippingAddress?: Address | null;
}
// /readonlyProfiles/{userId}/ticketOrders/{ticketOrderId}/
// -- same as order

// /showEvents/{eventId}/sellerApplications/{id}
// /showEvents/{eventId}/sponsorApplications/{id}
// used for both seller and sponsorApplication
export interface ShowEventSellerApplication {
  createdAt: AppDate;
  sellerId: string;
  seller: Profile;
  status: SellerApplicationStatus;
  sellerUserGroupIds: string[];
  templateId: string;
}

// /showEvents/{eventId}/sponsors/{id}
export interface ShowEventSponsor {
  /**
   * @deprecated replaced by `sortedAt` since 11/30/20
   */
  position: number;
  sortedAt?: AppDate;
  createdAt: AppDate;
  sellerId: string;
  seller: Profile;
  sellerUserGroupIds: string[];
  templateId: string;
}

// /showEvents/{eventId}/sellerApplicationsTemplates/{id}
export interface ShowEventSellerApplicationTemplate {
  name: string;
  createdAt: AppDate;
  sellerUserGroupIds: string[];
  description?: string;
  // eventId: string;
  active: boolean;
  image?: string;
}

// /showEvents/{eventId}/templates/
export interface ShowEventTemplate {
  type: 'timeRange';
  name: string;
  userGroupIds: string[];
  startedAfter: AppDate;
  endedBefore: AppDate;
  createdAt: AppDate;
}

// /liveListings/{id}
export interface LiveListing {
  livedAt: AppDate;
  endLiveAt?: AppDate;
  isActive: boolean;
  sellerId: string;
  sellerAvatar?: string;
  listingId: string;
  listing: Listing;
  liveStreamId: string;
  eventId?: string;
  userGroupIds?: string[];
  createdAt: AppDate;
}
export enum SellerApplicationStatus {
  approved = 'approved',
  pending = 'pending',
  rejected = 'rejected',
}

// /featuredShows/{id}
// /showEvents/{eventId}/featuredShows/{id}
export enum FeaturedShowType {
  upcomingShow = 'upcomingShow',
  liveShow = 'liveShow',
  event = 'event',
}

export enum FeaturedEvent {
  autoFeaturedStoreShow = 'autoFeaturedStoreShow',
}

export interface BaseFeaturedShow {
  sortedAt: AppDate;
  type: FeaturedShowType;
  active: boolean;
  featuredEvent?: FeaturedEvent;
}

export interface UpcomingFeaturedShow extends BaseFeaturedShow {
  type: FeaturedShowType.upcomingShow;
  upcomingShow: UpcomingShow;
}

export interface LiveShowFeaturedShow extends BaseFeaturedShow {
  type: FeaturedShowType.liveShow;
  liveStream: LiveStream;
}
export interface EventFeaturedShow extends BaseFeaturedShow {
  type: FeaturedShowType.event;
  event: ShowEvent;
}

/**
 * when support new ShipmentProvider
 * should change order-shipment.service.ts.getShippoAccount and change firebase config
 */
export enum ShipmentProvider {
  UPS = 'ups',
  USPS = 'usps',
  USPS_FIRST = 'usps_first',
}

export interface PrivateShowEvent {
  parcel: ShipmentParcel;
  shipmentProvider?: ShipmentProvider | null;
}

export interface ShipmentParcel {
  length: string;
  width: string;
  height: string;
  distance_unit: string;
  weight: string;
  mass_unit: string;
}

/**
 * @deprecated since 12/06/22
 */
export interface OrderShipmentParcel {
  length: string;
  width: string;
  height: string;
  distance_unit: string;
  weight?: string;
  mass_unit?: string;
}

/**
 * @deprecated since 12/06/22
 */
export interface ShipmentTemplateParcel {
  distance_unit?: string;
  height?: string;
  length?: string;
  width?: string;

  weight: string;
  mass_unit: string;
  template: string;
}

export enum ShipmentOriginalParcelSource {
  Default = 'DEFAULT',
  ShowEvent = 'SHOW_EVENT',
}

export type FeaturedShow =
  | UpcomingFeaturedShow
  | LiveShowFeaturedShow
  | EventFeaturedShow;

export enum VideoUploadType {
  storePreview = 'storePreview',
}

// /videoUploads/{id}
export interface BaseVideoUpload {
  type: VideoUploadType;
  originalURL: string;
  encodedURL?: string;
  userId: string;
  status: VideoTranscodeStatus;
  error?: string;

  /**** moderation start ***/
  moderationStatus?: ModerationStatus;
  moderatedBy?: 'AI' | 'User';
  moderatedAt?: AppDate;
  moderatorProfile?: Pick<
    Profile,
    'firstName' | 'lastName' | 'username' | 'avatar'
  > & { fullName: string };
  moderatorUserId?: string;
  // such as "Spam", "Adult Content", etc
  moderationFlag?: string;
  // user can provide an extra msg
  moderationAdditionalMsg?: string;
  // admin can provide his own reason for approval or disapproval
  adminModerationReason?: string;
  aiStorage?: {
    json_s3_file_key?: string;
    expected_processed_time?: number;
    // e.g: Message Pushed to Moderation Queue
    status?: string;
    error?: string;
    submittedAt?: AppDate;
  };
  aiModerationResponse?: {
    // e.g.: "Passed"
    aiVerdict?: string;
    isContentModerated?: boolean;
    // e.g.: "Reasoning why it was failed by AI"
    message?: string;
  };
  aiModerationLastError?: any;
  /**** moderation end ***/
}

export interface StorePreviewVideoUpload extends BaseVideoUpload {
  type: VideoUploadType.storePreview;
}

export type VideoUpload = StorePreviewVideoUpload;

export enum OrderBlockReason {
  fraud = 'fraud',
  chargebacks = 'chargebacks',
  multipleAccounts = 'multipleAccounts',
  inappropriateBehavior = 'inappropriateBehavior',
}

export interface PrivateOrderCreationBlocker {
  userProfile: Profile;
  sellerIds: string[];
  reason?: OrderBlockReason;
  createdAt: AppDate;
}

export enum OrderAddressChangeRequest {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Canceled = 'Canceled',
}

export interface OrderAddressChangeHistory {
  orderId: string;
  operator: string;
  createdAt: AppDate;
  reason: string;
  rejectReason?: string;
  beforeSnapshot: Address;
  afterSnapShot: Address;
  status: OrderAddressChangeRequest;
  shipmentSnapshot?: Shipment;
  sellerTrackingInfoSnapshot?: SellerTrackingInfo;
}

export interface DashboardFeedback {
  type: 'dashboard';
  topic?: { id: string; topic: 'order' | 'liveStream' };
  content: {
    message: string;
    extra?: string | null; // UUID
    imageUrls?: string[];
    videoUrls?: string[];
  };
  userId: string;
  status: 'initial' | 'processing' | 'finished';
  createdAt: AppDate;
  // must use signed doc id
  forceSign?: boolean;
  signedDocId?: string;
  creatorId?: string;
}

export enum ShippingFeePayer {
  Buyer = 'Buyer',
  Seller = 'Seller',
  Popshop = 'Popshop',
}

export interface OrderChargeDistribution {
  commissionFeeRate: number;
  commissionFee: number;
  gatewayProcessingFeeRate: number;
  gatewayProcessingFeeExtra?: number;
  gatewayProcessingFee: number;
  sellerWithdrawableAmount: number;
  listingQuantity?: number;
  listingDiscountedSubTotal?: number;
  sellerSubtotalPaidByPopshopToSeller?: number;
  shipmentDiscountedShippingFee?: number;
  // with new ds order holding logic, this is the amount of money that has been transferred to the seller
  // note that when finalizing a ds order, the remaining amount will be `sellerWithdrawableAmount-transferredAmount`
  transferredAmount?: number;
  buyerPaidAmount: number;
  shippingSalesTaxAmount?: number;
  shippingSalesTaxRate?: number;
  listingSalesTaxAmount?: number;
  listingSalesTaxRate?: number;
  salesTaxAmount: number;
  salesTaxRate: number;
  sellerGotAmount?: number;
  couponSellerGotAmount?: number;
  listingCouponSellerGotAmount?: number;
  shippingCouponSellerGotAmount?: number;
  bonus: number;
  couponDiscountProductAmount: number;
  couponDiscountProductAmountProvidedByPopshop?: number;
  couponDiscountProductAmountProvidedBySeller?: number;
  couponDiscountShippingFeeAmount?: number;
  couponDiscountShippingFeeAmountProvidedByPopshop?: number;
  couponDiscountShippingFeeAmountProvidedBySeller?: number;
  shippingFeePayer?: ShippingFeePayer;
  popshopShippingFee?: number;
  popshopShippingFeePaidBySellerToPopshop?: number;
  popshopShippingFeePaidByPopshopToSeller?: number;
  sellerShippingFee?: number;
  sellerShippingFeePaidByPopshopToSeller?: number;
  referralGiftingDiscountSubtotal?: number;
  referralGiftingDiscountShippingFee?: number;
  referralGiftingDiscountShippingSalesTaxAmount?: number;
  referralGiftingDiscountListingSalesTaxAmount?: number;
  referralGiftingDiscountSalesTaxAmount?: number;
  // dropship
  dropshipAmounts?: Pick<
    DropshipAmounts,
    | 'costAmount'
    | 'soldAmount'
    | 'subtotalPaidByPopshop'
    | 'subtotalPaidBySeller'
    | 'managementFee'
    | 'freeShippingDiscountAmount'
    | 'shippingFee'
    | 'shippingFeePaidByPopshop'
    | 'shippingFeePaidBySeller'
    | 'supplierWithdrawableAmount'
  >;
}

export interface SellerAdminNotification {
  createdAt: AppDate;
  read: boolean;
  readAt?: AppDate;
  type: 'download' | 'feedback' | 'sendItem';
  title?: string;
  content: string;
  from: string;
  to: string;
}

export interface AsyncTask {
  createdAt: AppDate;
  updatedAt: AppDate;
  requestPayload: string;
  responsePayload: string;
  type:
    | 'ordersLabelsDownload'
    | 'ordersPackingSlipDownload'
    | 'ordersShipmentLabelDownload';
  creatorId: string;
  status: 'notStarted' | 'inProgress' | 'completed' | 'failed';
  percentageComplete: number;
}

export interface UpcomingShowWatcher {
  createdAt?: AppDate;
  isFollower?: boolean;
  user: Profile;
}

export enum EmailTemplate {
  'eventPurchase' = 'eventPurchase',
  'noShippoBuyer' = 'noShippoBuyer',
  'orderBuyer' = 'orderBuyer',
  'orderSeller' = 'orderSeller',
  'orderShipped' = 'orderShipped',
  'orderSummary' = 'orderSummary',
  'refund' = 'refund',
  'transferApproved' = 'transferApproved',
  'transferInitiated' = 'transferInitiated',
  'tuneIn' = 'tuneIn',
  'vendorApplication' = 'vendorApplication',
  'welcome' = 'welcome',
}

export interface PackingSlipLineItem {
  quantity: number;
  sku?: string;
  title: string;
  total_price: number; //product price
  currency: string;
  variant_name?: string;
  coupon_discount_product_amount?: number;
  coupon_codes?: string;
  order_number: string;
  created_at: AppDate;
}

// /packingShips/{orderId}/

export interface PackingSlipInfo {
  to_address: PackingSlipAddress;
  from_address: PackingSlipAddress;
  placed_at: AppDate;
  order_number: string;
  line_items: PackingSlipLineItem[];
  shipping_cost: number;
  shipping_cost_currency: string;
  subtotal_price: number;
  total_price: number;
  total_tax: number;
  currency: string;
  weight: string;
  weight_unit: string;
  seller_name: string;
  buyer_name: string;
  sku?: string;
  isPickup: boolean;
}

export interface PackingSlipAddress {
  name: string;
  phone: string;
  email: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export type CreateSendItemInput = {
  liveStreamId: string;
  listingId: string;
  variantId?: string;
  to: string;
  quantity?: number;
  needShippingFee: boolean;
  price?: number;
  noNeedNotification?: boolean;
  auctionId?: string;
};

export type CloseSendItemInput = {
  id: string;
  liveStreamId: string;
  noNeedNotification?: boolean;
};

export interface ShipmentTrackingRecord {
  createdAt: AppDate;
  orderId: string;
  sellerId: string;
  buyerId: string;
  trackingNumber?: string;
  isRemoved?: boolean;
  voided?: boolean;
  trackingUrlProvider?: string;
  carrier?: string;
  status?: string;
  transactionId?: string;
}

export type ISO8601DateTimeString = string;
export type ISO3166Alpha2CountryCode = string;
export type ISO4217CurrencyCode = string;

// shippo enums for managing package template
export enum ShippoCarrierToken {
  USPS = 'usps',
}

export enum ShippoCarrierName {
  USPS = 'USPS',
}

export interface ShippoCarrierOption {
  ordinal: number; // for sorting
  token: ShippoCarrierToken;
  name: ShippoCarrierName;
  alias?: string; // its alias from popshop
}

export enum ShippoCarrierPopshopParcelTemplateType {
  CUSTOM = 'CUSTOM',
  SHIPPO_CARRIER_PARCEL_TEMPLATE = 'SHIPPO_CARRIER_PARCEL_TEMPLATE',
}

export enum ShippoCarrierParcelTemplateId {
  ENVELOPE = 'Envelope',
  PACKAGE = 'Package',
  USPS_REGIONAL_RATE_BOX_1 = 'USPS_RegionalRateBoxB1',
  USPS_REGIONAL_RATE_BOX_2 = 'USPS_RegionalRateBoxB2',
  USPS_IRREGULAR_PARCEL = 'USPS_IrregularParcel',
  USPS_CARD = 'USPS_Card',
  USPS_FLAT_RATE_CARDBOARD_ENVELOPE = 'USPS_FlatRateCardboardEnvelope',
  USPS_FLAT_RATE_LEGAL_ENVELOPE = 'USPS_FlatRateLegalEnvelope',
  USPS_FLAT_RATE_ENVELOPE = 'USPS_FlatRateEnvelope',
  USPS_FLAT_RATE_GIFT_CARD_ENVELOPE = 'USPS_FlatRateGiftCardEnvelope',
  USPS_LARGE_VIDEO_FLAT_RATE_BOX = 'USPS_LargeVideoFlatRateBox',
  USPS_LARGE_FLAT_RATE_BOARD_GAME_BOX = 'USPS_LargeFlatRateBoardGameBox',
  USPS_SMALL_FLAT_RATE_ENVELOPE = 'USPS_SmallFlatRateEnvelope',
  USPS_SOFT_PACK = 'USPS_SoftPack',
  USPS_LARGE_FLAT_RATE_BOX = 'USPS_LargeFlatRateBox',
  USPS_MEDIUM_FLAT_RATE_BOX_1 = 'USPS_MediumFlatRateBox1',
  USPS_MEDIUM_FLAT_RATE_BOX_2 = 'USPS_MediumFlatRateBox2',
  USPS_SMALL_FLAT_RATE_BOX = 'USPS_SmallFlatRateBox',
  USPS_REGIONAL_RATE_BOX_A1 = 'USPS_RegionalRateBoxA1',
  USPS_REGIONAL_RATE_BOX_A2 = 'USPS_RegionalRateBoxA2',
  USPS_APO_FLAT_RATE_BOX = 'USPS_APOFlatRateBox',
  USPS_FLAT_RATE_WINDOW_ENVELOPE = 'USPS_FlatRateWindowEnvelope',
  USPS_FLAT_RATE_PADDED_ENVELOPE = 'USPS_FlatRatePaddedEnvelope',
}

export enum ShippoCarrierParcelTemplateToken {
  USPS_REGIONAL_RATE_BOX_1 = 'USPS_RegionalRateBoxB1',
  USPS_REGIONAL_RATE_BOX_2 = 'USPS_RegionalRateBoxB2',
  USPS_IRREGULAR_PARCEL = 'USPS_IrregularParcel',
  USPS_CARD = 'USPS_Card',
  USPS_FLAT_RATE_CARDBOARD_ENVELOPE = 'USPS_FlatRateCardboardEnvelope',
  USPS_FLAT_RATE_LEGAL_ENVELOPE = 'USPS_FlatRateLegalEnvelope',
  USPS_FLAT_RATE_ENVELOPE = 'USPS_FlatRateEnvelope',
  USPS_FLAT_RATE_GIFT_CARD_ENVELOPE = 'USPS_FlatRateGiftCardEnvelope',
  USPS_LARGE_VIDEO_FLAT_RATE_BOX = 'USPS_LargeVideoFlatRateBox',
  USPS_LARGE_FLAT_RATE_BOARD_GAME_BOX = 'USPS_LargeFlatRateBoardGameBox',
  USPS_SMALL_FLAT_RATE_ENVELOPE = 'USPS_SmallFlatRateEnvelope',
  USPS_SOFT_PACK = 'USPS_SoftPack',
  USPS_LARGE_FLAT_RATE_BOX = 'USPS_LargeFlatRateBox',
  USPS_MEDIUM_FLAT_RATE_BOX_1 = 'USPS_MediumFlatRateBox1',
  USPS_MEDIUM_FLAT_RATE_BOX_2 = 'USPS_MediumFlatRateBox2',
  USPS_SMALL_FLAT_RATE_BOX = 'USPS_SmallFlatRateBox',
  USPS_REGIONAL_RATE_BOX_A1 = 'USPS_RegionalRateBoxA1',
  USPS_REGIONAL_RATE_BOX_A2 = 'USPS_RegionalRateBoxA2',
  USPS_APO_FLAT_RATE_BOX = 'USPS_APOFlatRateBox',
  USPS_FLAT_RATE_WINDOW_ENVELOPE = 'USPS_FlatRateWindowEnvelope',
  USPS_FLAT_RATE_PADDED_ENVELOPE = 'USPS_FlatRatePaddedEnvelope',
}

export enum ShippoCarrierParcelTemplateName {
  ENVELOPE = 'Envelope',
  PACKAGE = 'Package',
  USPS_REGIONAL_RATE_BOX_1 = 'Regional Rate Box B1',
  USPS_REGIONAL_RATE_BOX_2 = 'Regional Rate Box B2',
  USPS_IRREGULAR_PARCEL = 'Irregular Parcel',
  USPS_CARD = 'Card',
  USPS_FLAT_RATE_CARDBOARD_ENVELOPE = 'Flat Rate Cardboard Envelope',
  USPS_FLAT_RATE_LEGAL_ENVELOPE = 'Flat Rate Legal Envelope',
  USPS_FLAT_RATE_ENVELOPE = 'Flat Rate Envelope',
  USPS_FLAT_RATE_GIFT_CARD_ENVELOPE = 'Flat Rate Gift Card Envelope',
  USPS_LARGE_VIDEO_FLAT_RATE_BOX = "Flat Rate Large Video Box (Int'l only)",
  USPS_LARGE_FLAT_RATE_BOARD_GAME_BOX = 'Large Flat Rate Board Game Box',
  USPS_SMALL_FLAT_RATE_ENVELOPE = 'Small Flat Rate Envelope',
  USPS_SOFT_PACK = 'Soft Pack Padded Envelope',
  USPS_LARGE_FLAT_RATE_BOX = 'Large Flat Rate Box',
  USPS_MEDIUM_FLAT_RATE_BOX_1 = 'Medium Flat Rate Box 1',
  USPS_MEDIUM_FLAT_RATE_BOX_2 = 'Medium Flat Rate Box 2',
  USPS_SMALL_FLAT_RATE_BOX = 'Small Flat Rate Box',
  USPS_REGIONAL_RATE_BOX_A1 = 'Regional Rate Box A1',
  USPS_REGIONAL_RATE_BOX_A2 = 'Regional Rate Box A2',
  USPS_APO_FLAT_RATE_BOX = 'APO/FPO/DPO Large Flat Rate Box',
  USPS_FLAT_RATE_WINDOW_ENVELOPE = 'Flat Rate Window Envelope',
  USPS_FLAT_RATE_PADDED_ENVELOPE = 'Flat Rate Padded Envelope',
}

export interface ShippoCarrierParcelTemplateOption {
  ordinal: number; // for sorting
  type: ShippoCarrierPopshopParcelTemplateType;
  token?: ShippoCarrierParcelTemplateToken;
  name: ShippoCarrierParcelTemplateName;
  alias?: string; // its alias from popshop
  width?: number;
  height?: number;
  depth?: number;
  distanceUnit?: DistanceUnit;
}

export enum ShippoCarrierShipmentExtrasSignatureConfirmationToken {
  STANDARD = 'STANDARD',
  ADULT = 'ADULT',
  CERTIFIED = 'CERTIFIED', // USPS only
}

export enum ShippoCarrierShipmentExtrasSignatureConfirmationName {
  STANDARD = 'Signature Confirmation',
  ADULT = 'Adult Signature Required',
  CERTIFIED = 'Certified Mail', // USPS only
}

export interface ShippoCarrierShipmentExtrasSignatureConfirmationOption {
  ordinal: number; // for sorting
  token: ShippoCarrierShipmentExtrasSignatureConfirmationToken;
  name: ShippoCarrierShipmentExtrasSignatureConfirmationName;
  alias?: string; // its alias from popshop
}

export enum ShippoCarrierServiceLevelToken {
  // USPS_FIRST = 'usps_first',
  // USPS_PARCEL_SELECT = 'usps_parcel_select',
  USPS_GROUND_ADVANTAGE = 'usps_ground_advantage',
  USPS_PRIORITY = 'usps_priority',
  USPS_PRIORITY_EXPRESS = 'usps_priority_express',
}

export enum ShippoCarrierServiceLevelName {
  // USPS_FIRST = 'First Class Mail',
  // USPS_PARCEL_SELECT = 'Parcel Select',
  USPS_GROUND_ADVANTAGE = 'Ground Advantage',
  USPS_PRIORITY = 'Priority Mail',
  USPS_PRIORITY_EXPRESS = 'Priority Mail Express',
}

export enum ShippoCarrierPopshopServiceLevelType {
  SHIPPO_RATE_ATTRIBUTE = 'SHIPPO_RATE_ATTRIBUTE',
  SHIPPO_CARRIER_SERVICE_LEVEL = 'SHIPPO_CARRIER_SERVICE_LEVEL',
}

export enum ShippoCarrierPopshopServiceLevelToken {
  CHEAPEST = 'CHEAPEST',
  FASTEST = 'FASTEST',
  // USPS_PARCEL_SELECT = 'usps_parcel_select',
  // USPS_FIRST = 'usps_first',
  USPS_GROUND_ADVANTAGE = 'usps_ground_advantage',
  USPS_PRIORITY = 'usps_priority',
  USPS_PRIORITY_EXPRESS = 'usps_priority_express',
}

export enum ShippoCarrierPopshopServiceLevelName {
  CHEAPEST = 'CHEAPEST',
  FASTEST = 'FASTEST',
  // USPS_PARCEL_SELECT = 'Parcel Select',
  // USPS_FIRST = 'First Class Mail',
  USPS_GROUND_ADVANTAGE = 'Ground Advantage',
  USPS_PRIORITY = 'Priority Mail',
  USPS_PRIORITY_EXPRESS = 'Priority Mail Express',
}

export interface ShippoCarrierPopshopServiceLevelOption {
  ordinal: number; // for sorting
  type: ShippoCarrierPopshopServiceLevelType;
  token: ShippoCarrierPopshopServiceLevelToken;
  name: ShippoCarrierPopshopServiceLevelName;
  alias?: string; // its alias from popshop
}

// shippo objects returned from calling shippo API
export interface ShippoAddress {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  name: string;
  company: string;
  country: ISO3166Alpha2CountryCode;
  state: string;
  city: string;
  street_no: string;
  street1: string;
  street2: string;
  street3: string;
  zip: string;
  phone: string;
  email: string;
  is_complete: boolean;
  is_residential?: boolean;
  metadata: string;
  test: boolean;
  validate?: boolean | null;
  validation_results: {
    is_valid?: boolean;
    messages?: {
      source?: string;
      code?: string;
      type?: string;
      text?: string;
    }[];
  };
}

export enum ShippoParcelState {
  VALID = 'VALID',
}

export interface ShippoParcel {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  object_state: ShippoParcelState;
  length: string; // decimal (10,4)
  width: string; // decimal (10,4)
  height: string; // decimal (10,4)
  distance_unit: DistanceUnit;
  weight: string; // decimal (10,4)
  mass_unit: MassUnit;
  value_amount: string | null;
  value_currency: ISO4217CurrencyCode | null;
  metadata: string;
  template: string | null; // parcel template(https://goshippo.com/docs/reference#parcel-templates)
  extra: {
    COD?: {
      amount: string;
      currency: ISO4217CurrencyCode;
      payment_method?: 'SECURED_FUNDS' | 'CASH' | 'ANY';
    };
    insurance?: {
      amount: string;
      currency: ISO4217CurrencyCode;
      provider?: 'FEDEX' | 'UPS' | 'UDS';
      content?: string;
    };
  }; // parcel extra(https://goshippo.com/docs/reference#parcels-extras)
  line_items: any[];
  test: boolean;
}

export interface ShippoServicelevel {
  name?: string;
  token?: string;
  terms?: string;
  extended_token?: string;
  parent_servicelevel?: ShippoServicelevel | null;
}

export enum ShippoRateAttribute {
  CHEAPEST = 'CHEAPEST',
  FASTEST = 'FASTEST',
  BESTVALUE = 'BESTVALUE',
}

export interface ShippoRate {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  shipment: string; // shippo shipment object ID
  amount: string;
  currency: string;
  amount_local: ISO4217CurrencyCode;
  currency_local: ISO4217CurrencyCode;
  provider: string;
  provider_image_75: string;
  provider_image_200: string;
  servicelevel: ShippoServicelevel;
  estimated_days: number;
  duration_terms: string;
  carrier_account: string; // shippo carrier object ID
  arrives_by: any;
  attributes: ShippoRateAttribute[];
  messages: {
    code?: string; // an identifier for the corresponding message (not always available)
    message?: string; // a publishable message containing further information
  }[];
  test: boolean;
  zone: string;
}

export enum ShippoShipmentStatus {
  WAITING = 'WAITING',
  QUEUED = 'QUEUED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export enum ShippoShipmentExtraSignatureConfirmation {
  STANDARD = 'STANDARD',
  ADULT = 'ADULT',
  CERTIFIED = 'CERTIFIED', // USPS only
  INDIRECT = 'INDIRECT', // FedEx only
  CARRIER_CONFIRMATION = 'CARRIER_CONFIRMATION', // Deutsche Post only
}

export interface ShippoShipment {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  status: ShippoShipmentStatus;
  address_return: Address;
  address_from: Address;
  address_to: Address;
  alternate_address_to: Address | null;
  parcels: ShippoParcel[];
  shipment_date: ISO8601DateTimeString;
  customs_declaration: string | null; // shippo customs declarations object ID
  carrier_accounts: string[]; // shippo carrier object ID
  async: boolean;
  metadata: string;
  extra: {
    signature_confirmation?: ShippoShipmentExtraSignatureConfirmation;
    authority_to_leave?: boolean;
    saturday_delivery?: boolean;
    bypass_address_validation?: boolean;
    request_retail_rates?: boolean;
    customer_branch?: string;
    premium?: boolean;
    preferred_delivery_timeframe?:
      | '10001200'
      | '12001400'
      | '14001600'
      | '16001800'
      | '18002000'
      | '19002100';
    lasership_attrs?:
      | 'TwoPersonDelivery'
      | 'Explosive'
      | 'Hazmat'
      | 'ControlledSubstance'
      | 'Refrigerated'
      | 'Perishable'
      | 'NoRTS';
    lasership_declared_value?: string;
    container_type?: string;
    billing?: {
      type?: 'SENDER' | 'RECIPIENT' | 'THIRD_PARTY' | 'THIRD_PARTY_CONSIGNEE';
      account?: string;
      zip?: string;
      country?: ISO3166Alpha2CountryCode;
      participation_code?: string;
    };
    COD?: {
      amount?: string;
      currency?: ISO4217CurrencyCode;
      payment_method?: 'SECURED_FUNDS' | 'CASH' | 'ANY';
    };
    alcohol?: {
      contains_alcohol?: boolean;
      recipient_type?: 'licensee' | 'consumer';
    };
    dry_ice?: {
      contains_dry_ice?: boolean;
      weight?: string;
    };
    insurance?: {
      amount?: string;
      currency?: ISO4217CurrencyCode;
      content?: string;
      provider?: 'FEDEX' | 'UPS' | 'ONTRAC';
    };
    dangerous_goods_code?:
      | '01'
      | '02'
      | '03'
      | '04'
      | '05'
      | '06'
      | '07'
      | '08'
      | '09';
    is_return?: boolean;
    reference_1?: string;
    reference_2?: string;
    return_service_type?:
      | 'PRINT_AND_MAIL'
      | 'ATTEMPT_1'
      | 'ATTEMPT_3'
      | 'ELECTRONIC_LABEL';
    qr_code_requested?: boolean;
    ancillary_endorsement?:
      | 'FORWARDING_SERVICE_REQUESTED'
      | 'RETURN_SERVICE_REQUESTED';
    carbon_neutral?: boolean;
    delivery_instructions?: string;
    fulfillment_center?: string;
    carrier_hub_id?: string;
    critical_pull_time?: string;
    carrier_hub_travel_time?: number;
  };
  rates: ShippoRate[];
  messages: {
    code?: string; // an identifier for the corresponding message (not always available)
    message?: string; // a publishable message containing further information
  }[];
  test: boolean;
  order: any;
}

export enum ShippoTransactionState {
  VALID = 'VALID',
  INVALID = 'INVALID',
}

export enum ShippoTransactionStatus {
  WAITING = 'WAITING',
  QUEUED = 'QUEUED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  REFUNDED = 'REFUNDED',
  REFUNDPENDING = 'REFUNDPENDING',
  REFUNDREJECTED = 'REFUNDREJECTED',
}

export interface ShippoTransaction {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  object_state: ShippoTransactionState;
  status: ShippoTransactionStatus;
  parcel: string; // shippo parcel object ID
  rate: string; // shippo rate object ID
  tracking_number: string;
  tracking_status: ShippoTrackingRecordStatus;
  tracking_url_provider: string;
  eta: string | null; // The estimated time of arrival according to the carrier.
  label_url: string;
  commercial_invoice_url: string;
  messages: {
    code?: string; // an identifier for the corresponding message (not always available)
    message?: string; // a publishable message containing further information
  }[];
  metadata: string;
  billing: any;
  qr_code_url: string;
  test: boolean;
  order: any;
}

export enum ShippoTrackingRecordStatus {
  UNKNOWN = 'UNKNOWN',
  PRE_TRANSIT = 'PRE_TRANSIT',
  DELIVERED = 'DELIVERED',
  TRANSIT = 'TRANSIT',
  FAILURE = 'FAILURE',
  RETURNED = 'RETURNED',
}

export interface ShippoTrackingRecord {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  status: ShippoTrackingRecordStatus;
  status_details: string;
  status_date: ISO8601DateTimeString;
  substatus?: {
    action_required?: boolean;
    code?: string;
    text?: string;
  };
  location: {
    country: ISO4217CurrencyCode;
    state: string;
    city: string;
    zip: string;
  };
}

export interface ShippoTrackingStatus {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  carrier: string;
  tracking_number: string;
  address_from: ShippoAddress;
  address_to: ShippoAddress;
  transaction: string; // shippo transaction object ID
  eta: ISO8601DateTimeString | null;
  original_eta: ISO8601DateTimeString | null;
  servicelevel: ShippoServicelevel;
  metadata: string;
  tracking_status: ShippoTrackingRecord;
  tracking_history: ShippoTrackingRecord[];
  messages: {
    code?: string; // an identifier for the corresponding message (not always available)
    message?: string; // a publishable message containing further information
  }[];
}

export enum ShippoRefundStatus {
  QUEUED = 'QUEUED',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export interface ShippoRefund {
  object_created: ISO8601DateTimeString;
  object_updated: ISO8601DateTimeString;
  object_id: string;
  object_owner: string;
  status: ShippoRefundStatus;
  transaction: string; // shippo transaction object ID
  test: boolean;
}

export type DeleteShippingAddress = { deleteThis: true; pos?: number };
export type UpdateShippingAddress = Address & {
  deleteThis?: false;
  pos?: number;
  asDefault?: boolean;
};
export type UpdateOrDeleteShippingAddress =
  | DeleteShippingAddress
  | UpdateShippingAddress;

export interface ModelBase {
  createdAt: AppDate;
  updatedAt: AppDate;
}

export enum LiveStreamActionType {
  Auction = 'AUCTION',
}

export enum LiveStreamAuctionStatus {
  Ongoing = 'ONGOING',
  Closed = 'CLOSED',
  Canceled = 'CANCELED',
}

export interface LiveStreamAction extends ModelBase {
  type: LiveStreamActionType;
}

export interface LiveStreamAuctionBid extends ModelBase {
  userId: string;
  user: Profile;
  isUserBlocked: boolean;
  isValid: boolean;
  bidPrice: number;
  nftImageUrl?: string;
}

export enum LiveStreamAuctionExtendSendItemStatus {
  NONE = 'NONE',
}

export type LiveStreamAuctionSendItemStatus =
  | LiveStreamAuctionExtendSendItemStatus
  | SendItemStatus;

export interface LiveStreamAuction extends LiveStreamAction {
  type: LiveStreamActionType.Auction;
  listingId: string;
  variantId?: string;
  listing: Listing;
  startingPrice: number;
  status: LiveStreamAuctionStatus;
  sendItemStatus: LiveStreamAuctionSendItemStatus;
  winnerBidId?: string;
  winnerBid?: LiveStreamAuctionBid;
  sendItemId?: string;
  timer?: {
    startAt: AppDate;
    durationSeconds: number;
    countdownSeconds: number;
  };
}

export interface LiveStreamActionView extends LiveStreamAction {
  auctionId?: string;
  auction?: LiveStreamAuction & {
    bidCount: number;
    currentBidId?: string;
    currentBid?: LiveStreamAuctionBid;
  };
}

export enum RecordType {
  SYNC = 'SYNC',
  SHIPPO_WEB_HOOK = 'SHIPPO_WEB_HOOK',
  COMMENT_SOLD_FULFILL_WEB_HOOK = 'COMMENT_SOLD_FULFILL_WEB_HOOK',
  FINALIZE_ORDER = 'FINALIZE_ORDER',
  REFUND_ORDER = 'REFUND_ORDER',
  CANCEL_ORDER = 'CANCEL_ORDER',
}

export interface BalanceChangeRecord {
  withdrawableBalance?: number;
  withdrawablePendingBalance?: number;
  type: RecordType;
  orderId?: string;
  amount: number;
  pendingAmount: number;
  orderStatus?: OrderStatus;
  createdAt: AppDate;
}

export enum CustomNotificationType {
  UserGroupUsers = 'UserGroupUsers',
  ProfileFollowers = 'ProfileFollowers',
  ProfileFollowing = 'ProfileFollowing',
  Profile = 'Profile',
}

export enum CustomNotificationStatus {
  Processing = 'Processing',
  Success = 'Success',
  Failed = 'Failed',
}

export interface CustomNotification extends ModelBase {
  type: CustomNotificationType;
  sourceIds: string[];
  payload: NotificationPayloadType;
  title: string;
  status: CustomNotificationStatus;
  body?: string;
  error?: string;
  failureCount?: number;
  successCount?: number;
  senderAccount: string;
  senderId: string;
  imageUrl?: string;
}

export enum UserRole {
  Moderator = 'moderator',
  Seller = 'seller',
  SystemAdmin = 'system-admin',
  User = 'user',
  Finance = 'finance',
  AgencyAdmin = 'agency-admin',
  AgencyUser = 'agency-user',
}

export interface Category extends ModelBase {
  // e.g. hawaii-toys
  slug: string;
  // full name, e.g. toys/hawaii-toys
  name: string;
  parentId?: string;
  // full name for the direct parent category
  parentName?: string;
  // root category slug
  rootCategory: string;
  title: string;
  description?: string;
  imageUrl?: string;
  sort: number;
  enabled: boolean;
}

export enum CategorizedResourceType {
  Store = 'STORE',
  UpcomingShow = 'UPCOMING_SHOW',
  LiveStream = 'LIVE_STREAM',
  User = 'USER',
}

export interface CategorizedResourceBase extends ModelBase {
  categoryId: string;
  category: Category;
  sort: number;
  resourceType: CategorizedResourceType;
}

export interface CategorizedStore extends CategorizedResourceBase {
  resourceType: CategorizedResourceType.Store;
  storeId: string;
  store: StoreFront;
  // For a given store, the category with the smallest sequenceNo will their primary category
  sequenceNo?: number;
  // using this field to do Firestore query
  isPrimary?: boolean;
}
export interface CategorizedUpcomingShow extends CategorizedResourceBase {
  resourceType: CategorizedResourceType.UpcomingShow;
  upcomingShowId: string;
  upcomingShow: UpcomingShow;
}
export interface CategorizedLiveStream extends CategorizedResourceBase {
  resourceType: CategorizedResourceType.LiveStream;
  liveStreamId: string;
  liveStream: LiveStream;
}
export interface CategorizedUser extends CategorizedResourceBase {
  resourceType: CategorizedResourceType.User;
  userId: string;
  user: Profile;
}

export type CategorizedResource =
  | CategorizedStore
  | CategorizedUpcomingShow
  | CategorizedLiveStream
  | CategorizedUser;

export type HighlightVideo = {
  userId: string;
  createdAt: AppDate;
  url?: string;
  updatedAt?: AppDate;
  readAt?: AppDate;
};

export interface DashboardNews extends ModelBase {
  brief: string;
  url?: string;
  active: boolean;
  startedAt?: AppDate;
  expiredAt?: AppDate;
}
export interface Mail {
  from?: string;
  to?: string | string[];
  toUids?: string[];
  cc?: string | string[];
  ccUids?: string[];
  bcc?: string | string[];
  bccUids?: string[];
  headers?: { [key: string]: string };
  message?: {
    subject: string;
    html: string;
    text?: string;
  };
  // template has higher priority than message when both exists
  template?: {
    name: string;
    data?: any;
  };
  externalId?: string;
  context?: {
    recipientUserId: string;
    senderUserId: string;
    platform: Platform;
    subject: string;
    templateName: string;
    // don't set this
    data?: any;
  };
  createdAt?: AppDate;
  delivery?: {
    state: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'ERROR';
    startTime: AppDate;
    endTime: AppDate;
    leaseExpireTime: null | AppDate;
    error: null | string;
    attempts: number;
    info: {
      messageId: string;
      response: string;
      accepted: string[];
      rejected: string[];
      pending: string[];
    };
  };
}

export interface MailTemplate {
  messageId?: string;
  subject: string;
  text?: string;
  html?: string;
  amp?: string;
}

export interface DConTicket {
  token: string;
  uid: string;
  createdAt: AppDate;
  purchaseAt: AppDate;
  fromType: string;
  ticketType: string;
  status: 'init' | 'processed' | 'skipped';
  extra?: { [key: string]: any };
  isNewUser?: boolean;
}

export type FDLComponent = {
  shortLinkFDL: string;
  contentFDL: ContentFDL;
  // params for the link [contentFDL.link], which is denormalized for future data query.
  queryParams?: Record<string, string>;
};

export type BehavioralActions = {
  actions?: {
    usersToFollow?: string[];
    joinToUserGroups?: string[];
    addToWatchLists?: string[];
  };
};

export type Share = {
  type: LinkType.Share;
  userId: string; // user id of who shares
  profile: Profile; // user profile of who shares
  method: ShareMethod;
} & LinkBase;

export type ReferralGiftingLink = {
  type: LinkType.ReferralGifting;
  userId: string; // user id of who shares
  profile: Profile; // user profile of who shares
  referralGiftId: string;
  referralGift: ReferralGift;
  sentSuccess: boolean;
  variantId?: string;
} & LinkBase;

// https://firebase.google.com/docs/dynamic-links/create-manually?authuser=0
// /campaigns/{campaign_id}/
export type Campaign = {
  type: LinkType.Campaign;
  name?: string;
  isWebOnly?: boolean;
  // https://popshoplive.page.link/JapanLA
  // the target user this campaign is attributed to
} & LinkBase;

export type LinkBase = FDLComponent &
  ModelBase &
  BehavioralActions & {
    shareTarget: ShareTarget;
    isActive: boolean;
  };

// using campaignTemplate to avoid duplicated content in the admin

export interface ContentFDL {
  // link is the one hitting our website. by providing the compaign_id,
  // our backend receive the id and do following things.
  link?: string; // https://popshop.live/JapanLA?c=campaign_id;
  android?: {
    apn?: string; // The package name of the Android app to use to open the link. The app must be connected to your project from the Overview page of the Firebase console. Required for the Dynamic Link to open an Android app.
    afl?: string; // The link to open when the app isn't installed. Specify this to do something other than install your app from the Play Store when the app isn't installed, such as open the mobile web version of the content, or display a promotional page for your app.
    amv?: string; // The versionCode of the minimum version of your app that can open the link. If the installed app is an older version, the user is taken to the Play Store to upgrade the app.
  };
  ios?: {
    ibi?: string; //	The bundle ID of the iOS app to use to open the link. The app must be connected to your project from the Overview page of the Firebase console. Required for the Dynamic Link to open an iOS app.
    ifl?: string; //	The link to open when the app isn't installed. Specify this to do something other than install your app from the App Store when the app isn't installed, such as open the mobile web version of the content, or display a promotional page for your app.
    ius?: string; //	Your app's custom URL scheme, if defined to be something other than your app's bundle ID
    ipfl?: string; //	The link to open on iPads when the app isn't installed. Specify this to do something other than install your app from the App Store when the app isn't installed, such as open the web version of the content, or display a promotional page for your app.
    ipbi?: string; //	The bundle ID of the iOS app to use on iPads to open the link. The app must be connected to your project from the Overview page of the Firebase console.
    isi?: string; //	Your app's App Store ID, used to send users to the App Store when the app isn't installed
    imv?: string; //	The version number of the minimum version of your app that can open the link. This flag is passed to your app when it is opened, and your app must decide what to do with it.
    efr?: string; //	If set to '1', skip the app preview page when the Dynamic Link is opened, and instead redirect to the app or store. The app preview page (enabled by default) can more reliably send users to the most appropriate destination when they open Dynamic Links in apps; however, if you expect a Dynamic Link to be opened only in apps that can open Dynamic Links reliably without this page, you can disable it with this parameter. This parameter will affect the behavior of the Dynamic Link only on iOS.
  };
  other?: {
    ofl?: string; //	The link to open on platforms beside Android and iOS. This is useful to specify a different behavior on desktop, like displaying a full web page of the app content/payload (as specified by param link) with another dynamic link to install the app.
  };
  social?: {
    st?: string; //	The title to use when the Dynamic Link is shared in a social post.
    sd?: string; //	The description to use when the Dynamic Link is shared in a social post.
    si?: string; //	The URL to an image related to this link. The image should be at least 300x200 px, and less than 300 KB.
  };
  analytics?: {
    googlePlay?: {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_term?: string;
      utm_content?: string;
      gclid?: string;
    };
    itunesConnect?: {
      at?: string;
      ct?: string;
      mt?: string;
      pt?: string;
    };
  };
}

export enum ShareTargetType {
  Profile = 'PROFILE',
  Store = 'STORE',
  LiveStream = 'LIVE_STREAM',
  Replay = 'REPLAY',
  UpcomingShow = 'UPCOMING_SHOW',
  ListingLiveStream = 'LISTING_LIVE_STREAM',
  ListingReplay = 'LISTING_REPLAY',
  ListingEvergreen = 'LISTING_EVERGREEN',
  ListingUpcomingShow = 'LISTING_UPCOMING_SHOW',
  ListingPromotion = 'LISTING_PROMOTION',
  Event = 'EVENT',
  EventDiscovery = 'EVENT_DISCOVERY',
  EventTicket = 'EVENT_TICKET',
  HomePage = 'HOME_PAGE',
  Promotion = 'PROMOTION',
  ReferralGifting = 'REFERRAL_GIFTING',
  UserBadge = 'USER_BADGE',
  LiveStreamGiveaway = 'LIVE_STREAM_GIVEAWAY',
  UpcomingShowGiveaway = 'UPCOMING_SHOW_GIVEAWAY',
  PopClip = 'POP_CLIP',
  BecomeASeller = 'BECOME_A_SELLER',
}

export type ShareTarget =
  | {
      type: ShareTargetType.UserBadge;
      userId: string;
      userBadgeId: string;
      userBadge: UserBadge;
    }
  | {
      type: ShareTargetType.Profile;
      profileId: string;
      profile: Profile;
    }
  | {
      type: ShareTargetType.Store;
      storeId: string;
      store: StoreFront;
    }
  | {
      type: ShareTargetType.LiveStream;
      liveStreamId: string;
      liveStream: LiveStream;
    }
  | {
      type: ShareTargetType.Replay;
      liveStreamId: string;
      liveStream: LiveStream;
    }
  | {
      type: ShareTargetType.UpcomingShow;
      upcomingShowId: string;
      upcomingShow: UpcomingShow;
    }
  | {
      type: ShareTargetType.ListingLiveStream;
      listingId: string;
      liveStreamId: string;
      listing: Listing;
      liveStream: LiveStream;
    }
  | {
      type: ShareTargetType.ListingReplay;
      listingId: string;
      liveStreamId: string;
      listing: Listing;
      liveStream: LiveStream;
    }
  | {
      type: ShareTargetType.ListingEvergreen;
      listingId: string;
      storeId: string;
      store: StoreFront;
      listing: Listing;
    }
  | {
      type: ShareTargetType.Event | ShareTargetType.EventDiscovery;
      showEventId: string;
      showEvent: ShowEvent;
    }
  | {
      type: ShareTargetType.EventTicket;
      showEventId: string;
      ticketId: string;
      ticket: ShowTicket;
      showEvent: ShowEvent;
    }
  | {
      type: ShareTargetType.HomePage;
    }
  | {
      type: ShareTargetType.Promotion;
      promotionId: string;
      promotion: PromotionPage;
    }
  | {
      type: ShareTargetType.ListingPromotion;
      promotionId: string;
      promotion: PromotionPage;
      storeId: string;
      store: StoreFront;
      listingId: string;
      listing: Listing;
    }
  | {
      type: ShareTargetType.ListingUpcomingShow;
      upcomingShowId: string;
      upcomingShow: UpcomingShow;
      storeId: string;
      store: StoreFront;
      listingId: string;
      listing: Listing;
    }
  | {
      type: ShareTargetType.ReferralGifting;
      referralGiftId: string;
      referralGift: ReferralGift;
      variantId?: string;
    }
  | {
      type: ShareTargetType.LiveStreamGiveaway;
      liveStreamId: string;
      liveStream: LiveStream;
      giveawayItemId: string;
      giveawayItem: GiveawayItem;
    }
  | {
      type: ShareTargetType.UpcomingShowGiveaway;
      upcomingShowId: string;
      upcomingShow: UpcomingShow;
      giveawayItemId: string;
      giveawayItem: GiveawayItem;
    }
  | {
      type: ShareTargetType.PopClip;
      storeId: string;
      // a numeric id from DynamoDB (might be switched to firestore in the future)
      popClipId: string;
      store: StoreFront;
    }
  | {
      type: ShareTargetType.BecomeASeller;
    };

export enum ShareMethod {
  Sms = 'SMS',
  Email = 'EMAIL',
  Facebook = 'FACEBOOK',
  Instagram = 'INSTAGRAM',
  Twitter = 'TWITTER',
  Embed = 'EMBED',
  Generic = 'GENERIC',
}

// https://popshop.live/JapanLA?s={id}
// https://popshop.live/liveStream_xxxxxx?s={id}
// https://popshop.live/upcomingShow_xxxxxx?s={id}
// https://popshop.live/event_xxxxxx?s={id}

export enum LinkType {
  Share = 'SHARE',
  Campaign = 'CAMPAIGN',
  ReferralGifting = 'REFERRAL_GIFTING',
}

export type Link = Share | Campaign | ReferralGiftingLink;

export type LinkHistory = {
  linkId: string;
  link: Link;
  userId: string;
  profile: Profile;
  isNewUser: boolean;
  extraData?: Record<string, any>;
  userAgent?: string;
} & ModelBase;

// output from bigquery
// user stats
// /userStats/{userId}
export type ExposureUserStats = {
  updatedAt?: AppDate;
  followerCount?: number;
  followingCount?: number;
  passcodeReferralCount?: number;
  shareReferralCount?: number;
  /**
   * the number of users I invited
   */
  referralCount?: number;
  /**
   * the user who invited me
   */
  referrerUserId?: string;
  referrerUserProfile?: Profile;
  /**
   * @deprecated since 03/15/2024, migrated to PrivateProfile
   */
  referrerManuallyModifiedAt?: AppDate;
};

// user referral(i.e. signup) from a user's passcode and share
// /userStats/{referrerUserId}/userReferrals/{presenteeUserId}
export type ExposureUserReferral = {
  createdAt: AppDate;
  updatedAt: AppDate;
  type: 'SHARE' | 'PASSCODE' | 'MANUAL';
  /**
   * the invitee
   */
  presenteeUserId: string;
  presenteeUserProfile: Profile;
  /**
   * the user who invited the invitee
   */
  referrerUserId: string;
  referrerUserProfile: Profile;
  passcode: Referral | null;
  share: LinkHistory | null;
};

// show referral(i.e. signup and join a show) from a user's share
// /sellerStats/{sellerId}/showStats/{liveStreamId}/showReferrals/{presenteeUserId}
export type ExposureShowReferral = {
  createdAt: AppDate;
  updatedAt: AppDate;
  presenteeUserId: string;
  presenteeUserProfile: Profile;
  referrerUserId: string;
  referrerUserProfile: Profile;
  share: LinkHistory;
};

// upcoming show referral(i.e. signup and watch a upcoming show) from a user's share
// /sellerStats/{sellerId}/upcomingShowStats/{upcomingShowId}/upcomingShowReferrals/{presenteeUserId}
export type ExposureUpcomingShowReferral = ExposureShowReferral;

export interface RandomPickWinner extends ModelBase {
  profile: Profile;
  profileId: string;
}

export enum ReminderType {
  GiftRecipientAddressRequired = 'GIFT_RECIPIENT_ADDRESS_REQUIRED',
  GiftCreated = 'GIFT_CREATED',
  GiftRejected = 'GIFT_REJECTED',
}

export interface GiftRecipientAddressRequiredReminder {
  type: ReminderType.GiftRecipientAddressRequired;
}

export interface GiftCreatedReminder {
  type: ReminderType.GiftCreated;
  orderInfo: {
    senderId: string;
    liveStreamId?: string | null;
    sender: {
      firstName: string;
      lastName: string;
      avatar?: string;
      nftImageUrl?: string;
    };
    recipientId: string;
    recipient: {
      firstName: string;
      lastName: string;
      avatar?: string;
      nftImageUrl?: string;
    };
    sellerId: string;
    seller: {
      firstName: string;
      lastName: string;
      avatar?: string;
    };
    shippingAddress: Address;
    listingSnapshot: Listing;
    listingId: string;
    variantId?: string | null;
    quantity: number;
    createdAt: AppDate;
    orderId: string;
  };
}

export interface GiftRejectedReminder {
  type: ReminderType.GiftRejected;
  orderInfo: {
    senderId: string;
    liveStreamId?: string | null;
    sender: {
      firstName: string;
      lastName: string;
      avatar?: string;
      nftImageUrl?: string;
    };
    recipientId: string;
    recipient: {
      firstName: string;
      lastName: string;
      avatar?: string;
      nftImageUrl?: string;
    };
    sellerId: string;
    seller: {
      firstName: string;
      lastName: string;
      avatar?: string;
    };
    shippingAddress: Address;
    listingSnapshot: Listing;
    listingId: string;
    variantId?: string | null;
    quantity: number;
    createdAt: AppDate;
    orderId: string;
  };
}

// privateProfiles/{uid}/reminders/{reminderId}
export type Reminder = (
  | GiftRecipientAddressRequiredReminder
  | GiftCreatedReminder
  | GiftRejectedReminder
) &
  ModelBase & { processed: boolean };

// privateProfiles/{uid}/inviteBy/{uId}
// invitedBy just have one doc
export type InvitedBy = Profile;

export type WatchedSeller = Profile & { watchedAt: AppDate };

export interface PausedBy {
  userId: string;
  role: 'host' | 'admin';
}

export interface ShowCustomization {
  primaryLightColor?: string;
  primaryDarkColor?: string;
  lightingBoltImages?: string[];
  lightingBoltButtonImage?: string;
  disableLightingBoltForAll?: boolean;
  tippingEnabled?: boolean;
}

export enum TinStatus {
  NotStart = 'NotStart',
  Accepted = 'Accepted',
  Blocked = 'Blocked',
  Processing = 'Processing',
  Rejected = 'Rejected',
}

export enum FormW9Status {
  NotStart = 'NotStart',
  Pending = 'Pending',
  Completed = 'Completed',
  Expired = 'Expired',
}

export interface SellerFormW9 {
  sellerId: string;
  clientRecipientId: string;
  formW9Url?: string;
  formW9CreatedAt?: AppDate;
  formW9ExpiredAt?: AppDate;
  formW9CompletedAt?: AppDate;
  formW9Status: FormW9Status;
  tinMatchRequestId?: number;
  tinStatus: TinStatus;
  tinReason?: string;
  createdAt: AppDate;
  updatedAt: AppDate;
}

export interface SellerFormW9Webhook {
  callbackUID: string;
  sellerId: string;
  type: 'FormW9';
}

// /config/public_config

export interface SellerOnboardingCheckListItemConfig {
  required?: boolean;
  title?: string;
  description?: string;
  sort_order?: number;
}

export interface SellerOnboardingConfigParameters {
  enforced_after?: AppDate;
  limit_in_days?: number;
}

export interface SellerOnboardingCheckListConfig {
  force_completion_before_proceeding_in_app?: boolean;
  continue_button_text?: string;
  skip_button_text?: string;
  psl_pay?: SellerOnboardingCheckListItemConfig;
  store_info?: SellerOnboardingCheckListItemConfig;
  store_reel?: SellerOnboardingCheckListItemConfig;
  business_location?: SellerOnboardingCheckListItemConfig;
  shipping_options?: SellerOnboardingCheckListItemConfig;
  commentchat?: SellerOnboardingCheckListItemConfig;
  inventory_listings?: SellerOnboardingCheckListItemConfig & {
    min_listing_count?: number;
  };
  store_favorites?: SellerOnboardingCheckListItemConfig & {
    min_listing_count?: number;
  };
}

export type SellerOnboardingConfig = SellerOnboardingConfigParameters & {
  min_inventory_listing_count?: number;
  min_public_listing_count?: number;
  check_list_config?: {
    default?: SellerOnboardingCheckListConfig;
    business?: SellerOnboardingCheckListConfig &
      SellerOnboardingConfigParameters;
    creator?: SellerOnboardingCheckListConfig &
      SellerOnboardingConfigParameters;
  };
};

export type PopStoreOnboardingContestTaskRequirements<
  T extends PopStoreOnboardingContestTaskType,
> = ExtractRequirement<PopStoreOnboardingContestTasks[T]>;

export type ShowNumericProgress = boolean | 'when-has-progress';

export type PopStoreOnboardingContestTaskConfig<
  T extends PopStoreOnboardingContestTaskType,
> = SellerOnboardingCheckListItemConfig &
  PopStoreOnboardingContestTaskRequirements<T> & {
    ctaButton?: {
      title?: string;
      show_numeric_progress?: ShowNumericProgress;
    };
  };

// configuration for each task item for popstore onboarding contest
export type PopStoreOnboardingContestTaskListConfig = {
  [T in PopStoreOnboardingContestTaskType]: PopStoreOnboardingContestTaskConfig<T>;
};

export type AnimationConfig = {
  style?: AnimationStyle;
  disabled?: boolean;
  interval?: number;
  repeat?: number;
  speed?: 'slower' | 'slow' | 'fast' | 'faster';
};

export type PopStoreOnboardingContestBannerConfig = {
  title_image_animation?: AnimationConfig;
  right_image_animation?: AnimationConfig;
};

export const kPopStoreOnboardingContestDefaultWeekStartsOn = 1 as const;
export const kPopStoreOnboardingContestDefaultTimeZone = 'America/Los_Angeles';

// popstore onboarding contest
export type PopStoreOnboardingContestConfig = {
  // when the contest starts
  // used to calculate the week number
  begin_at?: AppDate;

  // when the contest ends
  end_at?: AppDate;

  // default to America/Los_Angeles
  timezone?: string;

  // specify if the leader board is enabled for view.
  leaderboard_enabled?: boolean;

  /** note displayed in leaderboard weekly entries page  */
  leaderboard_weekly_entries_note?: string;

  // first week day, where Sunday is the first element (0) and
  // Saturday is the last (6), default to 1 (Monday).
  week_starts_on?: 0 | 1 | 2 | 3 | 4 | 5 | 6;

  // which week number type to display
  week_no_format?: 'relative' | 'absolute';

  // contest status, default to 'active'
  status?: 'active' | 'paused' | 'stopped';

  // max allowed number of days (including sign up day) to submit after signup
  // default to 6 days right now
  max_submission_due_days?: number;

  // banner config
  banner?: PopStoreOnboardingContestBannerConfig;

  // terms & conditions link
  // default to: https://get.pop.store/555contest
  terms_conditions_link?: string;

  // if digital download button should be counted as link button
  is_digital_download_button_counted_as_link_button?: boolean;

  // if calendar button should be counted as link button
  is_calendar_button_counted_as_link_button?: boolean;

  /** if confirm required before submit profile */
  should_confirm_before_submit?: boolean;

  /** custom confirm message before submit profile */
  submission_confirm_message?: string;

  // the individual configuration for each task list item
  task_list_config?: PopStoreOnboardingContestTaskListConfig;
};

export type VideoCoverRenderAsType = 'video' | 'gif' | 'image' | 'auto';
export type VideoCoverImageType = 'thumbnail' | 'product-image';
export type NativeCompressVideoType =
  | 'popclip'
  | 'order-video'
  | 'upcoming-preview'
  | 'store-reel';

export type VideoCompressionConfig = {
  method?: 'auto' | 'manual';
  maxSize?: number;
  resolution?: number;
};

export type CommentChatUrlsConfig = {
  facebookBusinessPageHelpUrl: string;
  instagramProfessionalAccountHelpUrl: string;
  instagramEnableDMsHelpUrl: string;
  facebookBusinessManagerUrl: string;
  instagramConnectFacebookBusinessPageHelpUrl?: string;
  missingAccountsHelpUrl: string;
};

export type EditorialTextFormat = 'html' | 'text' | 'markdown';

export interface PublicConfigTextContent {
  format: EditorialTextFormat;
  value: string;
  title?: string;
  hidden?: boolean;
  collapsible?: boolean;
}

export interface PublicConfig {
  enable_cs_iframe_proxy?: boolean;
  auction_timer_countdown_seconds: number;
  upvote_pin_time: {
    initial: number;
    additional: number;
  };
  // toggle the display of top reviews row on the discovery page
  discovery_top_reviews?: boolean;
  enable_referral_gifting?: boolean;
  force_merge_account_for_signup_after?: AppDate;
  psl_pay_remind_after?: AppDate;
  psl_pay_enforced_after?: AppDate;
  variant_price_enable?: boolean;
  enable_seller_onboarding?: boolean;
  allow_remove_categories?: boolean;

  stripe_required?: boolean;
  braintree_required?: boolean;

  apple_pay_use_stripe?: boolean;
  apple_pay_use_psl_as_merchant_name?: boolean;

  auto_featured_show_cap?: {
    max_auto_featured_live_stream_quantity: number;
    max_auto_featured_upcoming_show_quantity: number;
  };
  seller_onboarding?: SellerOnboardingConfig;
  psl_features?: {
    enable_pop_clips_seller: boolean;
    enable_pop_clips_buyer: boolean;
  };
  video_cover?: {
    with_image_cover_bg?: boolean;
    image_cover_type?: VideoCoverImageType;
    ios?: VideoCoverRenderAsType;
    android?: VideoCoverRenderAsType;
    web?: VideoCoverRenderAsType;
  };
  native_compress_videos?: NativeCompressVideoType[];
  order_videos?: {
    compression?: VideoCompressionConfig;
  };
  pop_clips?: {
    compression?: VideoCompressionConfig;
  };
  moderation?: {
    user_blocking?: boolean;
  };
  discovery?: {
    use_featured_show_impl_for_event?: boolean;
  };
  auth?: {
    facebook_login_enabled?: boolean;
    required_for_mobile?: boolean;
    login_remind_enabled?: boolean;
    login_remind_max_count?: number;
    initial_login_remind_delay?: number;
    recurrent_login_remind_interval?: number;
  };
  commentchat?: {
    enabled_for_all_sellers_by_default?: boolean;
    enabled_in_seller_dashboard?: boolean;
    enabled_in_app?: boolean;
    instagram_dm_check_enabled?: boolean;
    urls?: Partial<CommentChatUrlsConfig>;
    connect_ig_channels_only?: boolean;
    max_dm_response_count?: number;
  };
  shopify_sync?: {
    enabled_in_seller_dashboard?: boolean;
    enabled_in_app?: boolean;
  };
  pdp?: {
    show_sku_label?: boolean;
    show_dropship_brand_partner_label?: boolean;
    show_dropship_supplier_name_label?: boolean;
    show_seller_link?: boolean;
    hide_single_variant?: boolean;
    refund_return_text_copy?: PublicConfigTextContent;
    min_inventory_level_for_hide_qty?: number;
    shipping_to_us_only?: PublicConfigTextContent;
    rating_count_visible_threshold?: number;
    apple_pay?: {
      deduplicate_stripe_payment_method?: boolean;
    };
    openbox?: {
      explicit_free_shipping?: PublicConfigTextContent;
      free_shipping?: PublicConfigTextContent;
      free_shipping_available?: {
        psl?: PublicConfigTextContent;
        dropship?: PublicConfigTextContent;
      };
    };
  };
  msm?: {
    enabled?: boolean;
    help_article_url?: string;
  };
  profile_page?: {
    creator?: {
      stats_visible_min_sold_items?: number;
      hours_live_visible_min_hours?: number;
    };
  };
  modelme?: {
    beta?: boolean;
    disabled_for_business_seller?: boolean;
    min_face_photos?: number;
    min_standing_photos?: number;
    max_allowed_image_size_in_mb?: number;
  };
  popstore?: {
    show_on_min_followers?: number;
    video_overlay_auto_hide_in_seconds?: number;
    video_touch_to_hide_overlay_threshold?: number;
    gated_mini_player_blurred_in_seconds?: number;
    gated_mini_player_blurry_intensity?: number;
    mini_player_support_content_types?: (
      | 'upcomingShow'
      | 'liveShow'
      | 'replay'
      | 'videoClip'
      | 'storeReel'
    )[];
    onboarding_contest?: PopStoreOnboardingContestConfig;
  };
}

export interface BraintreeDispute {
  amountDisputed: string;
  amountWon: string;
  caseNumber: string;
  createdAt: string;
  currencyIsoCode: string;
  evidence: Evidence;
  id: string;
  kind: string;
  merchantAccountId: string;
  originalDisputeId: string;
  processorComments: string;
  reason: string;
  reasonCode: string;
  reasonDescription: string;
  receivedDate: string;
  referenceNumber: string;
  replyByDate: string;
  status: DisputeStatus;
  statusHistory: DisputeStatusHistory[];
  transaction: {
    amount: string;
    createdAt: string;
    id: string;
    orderId: string;
    paymentInstrumentSubtype: string;
    purchaseOrderNumber: string;
  };
  updatedAt: string;
}

export enum DisputeStatus {
  Accepted = 'Accepted',
  Disputed = 'Disputed',
  Expired = 'Expired',
  Lost = 'Lost',
  Won = 'Won',
  Open = 'Open',
}

export interface DisputeStatusHistory {
  disbursementDate: string;
  effectiveDate: string;
  status: DisputeStatus;
  timestamp: string;
}

export interface Evidence {
  comment?: string;
  createdAt: string;
  id: string;
  sendToProcessorAt: string;
  url?: string;
}

export interface BraintreeDisbursementDetails {
  disbursementDate: string;
  fundsHeld: boolean;
  settlementAmount: string;
  settlementCurrencyExchangeRate: string;
  settlementCurrencyIsoCode: string;
  success: boolean;
}

// /referralGifts/{referralGiftId}
export type ReferralGift = {
  storeId: string;
  store: StoreFront;
  listingId: string;
  active: boolean;
  sortedAt: AppDate;
  listing: Listing;
  givenTimes: number;
} & ModelBase;

// /sentReferralGifts/{id}
export type SentReferralGift = {
  referralGiftingLinkId: string;
  referralGiftingLink: ReferralGiftingLink;
  listing: Listing;
  listingId: string;
  storeId: string;
  variantId?: string;
  received: boolean;
  expiredAt: AppDate;
  receiverId: string;
  referralGiftId: string;
} & ModelBase;

export interface OrderPriceComponentsCouponItem {
  code: string;
  id: string;
  discountedAmount: number;
  discountPublisher: 'popshop' | 'seller';
  type: CouponType;
}

export enum PopshopShippingFeeSource {
  Default = 'DEFAULT',
  // this is from readonlyProfile.adjustedPopshopShippingFee
  ReadonlyProfile = 'READONLY_PROFILE',
  ReadonlyProfileShippingPrice = 'READONLY_PROFILE_SHIPPING_PRICE',
  ShowEvent = 'SHOW_EVENT',
  Config = 'CONFIG',
  /**
   * @deprecated
   */
  ShippoPlatform = 'SHIPPO_PLATFORM',
  PackageTemplate = 'PACKAGE_TEMPLATE',
}

export enum SellerShippingFeeSource {
  Store = 'STORE',
  Listing = 'LISTING',
  PackageTemplate = 'PACKAGE_TEMPLATE',
}

export enum ListingPriceSource {
  Listing = 'LISTING',
  Variant = 'VARIANT',
}

export enum ProcessingFeePayer {
  Seller = 'SELLER',
  Popshop = 'POPSHOP',
}

export type RefundDetail = Partial<
  Pick<
    OrderPriceComponents,
    | 'listingDiscountedSubTotal'
    | 'shipmentDiscountedShippingFee'
    | 'salesTaxAmount'
  >
> & {
  subtotal: number;
  listingQuantity?: number;
  deductProcessingFee: number;
  processingFeePayer: ProcessingFeePayer;
};

export enum RefundType {
  ALL = 'all',
  SHIPPING_FEE = 'shippingFee',
  LISTING = 'listing',
  CUSTOM_AMOUNT = 'customAmount',
  CANCEL = 'cancel',
  CANCEL_LISTING = 'cancelListing',
}

export type RefundRecord = {
  /**
   * detail including:
   * 1. total
   * 2. listing subtotal
   * 3. listing quantity
   * 4. shipping fee
   * 5. sales tax amount
   * 6. gateway processing fee
   * 7. gateway processing fee payer
   */
  detail: RefundDetail;
  types: RefundType[];
  shouldRestock?: boolean;
  shouldFullyRefund?: boolean;
  shouldSubtotalFullyRefund?: boolean;
  shouldShippingFeeFullyRefund?: boolean;
  reason?: string;
  /**
   * if order refund, stripe main account transfer fee back to connect account
   */
  refundFeeTransaction?: RefundFeeTransaction;
  dropshipSupplier?: DropshipSupplier & { id: string };
} & ModelBase;

export interface RefundFeeTransaction {
  stripeAccountRefundFee: number;
  isReturned?: boolean;
  updatedAt: AppDate;
}

export type RefundRecordDetail = {
  intent: StripeRefund | BraintreeTransaction;
  operatorId: string;
  operator: Profile | null;
  quantity?: number;
  listingSnapshot: Listing;
  listingId: string;
  variantId?: string | null;
  sellerId: string;
  sellerAmountDistribution: {
    // amount to refund to buyer
    returnCommissionFee: number; // commission fee to refund to buyer
    orderReturnGatewayProcessingFee: number; // gateway processing fee to refund to buyer from seller
    orderDeductSellerGotWithdrawableAmount: number; // seller withrawable amount to refund to buyer from seller
    // amount to deduct from PSL
    returnGatewayProcessingFee: number; // gateway processing fee to refund to buyer from PSL, zero if seller pays
    // amount to deduct from seller
    deductShippingFee: number; // deduct shipping fee from seller
    deductSellerGotWithdrawableAmount: number; // seller withrawable amount(plus gateway processing fee if seller pays) to refund to buyer from seller
    deductBalanceByCoupon?: number; // = deductCouponSellerGotAmount
    deductCouponSellerGotAmount?: number; // deduct PSL provided listing&shipping coupon amount from seller
    deductItemCouponAmount: number; // deduct PSL provided listing coupon amount from seller
    deductShippingCouponSellerGotAmount?: number; // deduct PSL provided shipping coupon amount from seller
    deductSellerSubtotalPaidByPopshopToSeller?: number;
    deductPopshopShippingFee?: number;
    deductPopshopShippingFeePaidBySellerToPopshop?: number;
    deductPopshopShippingFeePaidByPopshopToSeller?: number;
    deductSellerShippingFee?: number;
    deductSellerShippingFeePaidByPopshopToSeller?: number;
    // amount after this refund
    commissionFee: number; // commission fee after this refund
    orderGatewayProcessingFee: number; // gateway processing fee after this refund
    orderSellerGotWithdrawableAmount: number; // seller withrawable amount after this refund
    couponSellerGotAmount?: number; // dropship ? dropshipSubtotalPaidByPopshop : sellerSubtotalPaidByPopshop + popshopShippingFeePaidByPopshopToSeller + sellerShippingFeePaidByPopshopToSeller
    listingCouponSellerGotAmount?: number; // dropship ? dropshipSubtotalPaidByPopshop : sellerSubtotalPaidByPopshop
    shippingCouponSellerGotAmount?: number; // dropship ? 0 : popshopShippingFeePaidByPopshopToSeller + sellerShippingFeePaidByPopshopToSeller
    sellerSubtotalPaidByPopshopToSeller?: number;
    popshopShippingFee?: number;
    popshopShippingFeePaidBySellerToPopshop?: number;
    popshopShippingFeePaidByPopshopToSeller?: number;
    sellerShippingFee?: number;
    sellerShippingFeePaidByPopshopToSeller?: number;
  };
  dropshipSupplierAmountDistribution?: {
    // amount to deduct from dropship supplier
    deductCostAmount: number;
    deductSoldAmount: number;
    deductSubtotalPaidByPopshop?: number;
    deductSubtotalPaidBySeller?: number;
    deductManagementFee: number; // management fee to refund to buyer from dropship supplier
    deductFreeShippingDiscountAmount?: number;
    deductShippingFee: number; // shipping fee to refund to buyer from dropship supplier
    deductShippingFeePaidByPopshop?: number;
    deductShippingFeePaidBySeller?: number;
    deductSupplierWithdrawableAmount: number; // withdrawable amount to refund to buyer from dropship supplier
    // amount after this refund
    costAmount: number;
    soldAmount: number;
    subtotalPaidByPopshop?: number;
    subtotalPaidBySeller?: number;
    managementFee: number; // management fee after this refund
    freeShippingDiscountAmount?: number;
    shippingFee: number; // shipping fee after this refund
    shippingFeePaidByPopshop?: number;
    shippingFeePaidBySeller?: number;
    supplierWithdrawableAmount: number; // withdrawable amount after this refund
  };
} & RefundRecord;

export type StripeRefund = {
  id: string;
  object: 'refund';
  amount: number;
  balance_transaction: string | null;
  charge: string | null;
  created: number;
  currency: string;
  description?: string;
  failure_balance_transaction?: string;
  failure_reason?: string;
  instructions_email?: string;
  payment_intent: string | null;
  reason: string | null;
  receipt_number: string | null;
  source_transfer_reversal: string;

  status: string | null;
};

export enum ShippingIntegrationType {
  Shippo = 'shippo',
  ShippoPlatform = 'shippo_platform',
}

export type ShippingCost =
  | {
      status: 'pending' | 'failed';
      message?: string;
    }
  | {
      status: 'success';
      message?: string;
      shippoShipmentId?: string;
      shippoRateId?: string;
      shippoRate?: ShippoRate;
      /**
       * @deprecated
       */
      shipmentId?: string;
      /**
       * @deprecated
       */
      rates?: ShippingRate[];
    };

/**
 * @deprecated since 12/06/22
 */
export type ShippingRate = {
  id: string;
  attributes: string[];
  duration_terms: string;
  estimated_days: number;
  provider_image_75: string;
  provider_image_200: string;
  servicelevel: {
    name: string;
    terms: string;
    token: string;
  };
  cost: {
    currency: string;
    base: number;
    margin: number;
    total: number;
  };
  test: boolean;
};

export enum SalesTaxRecipient {
  Seller = 'seller',
  Popshop = 'popshop',
}

export interface CommentSoldSalesTaxCalcResultDetail {
  tax_name: string;
  amount: number; // in cents
  rate: number;
  flat_rate: boolean;
}

export interface CommentSoldSalesTaxCalcResultLine {
  sku: string;
  tax_amount: number; // in cents
  taxable_amount: number; // in cents
  details: CommentSoldSalesTaxCalcResultDetail[];
}

export interface CommentSoldSalesTaxCalcResult {
  tax_total: number; // in cents
  taxable_amount: number; // in cents
  details: CommentSoldSalesTaxCalcResultDetail[];
  lines: CommentSoldSalesTaxCalcResultLine[];
}

export interface AvalaraSalesTaxCalcResult {
  id: string; // avalara id
  totalAmount: number; // dollar
  totalTax: number; // dollar
  totalTaxable: number; // dollar
  totalExempt: number; // dollar
  status: string;
  code?: string; // avalara code
}

export interface OrderPriceComponents {
  shippingFeeTaxable?: boolean; // shippingFee starts to be taxable after June 2023
  commentSoldSalesTaxCalcResult?:
    | CommentSoldSalesTaxCalcResult
    | AvalaraSalesTaxCalcResult; // raw result of sales tax from CommentSold after June 2023
  commentSoldSalesTaxCalcError?: string;
  shippingSalesTaxAmount?: number; // shipping part of sales tax from CommentSold
  shippingSalesTaxRate?: number;
  listingSalesTaxAmount?: number; // listing part of sales tax from CommentSold
  listingSalesTaxRate?: number;
  salesTaxAmount: number;
  salesTaxRate: number;
  // Whom ths tax is attributed to (default is seller),
  // before 2023-05-31, tax always goes to seller balance
  // but now (2023-05-31), tax will always go to popshop
  salesTaxRecipient?: SalesTaxRecipient;
  buyerPaidAmount: number;
  sellerGotAmount: number;
  total: number;
  /**
   * if shippingIntegrationType is shippo_platform and
   *    shipmentShippingFeePayer is Buyer,
   *    sellerGotWithdrawableAmount also includes popshopShippingFee (aka shippingPrice)
   */
  sellerGotWithdrawableAmount: number;
  listingPrice: number;
  listingOriginPrice?: number;
  listingPriceSource?: ListingPriceSource;
  listingQuantity: number;
  listingDiscountedSubTotal: number;
  sellerSubtotalPaidByPopshopToSeller?: number; // popshop provided coupon for non-dropship
  commissionFeeRate: number;
  eventExtraCommissionFeeRate?: number;
  baseCommissionFeeRate: number;
  commissionFee: number;
  gatewayProcessingFeeRate: number;
  gatewayProcessingFeeExtra?: number;
  gatewayProcessingFee: number;
  shipmentDiscountedShippingFee: number;
  shipmentOriginShippingFee: number;
  shipmentShippingMethod: OrderShippingMethod;
  shipmentShippingFeePayer: ShippingFeePayer;
  couponDiscountProductAmount: number; // couponDiscountProductAmountProvidedByPopshop + couponDiscountProductAmountProvidedBySeller
  couponDiscountProductAmountProvidedByPopshop?: number; // dropship ? dropshipSubtotalPaidByPopshop : sellerSubtotalPaidByPopshopToSeller
  couponDiscountProductAmountProvidedBySeller?: number; // dropship ? dropshipSubtotalPaidBySeller : couponDiscountProductAmountProvidedBySeller
  couponDiscountShippingFeeAmount: number; // couponDiscountShippingFeeAmountProvidedByPopshop + couponDiscountShippingFeeAmountProvidedBySeller
  couponDiscountShippingFeeAmountProvidedByPopshop?: number; // dropship ? dropshipShippingFeePaidByPopshop - dropshipFreeShippingDiscountAmount : popshopShippingFeePaidByPopshopToSeller + sellerShippingFeePaidByPopshopToSeller
  couponDiscountShippingFeeAmountProvidedBySeller?: number; // dropship ? dropshipShippingFeePaidBySeller - shipping fee if seller pays : popshopShippingFeePaidBySellerToPopshop - 'shippo' shipping fee if seller pays
  coupons?: OrderPriceComponentsCouponItem[];
  paymentProvider: 'braintree' | 'stripe';
  couponSellerGotAmount: number; // dropship ? dropshipSubtotalPaidByPopshop : sellerSubtotalPaidByPopshop + popshopShippingFeePaidByPopshopToSeller + sellerShippingFeePaidByPopshopToSeller
  listingCouponSellerGotAmount?: number; // dropship ? dropshipSubtotalPaidByPopshop : sellerSubtotalPaidByPopshop
  shippingCouponSellerGotAmount?: number; // dropship ? 0 : popshopShippingFeePaidByPopshopToSeller + sellerShippingFeePaidByPopshopToSeller
  /**
   * if shippingIntegrationType is shippo_platform, this is also what we called `shippingPrice`
   */
  popshopShippingFee?: number;
  popshopShippingFeeSource?: PopshopShippingFeeSource;
  popshopShippingFeePaidBySellerToPopshop?: number;
  popshopShippingFeePaidByPopshopToSeller?: number;

  shippingIntegrationType?: ShippingIntegrationType;
  // only when this is true can an order be finalized by seller manually
  useArrangeMyOwnShippingV2?: boolean;
  /**
   * shipping fee margin rate.
   * take from config(shippo.shipping_margin_rate) first, then take 2.5%.
   */
  shippingMarginRate?: number;
  /**
   * default shipping cost base on seller profile default shipping configs.
   * i.e. package template, service level, etc.
   * @deprecated since 12/06/22
   */
  defaultShippingCost?: ShippingCost;
  /**
   * i.e. the actual shipping rate shippo charges the seller
   * @deprecated since 12/06/22
   */
  userSelectedShippingCost?: ShippingCost;
  /**
   * shipping cost for preview when sellers enable new shipping solution.
   * shippo rate amount via package template on this order.
   */
  shippingSurcharge?: number;

  sellerShippingFee?: number;
  sellerShippingFeeSource?: SellerShippingFeeSource;
  sellerShippingFeePaidByPopshopToSeller?: number;
  referralGiftingDiscountSubtotal?: number;
  referralGiftingDiscountShippingFee?: number;
  referralGiftingDiscountShippingSalesTaxAmount?: number;
  referralGiftingDiscountListingSalesTaxAmount?: number;
  referralGiftingDiscountSalesTaxAmount?: number;
  // dropship
  dropshipAmounts?: DropshipAmounts;
  /**
   * buyer payment currency
   */
  currency?: string;
  subtotal?: number;
}

export enum DeleteAccountApplicationStatus {
  approved = 'approved',
  pending = 'pending',
  rejected = 'rejected',
}

export interface DeleteAccountApplication extends ModelBase {
  message?: string;
  userId: string;
  user: Profile;
  status: DeleteAccountApplicationStatus;
  handlerId?: string;
  handler?: Profile;
}

export enum TimerStatus {
  RUNNING = 'RUNNING',
  TERMINATED = 'TERMINATED',
}

export enum TimerType {
  DATE_TIME = 'DATE_TIME',
}

// liveStreams/{id}/timers
// upcomingShows/{id}/timers
export type ShowTimer = DateTimeTimer;
export interface DateTimeTimer extends ModelBase {
  type: TimerType.DATE_TIME;
  status: TimerStatus;
  name: string;
  // the timer will stop at
  maxDate: AppDate;
  // the timer removed at.
  terminatedAt?: AppDate;
}

export interface SlackContact {
  slackMemberId: string;
  slackName?: string;
  zendeskOwnerId?: number;
}

export interface RequirePaymentCaptureGroup extends BaseCaptureGroup {
  status: CaptureGroupStatus.RequiresPayment;
  closed: false;
}

export interface RequiredCaptureCaptureGroup extends BaseCaptureGroup {
  paymentIntentId: string;
  status: CaptureGroupStatus.RequiresCapture;
  closed: boolean;
  authorizedAmount: number;
}

export interface CapturedCaptureGroup extends BaseCaptureGroup {
  paymentIntentId: string;
  status: CaptureGroupStatus.Captured;
  closed: true;
  capturedAmount: number;
  refundAmount: number;
  authorizedAmount: number;
}

export interface CanceledCaptureGroup extends BaseCaptureGroup {
  status: CaptureGroupStatus.Canceled;
  paymentIntentId: string;
  closed: true;
  authorizedAmount: number;
}

export interface FailedCaptureGroup extends BaseCaptureGroup {
  status: CaptureGroupStatus.Failed;
  paymentIntentId: string;
  closed: true;
  authorizedAmount: number;
  capturedAmount: 0;
}

export interface BaseCaptureGroup extends ModelBase {
  buyerId: string;
  paymentMethodId: string;
  total: number;
  fingerprint: string;
  sellerId?: string;
  /**
   * remove on next version from 2023-08-15, equal to pslGotAmount
   */
  totalApplicationFeeAmount?: number;
  profitBreakdown?: {
    /**
     * Psl get the sum of the amount from the capture group
     */
    pslGotAmount: number;
    dropshipping?: {
      /**
       * Creator get the sum of the amount from the capture group
       */
      creatorGotAmount: number;
      /**
       * DS Management get the sum of the amount from the capture group
       */
      partnerGotAmount: number;
    };
  };
  /**
   * records of dropshipping transactions, included creator income transaction
   */
  dropshippingTransactions?: {
    creatorTransaction: {
      amount: number;
      updatedAt: AppDate;
    };
  };
}

export type CaptureGroup =
  | RequirePaymentCaptureGroup
  | RequiredCaptureCaptureGroup
  | CapturedCaptureGroup
  | CanceledCaptureGroup
  | FailedCaptureGroup;

export enum ChangeRecordType {
  CreateOrder = 'createOrder',
  CreateOrderRevert = 'createOrderRevert',
  RefundOrder = 'refundOrder',
  RefundOrderRevert = 'refundOrderRevert',
}

export interface CaptureGroupChangeRecord extends ModelBase {
  orderId: string;
  amount: number;
  type: ChangeRecordType;
}

export interface InventoryFilter {
  filter: string;
  createdAt: AppDate;
}

export interface CommunityWaitlistUser extends ModelBase {
  profile: Profile;
}

// /featuredTags/{autoId}
export interface FeaturedTag extends ModelBase {
  name: string;

  sortedAt: AppDate;

  sellerTags: string[];
}

/**
 * Aggregated from `Listing.tags`
 *
 * @path: /sellerTags/{autoId}
 */
export interface SellerTag extends ModelBase {
  name: string;
}

export interface StripeAccountLink {
  url: string;
  expiredAt: AppDate;
}

export enum ListingInventoryChangeOperation {
  OrderCreate = 'ORDER_CREATE',
  OrderCreateRevert = 'ORDER_CREATE_REVERT',
  OrderCancel = 'ORDER_CANCEL',
  SendItemCreate = 'SEND_ITEM_CREATE',
  SendItemCreateRevert = 'SEND_ITEM_CREATE_REVERT',
  SendItemClose = 'SEND_ITEM_CLOSE',
  Set = 'SET',
  ListingCreate = 'LISTING_CREATE',
  ListingDELETE = 'LISTING_DELETE',
  OrderClose = 'ORDER_CLOSE',
}

export enum ListingInventorySource {
  Order = 'ORDER',
  User = 'USER',
  SendItem = 'SEND_ITEM',
  CommentSold = 'COMMENT_SOLD',
}

export interface ListingInventoryChange extends ModelBase {
  variantId?: string | null;
  operation: ListingInventoryChangeOperation;
  source?: ListingInventorySource;
  sourceId?: string;
  quantity: number;
  beforeQuantity?: number;
  afterQuantity?: number;
  index: number;
  remark?: string;
}

export enum ListingModelMeImageStatus {
  Ready = 'READY',
  Approved = 'APPROVED',
  Dismissed = 'DISMISSED',
}

export const ImageSourceKey = 'source';
export enum ImageSourceValue {
  ModelMe = 'modelme',
}

export enum ListingModelMeImageRating {
  One = 1,
  Two = 2,
  Three = 3,
  Four = 4,
  Five = 5,
}

export interface ListingModelMeImageFeedback {
  rating: ListingModelMeImageRating;
  note?: string;
  timestamp: string;
}

export interface ListingModelMeImage extends ModelBase {
  processId: string; // trigger-modelme-process ID
  awsS3Url: string;
  // deprecated, please use url
  // awsS3UrlWithBlurhash?: string;
  // it's a firestore storage url with query parameters(id={imageId}, source=modelme) and blurhash
  url: string;
  status: ListingModelMeImageStatus;
  feedback?: ListingModelMeImageFeedback;
}

// imported from: https://assets.avalara.com/taxcodes/global/prod/Avalara_goods_and_services.xlsx?date=2023-07-05
// see: https://taxcode.avatax.avalara.com/
export interface TaxCode extends ModelBase {
  taxCode: string;
  description?: string;
  extraDescription?: string;
}

export enum OnboardingPushContentType {
  video = 'video',
  richText = 'richText',
}

export enum OnboardingPushPageType {
  // app pages
  AppStorefrontHome = 'app:storefront:home',
  AppHome = 'app:home',
  AppDiscovery = 'app:discovery',
  AppShopping = 'app:shopping',
  AppSettings = 'app:settings',
  AppMessages = 'app:messages',
  AppLiveShowHost = 'app:live_show:host',
  AppUpcomingShowHost = 'app:upcoming_show:host',
  AppStorefrontOrders = 'app:storefront:orders',
  AppStorefrontInventory = 'app:storefront:inventory',
  AppStorefrontPopclips = 'app:storefront:popclips',
  AppStorefrontCommentchats = 'app:storefront:commentchats',
  AppCommentchatSetup = 'app:commentchat:setup',
  AppNewCommentchat = 'app:commentchat:new',
  AppStoreInfoSettings = 'app:settings:store_info',
  AppPayoutSettings = 'app:settings:payout',
  AppTaxCodeSettings = 'app:settings:taxcode',
  AppShippingOptions = 'app:settings:shipping_options',
  AppStoreReelSettings = 'app:settings:store_reel',
  AppBusinessLocationSettings = 'app:settings:business_location',
  AppSellerSupport = 'app:seller_support',
  AppGoLive = 'app:go_live',
  AppScheduleShow = 'app:schedule_show',
  AppUploadPopclip = 'app:popclip:new',
  AppSellerReferrals = 'app:seller:referrals',
  AppSellerHowTo = 'app:seller:how-to',

  // seller dashboard pages.
  SellerDashboardHome = 'seller_dashboard:home',
  SellerDashboardStoreInfo = 'seller_dashboard:store_info',
  SellerDashboardPopStore = 'seller_dashboard:pop_store',
  SellerDashboardInventory = 'seller_dashboard:inventory',
  SellerDashboardDropshipCatalog = 'seller_dashboard:dropship_catalog',
  SellerDashboardCollections = 'seller_dashboard:collections',
  SellerDashboardOrders = 'seller_dashboard:orders',
  SellerDashboardStorefrontOrders = 'seller_dashboard:orders:storefront',
  SellerDashboardReplayOrders = 'seller_dashboard:orders:replay',
  SellerDashboardOrderItems = 'seller_dashboard:order_items',
  SellerDashboardFulfillment = 'seller_dashboard:fulfillment',
  SellerDashboardShipping = 'seller_dashboard:shipping',
  SellerDashboardPickups = 'seller_dashboard:pickups',
  SellerDashboardPickupDetail = 'seller_dashboard:pickup_detail',
  SellerDashboardOrderDetail = 'seller_dashboard:order_detail',
  SellerDashboardFulfillmentSettings = 'seller_dashboard:fulfillment:settings',
  SellerDashboardPayout = 'seller_dashboard:finance:payout',
  SellerDashboardSalesTax = 'seller_dashboard:finance:sales_tax',
  SellerDashboardCustomers = 'seller_dashboard:customers',
  SellerDashboardCustomerReviews = 'seller_dashboard:customer_reviews',
  SellerDashboardCommentchat = 'seller_dashboard:commentchat',
  SellerDashboardAddProduct = 'seller_dashboard:product:new',
  SellerDashboardEditProduct = 'seller_dashboard:product:edit',
  SellerDashboardShopifySync = 'seller_dashboard:shopify_sync',
  SellerDashboardLiveShows = 'seller_dashboard:live_shows',
  SellerDashboardLiveShow = 'seller_dashboard:live_show',
  SellerDashboardScheduleShow = 'seller_dashboard:schedule_show',
  SellerDashboardReferrals = 'seller_dashboard:referrals',
  SellerDashboardCoupons = 'seller_dashboard:coupons',
  SellerDashboardBadges = 'seller_dashboard:badges',
  SellerDashboardSettings = 'seller_dashboard:settings',
  SellerDashboardContactSupport = 'seller_dashboard:contact_support',
}

// /onboardingPushes/{id}
export interface OnboardingPush extends ModelBase {
  title: string;
  summary: string;
  subjectLine: string;
  subtitle: string;
  editor: string;
  embedCode: string;
  contentType: OnboardingPushContentType;
  cover?: string;
  videoUrl?: string;
  videoSize?: {
    width: number;
    height: number;
  };
  // this takes precedence over appearOnSpecificPages
  appearOnAllScreens?: boolean;
  appearOnSpecificPages?: string[];
  visibleTo: StoreType[];
  buttonDelayInSeconds: number;
  startTime: AppDate;
  totalViewers: number;
  // if true, no longer show this push
  disabled: boolean;
  // flow: false(draft) -> true(published). cannot be set to false again
  published?: boolean;
  // mainly for testing purpose
  // if defined, this push will only be applied to these sellers
  sellerIds?: string[];
  historySellerIds?: string[];
  // defines a simple action button
  cta?: {
    title: string; // button text/title
    link: string; // any arbitrary link
  };
  notification?: {
    title: string;
    body?: string;
  };
}

// /stores/{storeId}/onboardingPushes/{id}
// shares the same doc id as /onboardingPushes/{id}
export interface StoreOnboardingPush extends ModelBase {
  // true if the seller has viewed this push
  viewed: boolean;
  viewedAt?: AppDate;
  disabled: boolean;
  profile: Profile;
}

/**
 * Ref: https://docs.stripe.com/api/subscriptions/object
 */
export enum SubscriptionStatus {
  active = 'active',
  canceled = 'canceled',
  incomplete = 'incomplete',
  incomplete_expired = 'incomplete_expired',
  trialing = 'trialing',
  past_due = 'past_due',
  unpaid = 'unpaid',
  paused = 'paused',
}

export enum SubscriptionPlanInterval {
  day = 'day',
  month = 'month',
  year = 'year',
}

export enum SubscriptionPlanType {
  plan = 'plan',
  commentChatAddOn = 'commentChatAddOn',
  modelMeAddOn = 'modelMeAddOn',
  taxProduct = 'taxProduct',
}

export interface SubscriptionPlan {
  productId: string;
  productName: string;
  active: boolean;
  amount: number;
  discountAmount?: number;
  billingScheme: string;
  interval: SubscriptionPlanInterval;
  intervalCount: number;
  planType: SubscriptionPlanType;
  commentChatContactCount?: number;
  modelMePhotoCount?: number;
  modelMeOutfitsCount?: number;
  includeLinkInBioSite?: boolean;
  includeProductCatalog?: boolean;
  subscriptionItemId?: string;
  description: string;
  couponId?: string;
}

export interface SellerSubscription extends ModelBase {
  paymentIntentId: string;
  defaultPaymentMethod: string;
  status: SubscriptionStatus;
  /**
   * Either charge_automatically, or send_invoice. When charging automatically, Stripe will attempt to pay this subscription at the end of the cycle using the default source attached to the customer.
   * When sending an invoice, Stripe will email your customer an invoice with payment instructions and mark the subscription as active.
   */
  collectionMethod: string;
  initialStartDate: AppDate;
  trialEnd: AppDate | null;
  /**
   * Start of the current period that the subscription has been invoiced for.
   */
  currentPeriodStart: AppDate;
  /**
   * End of the current period that the subscription has been invoiced for. At the end of this period, a new invoice will be created.
   */
  currentPeriodEnd: AppDate;
  /**
   * If the subscription has been canceled, the date of that cancellation.
   */
  cancelledAt: AppDate | null;
  /**
   * Boolean indicating whether this subscription should cancel at the end of the current period.
   */
  cancelAtPeriodEnd?: boolean;
  cancellationAppliedAt: AppDate | null;
  lastInvoiceId: string;
  plans: (SubscriptionPlan & { priceId: string })[];
  price: number;
  discountedPrice?: number;
  taxAmount: number;
}

/**
 * Ref: https://docs.stripe.com/invoicing/overview#workflow-overview
 */
export enum SubscriptionInvoiceStatus {
  draft = 'draft',
  open = 'open',
  paid = 'paid',
  void = 'void',
  uncollectible = 'uncollectible',
}

// Ref: https://docs.stripe.com/api/invoices/object
export interface SubscriptionInvoice extends ModelBase {
  amountDue: number;
  amountPaid: number;
  attemptCount: number;
  collectionMethod: string;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  /**
   * The time at which the invoice was created in Stripe
   */
  created: AppDate;
  defaultPaymentMethod: string;
  /**
   * The status of the invoice, one of draft, open, paid, uncollectible, or void.
   */
  status: SubscriptionInvoiceStatus;
}

/**
 * Enum that represents the status of a subscription
 */
export enum PopStoreSubscriptionStatus {
  /** Unavailable, not configured yet */
  Pending = 0,

  /** Active, successfully configured */
  Active = 1,
}

export type SubscriptionPlatformLevelInfo = {
  // the id of this subscription level
  id: number;

  // platform subscription level definition metadata
  platform_sub_level_id: number;
  platform_sub_level: PopStoreSubscriptionLevel;
  platform_sub_level_precedence: number;
};

export type SubscriptionPriceInfo = {
  creator_sub_price_id: number;
  creator_sub_id: number;
  creator_sub_price_without_precision: number;
  creator_sub_price_currency: string;
  default_commitment_period: number;
  creator_sub_commitment_discount_percentage?: number | null;
  creator_sub_discount_commitment_period?: number | null;
  creator_sub_price_created_date?: string;
  creator_sub_price_end_date: string | null;
  creator_sub_current_price: boolean;
  creator_sub_activated_on: number;
  discount_for_longer_subscription_commitment?: boolean;
  created_at: string;
};

export type SubscriptionBasicInfo = {
  // id of the subscription configuration
  creator_sub_id: number;
  creator_store_id: string;
  platform_sub_level_id: number;
  creator_sub_name: string;
  creator_sub_description: string;
  creator_sub_primary_image: string | null;
  creator_sub_background_image: string | null;
  creator_sub_preview_video: string | null;
  created_at: string;
  active?: boolean;
};

export type SubscriptionConfiguration = {
  // subscription level information
  platform_subscription_level: SubscriptionPlatformLevelInfo;

  // subscription basic information
  creator_subscription: SubscriptionBasicInfo;

  // subscription pricing information
  creator_subscription_price: SubscriptionPriceInfo;
};

export type SubscriptionStatistics = {
  registered_users: number;
  paying_users: number;
  current_price: {
    currency: string;
    frequency: string;
    price_without_precision: number;
    currencyPrecisionFactor: number;
  };
  previous_price?: {
    currency: string;
    frequency: string;
    price_without_precision: number;
    currencyPrecisionFactor: number;
  } | null;
  expected_income?: {
    currency: string;
    frequency?: string;
    price_without_precision: number;
    currencyPrecisionFactor: number;
  } | null;
};

export type SubscriptionBase = {
  platform_sub_level: PopStoreSubscriptionLevel;
  platform_sub_level_display_name: string;
  platform_sub_level_id: number;
};

/**
 * Represents a placeholder subscription of popstore creator's subscriptions.
 * This is used when the subscription is not configured yet.
 */
export type PlaceholderSubscription = SubscriptionBase & {
  // subscription configuration status
  subscription_configuration_status: PopStoreSubscriptionStatus.Pending;
  payingUsers: number;
};

/**
 * Represents an active subscription of popstore creator's subscriptions.
 * This is used when the subscription is already successfully configured.
 */
export type ActiveSubscription = SubscriptionBase & {
  // subscription configuration status
  subscription_configuration_status: PopStoreSubscriptionStatus.Active;

  // subscription configuration details
  subscription_data: SubscriptionConfiguration;

  // creator subscription statistics data
  creator_subscription: SubscriptionStatistics;
};

type WithGeneralTimestamps<T> = T & {
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_at_timestamp: number;
  updated_at_timestamp: number;
  deleted_at_timestamp: number | null;
};

/**
 * Type that represents the information of a user subscription for a customer.
 */
export type PopStoreUserSubscription = {
  user_subscription_id: number;
  psl_user_id: string;
  creator_sub_price_id: number;
  creator_sub_id: number;
  subscription_start_date: number;
  subscription_end_date: string | null;
  subscription_renewal_date: number;
  subscription_status: 'active' | 'cancelled';
  cancelled_at: number | null;
  last_charged: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_at_timestamp: number;
  updated_at_timestamp: number;
  deleted_at_timestamp: number | null;
  creator_subscription: WithGeneralTimestamps<SubscriptionBasicInfo> & {
    platform_subscription_level: SubscriptionPlatformLevelInfo;
  };
  creator_subscription_price: WithGeneralTimestamps<SubscriptionPriceInfo>;
};

/**
 * Represents a subscription of popstore creator's subscriptions,
 * which can be either placeholder (not configured yet) or active
 * (successfully configured).
 */
export type PopStoreSubscription = PlaceholderSubscription | ActiveSubscription;

export type PopStoreFollowerSubscription = {
  level: PopStoreExtendedSubscriptionLevel.FOLLOWER;
  description?: string;
  active?: boolean;
};

export type PopStorePlatformSubscription = {
  level: PopStoreSubscriptionLevel;
  subscription: PopStoreSubscription;
};

export type PopStoreExtendedSubscription =
  | PopStoreFollowerSubscription
  | PopStorePlatformSubscription;

export interface Subscriptions {
  fan: PopStoreSubscription;
  subscriber: PopStoreSubscription;
  vip: PopStoreSubscription;
}

// seller's subscription level to our platform
export enum PopStoreSubscriptionLevel {
  SUBSCRIBER = 'subscriber',
  FAN = 'fan',
  VIP = 'vip',
}

export enum PopStoreExtendedSubscriptionLevel {
  FOLLOWER = 'follower',
  SUBSCRIBER = 'subscriber',
  FAN = 'fan',
  VIP = 'vip',
}

// the user's subscription level that is required to access the creator's content
export enum PopStoreUserAccessLevel {
  ALL = 'all',
  SUBSCRIBER = 'subscriber',
  FAN = 'fan',
  VIP = 'vip',
}

export enum PopStoreExtendedUserAccessLevel {
  ALL = PopStoreUserAccessLevel.ALL,
  FOLLOWER = 'follower',
  SUBSCRIBER = PopStoreUserAccessLevel.SUBSCRIBER,
  FAN = PopStoreUserAccessLevel.FAN,
  VIP = PopStoreUserAccessLevel.VIP,
}

export enum CreatorSubscriptionStatus {
  succeeded = 'succeeded',
  canceled = 'canceled',
  processing = 'processing',
  requires_action = 'requires_action',
  requires_capture = 'requires_capture',
  requires_confirmation = 'requires_confirmation',
  requires_payment_method = 'requires_payment_method',
}

export interface PopStoreUserSubscriptionInfo {
  psl_user_id: string;
  user_subscription_id: number;
  platform_sub_level: PopStoreSubscriptionLevel;
  platform_sub_level_precedence: number;
  subscription_end_date: number | null; // unix timestamp
  subscription_start_date: number; // unix timestamp
  creator_sub_description: string;
  subscription_status: 'active' | 'cancelled';
}

export interface CreatorChargeDistribution {
  subtotal: number;
  taxAmount: number;
  commissionFee: number;
  gatewayProcessingFee: number;
  creatorNetIncome: number;
  commissionFeeRate: number;
  gatewayProcessingFeeRate: number;
  gatewayProcessingFeeExtra?: number;
}

export interface CreatorSubscriptionBalanceRecord extends ModelBase {
  subscriptionTransactionId: string;
  /**
   * the index of the balance record in the subscription transaction
   * start from 1
   */
  index: number;
  availableBalance: number;
  pendingBalance: number;
  nextAvailableDate?: AppDate;
  currency?: string;
}

export interface CreatorSubscriptionTransaction extends ModelBase {
  commentSoldSubscriptionId: string;
  status: CreatorSubscriptionStatus;
  currentPeriodStart: AppDate;
  currentPeriodEnd: AppDate;
  subscriptionLevel: PopStoreSubscriptionLevel;
  subscriptionInterval: string; // 6 month, 12 month
  subscriptionIntervalCount?: number; // unit: month
  subscriptionType: string; // 'new', 'renewal'
  price: number;
  creatorChargeDistribution: CreatorChargeDistribution;
  /**
   * the  time indicates the balance from pending to available
   */
  creatorBalanceAvailableAt?: AppDate;
  creatorMonthlyIncome?: number;
  latestBalanceRecord?: CreatorSubscriptionBalanceRecord;
  transactionInMultiCurrency?: CreatorSubscriptionTransactionInMultiCurrency;
}

export interface CreatorSubscriptionTransactionInMultiCurrency
  extends CreatorSubscriptionTransaction {
  currency?: string;
  exchangeRate?: number; // the exchange rate from USD to this currency
}

export interface CreatorSubscription extends ModelBase {
  commentSoldSubscriptionId: string;
  initialStartDate: AppDate;
  endDate: AppDate;
  trialEnd: AppDate | null;
  subscriptionLevel: PopStoreSubscriptionLevel;
  latestTransactionId: string;
  latestTransactionUpdateDate: AppDate;
  isActive: boolean;
}

// popstore creator application for selling dropship products
export interface PopStoreDropshipApplication extends ModelBase {
  creatorId: string; // popstore creator id
  creatorProfile: Profile; // popstore creator profile
  state: string; // state(i.g. NY, CA), only states in US supported now
  instagramId: string; // instagram id(i.g. @popshop.live)
  status: PopStoreDropshipApplicationStatus;
  reason?: string; // reason for approve or reject
  auditedAt?: AppDate;
  auditorId?: string;
  auditorProfile?: Profile;
}

export enum PopStoreDropshipApplicationAction {
  Approve = 'approve',
  Reject = 'reject',
}

export enum PopStoreDropshipApplicationStatus {
  Submitted = 'submitted',
  Approved = 'approved',
  Rejected = 'rejected',
}

// popstore onboarding contest submission
export interface PopStoreOnboardingContestSubmission extends ModelBase {
  creatorId: string;
  creatorProfile: Pick<
    Profile,
    | 'avatar'
    | 'username'
    | 'firstName'
    | 'lastName'
    | 'urlCode'
    | 'storeFrontName'
  >;
  data: PopStoreCreatorOnboardingContest; // snapshot of privateProfile.popStoreOnboardingContest when submission is created
  numOfCompletedTasks: number;
  week: string; // ISO 8601 first day of week (e.g. 2025-03-30 if week starts on sunday)
  isWinner: boolean;
  // below are for winner
  pickedBy?: Pick<Profile, 'avatar' | 'username' | 'firstName' | 'lastName'> & {
    id: string;
  };
  pickedAt?: AppDate;
  defaultScreenshotStatus?: 'initial' | 'processing' | 'completed' | 'error';
  defaultScreenshotError?: string;
  // screenshot of popstore profile generated by cloud functions when submission is created
  // available when defaultScreenshotStatus is completed
  defaultScreenshotImageURL?: string;
  customScreenshotImageURL?: string;
}

/**
 * For compatible PSL Creator to Pop.Store
 */
export enum XPopPlatform {
  Psl = 'psl',
  Popstore = 'popstore',
}

// see https://animate.style/.
export enum AnimationStyle {
  None = 'none',
  Bounce = 'bounce',
  Flash = 'flash',
  Pulse = 'pulse',
  RubberBand = 'rubberBand',
  ShakeX = 'shakeX',
  ShakeY = 'shakeY',
  HeadShake = 'headShake',
  Swing = 'swing',
  Tada = 'tada',
  Wobble = 'wobble',
  Jello = 'jello',
  HeartBeat = 'heartBeat',
}

export enum RecaptchaAction {
  GuestAccountCreation = 'guest_account_creation',
}

export enum PostContentType {
  RichText = 'RICH_TEXT',
  MediaGallery = 'MEDIA_GALLERY',
}

export enum PostMediaType {
  Image = 'IMAGE',
  Video = 'VIDEO',
}

export interface PostMediaDimensions {
  width: number;
  height: number;
}

export interface PostMediaMetadata {
  dimensions?: PostMediaDimensions;
  // the blurhash of the image that can be used to render the placeholder
  // either when loading the image or when the image is not accessible
  // to the user.
  blurhash?: string;
}

export interface PostMedia {
  type: PostMediaType;
  contentType: string; // e.g. image/jpeg, video/mp4
  id: string; // media gallery object id
  url: string;
  processedUrl?: string; // processed url.
  thumbnailUrl?: string; // thumbnail image url
  thumbnailVideoUrl?: string;
  caption?: string;
  metadata?: PostMediaMetadata;
  // moderation information
  moderation?: ContentModerationInfo;
}

export enum PostStatus {
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
  Scheduled = 'SCHEDULED',
}

export enum PostVisibility {
  Public = 'PUBLIC',
  Restricted = 'RESTRICTED',
  Private = 'PRIVATE',
}

// open questions:
//  1. do we allow delete or hide post is user paid for it?

// `/stores/{creatorId}/posts`
export interface Post extends ModelBase {
  // unique internal post identifier
  cid: string;

  title: string; // FTS index
  description?: string; // FTS index
  tags?: string[];
  // pattern: `{urlCode}/post/{full year}/{full month}/{title_slub}{optional suffix}`
  // example: `andy.us1/post/2025/04/illum-aut-qui-ut-suscipit-non-quia`
  // example: `andy.us1/post/2025/04/illum-aut-qui-ut-suscipit-non-quia_qwert`
  permaLink?: string;

  pinned?: boolean; // pinned to the top of the post list
  pinnedAt?: AppDate; // pin timestamp

  // post status
  status: PostStatus;

  // the scheduled time to release/publish this post at
  scheduledReleaseTime?: AppDate;

  visibility: PostVisibility;

  // if set, this post will be turned to "pay to view" only mode
  // after this many days since the post publish time
  payToViewOnlyMode?: {
    enabled?: boolean;
    daysAfterPublished?: number;
  };

  subscribeToView?: {
    enabled?: boolean;
    level: PopStoreExtendedSubscriptionLevel;
  };

  payToView?: {
    enabled?: boolean;
    // support multi-currency?
    price: number;
    discountEnabled?: boolean;
    discountPrice?: number;
    discountStartDate?: AppDate;
    discountEndDate?: AppDate;
    // the hidden backing listing
    listingId?: string;
  };

  tipToView?: {
    enabled?: boolean;
  };
  tippingAmounts?: Array<TippingAmount>;

  crowdTippingToView?: {
    enabled?: boolean;
    targetAmount: number;
    currentAmount: number;
    currency: 'USD';
  };

  // whether DMs have been sent
  // a post might be moderated(accepted) multiple times, DMs are sent only once
  dmSent?: boolean;
  dmOnly?: {
    enabled?: boolean;
    message?: string;
    // use array so that we can support multiple subscription levels
    sendTo: PopStoreExtendedSubscriptionLevel[];
  };

  // primary post type
  postType: PostContentType;

  // rich text content, must exist if postType is RICH_TEXT
  textContent?: string;

  // media gallery content, must exist if postType is MEDIA_GALLERY
  medias?: PostMedia[];
  unrestrictedLeadingMediaCount?: number;

  // statistics
  commentCount: number; // top level comments count
  likeCount: number;

  // permissions
  allowComments: boolean;

  // published time (in addition to cerate time)
  publishedAt?: AppDate;

  // moderation information
  moderation?: ContentModerationInfo;

  // text content moderation info
  textContentModeration?: ContentModerationInfo;

  // whether emails have been sent to the followers of the creator
  emailSent?: boolean;
}

// post comments & replies:
//   `/stores/{creatorId}/posts/{postId}/comments`
export interface PostComment extends ModelBase {
  //e.g. 1/2/3, only support 3 levels of comments
  level: number;
  parentId?: string;
  text: string;
  userId: string;
  profile: Profile;
  // if this user is a follower of the post creator
  isFollower: boolean;
  // if the user is a subscriber, this will be the subscription level
  subscriptionLevel?: PopStoreSubscriptionLevel;
  // comment stats
  likeCount: number;
  // number of replies to this comment
  replyCount: number;
  removed: boolean;
}

// post like details:
//  `/stores/{creatorId}/posts/{postId}/likes`
export interface PostLike extends ModelBase {
  userId: string;
  profile: Profile;
}

// comment like details:
//  `/stores/{creatorId}/posts/{postId}/comments/{commentId}/likes`
export interface PostCommentLike extends ModelBase {
  userId: string;
  profile: Profile;
}

/**
 * Use to calculate the sale tax for pop.store order
 */
export interface PaymentBillingAddress {
  firstName?: string;
  lastName?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}
